import dayjs from '@dcp/shared/utils/dayjs'
import { getTenantClient } from './supabase-tenant'
import { getSupabaseClient } from './supabase'
import type { TenantTables } from '@dcp/shared/types/tenant-database'
import { businessDate } from '@dcp/shared/utils/business-date'
import { oldestTransactionDate } from '@dcp/shared/utils/initial-import-date'
import { isUuid, PLAID_CATEGORY_REGEX } from '@dcp/shared/utils/validators'
import { isLedgerSyncSyncStatus, type LedgerSyncSyncStatus } from '@dcp/shared/ledgersync/sync-state'
import type { ProjectionFilters, ProjectionWithRelations } from '@/lib/supabase'
import type { ManualTransactionInput, FileImportRow, FileImportInput, BalanceImportStatus, UpdateSource, ImportStatus, BankTransactionSort, BankTransactionSortKey } from '@/types/banking'
import { isValidImportType, isBankTransactionSortKey, DEFAULT_DETAILS_PAGE_SIZE, MANUAL_PAGE_TRANSACTION_SOURCES, NO_CATEGORY_FILTER_VALUE } from '@/types/banking'
// DCP-371: the account types that must never reach a Bank Balance figure.
// DCP-400: the ones Daily Banking hides from the transactions table.
// DCP-374: the fetch step for the Credit Card Availability scroll.
import {
  BANK_BALANCE_EXCLUDED_ACCOUNT_TYPES,
  CREDIT_CARD_PAGE_SIZE,
  DAILY_BANKING_EXCLUDED_ACCOUNT_TYPES,
} from '@/constants/banking'
import { getCategoryLabel } from '@/lib/plaid-categories'
// DCP-324 AC#5: one bound for the connection name, shared with the rename form
// so the data layer and the dialog cannot disagree about it.
import { MAX_CONNECTION_NAME_LENGTH } from '@/lib/connection-rename-form'
// Shared AC#3 status rule (single source of truth — also used by the UI editor).
// Value import is runtime-acyclic: sage-utils only type-imports from this file.
import { canAssignStatus, resolveDateCashed, storedStatusFor } from '@/lib/sage-utils'
import { checkStatusFilter } from '@/lib/sage-check-status-filter'
// DCP-386: created-day window arithmetic for the projection created-date filter.
import { nextCalendarDay } from '@/lib/date-utils'
import { connectionAttention } from '@/lib/ledgersync-connection-attention'

/**
 * NOTE ON TYPE CASTS (as any):
 *
 * The Supabase client is typed via TenantSchemaDb which maps the dynamic tenant schema
 * to the "public" key for type resolution. However, .insert() and .update() methods
 * expect exact schema-typed inputs that don't match the generic TenantDatabase[string]
 * mapping. This is a known limitation of Supabase's type system with dynamic schemas.
 *
 * The `as any` casts on insert/update operations are unavoidable without a breaking
 * change to Supabase's type generation. All inputs are typed at the function signature
 * level (e.g., ProjectionInsert, CategoryRow), providing compile-time safety for callers.
 * The casts only bypass the internal Supabase builder constraint, not our own types.
 */

/** Result types for error handling (same pattern as supabase.ts) */
interface DbError {
  message: string
  code: string
}
interface DbSuccess<T> {
  data: T
  error: null
}
interface DbFailure {
  data: null
  error: DbError
}
type DbResult<T> = DbSuccess<T> | DbFailure

/**
 * Escape special characters for SQL LIKE patterns
 * Escapes %, _, and \ characters to prevent SQL injection in LIKE clauses
 * @param str - The string to escape
 * @returns Escaped string safe for use in LIKE patterns
 */
function escapeLikePattern(str: string): string {
  return str.replace(/[%_\\]/g, '\\$&')
}

/**
 * Escape special characters for use in PostgREST .or() ILIKE filter values.
 * Handles both SQL LIKE metacharacters (%, _, \) and PostgREST OR grammar
 * metacharacters (comma, parentheses, dot, double-quote) by wrapping the
 * value in PostgREST double-quote delimiters.
 * @param str - The raw user input string
 * @returns Escaped and quoted string safe for use in .or() ILIKE patterns
 */
function escapeOrFilterValue(str: string): string {
  // First escape SQL LIKE metacharacters
  const likeEscaped = escapeLikePattern(str)
  // Escape embedded double-quotes, then wrap the full ILIKE pattern
  // (including % wildcards) in double-quotes so PostgREST treats
  // the entire value as a single literal token
  return `"%${likeEscaped.replace(/"/g, '\\"')}%"`
}

/**
 * Build the PostgREST OR-clause for the dashboard account filter, or null
 * when there is nothing to filter on. A transaction matches via its Plaid FK
 * (`plaid_account_id`) or the canonical `bank_account_id` (manual, file-import,
 * and LedgerSync — DCP-217 collapsed the LedgerSync account model onto
 * bank_accounts, so LedgerSync transactions now match via bank_account_id, not
 * the retired ledgersync_account_id). IDs are UUID-validated before
 * interpolation so a crafted value can't inject extra filter clauses. Shared by
 * `bankTransactions.list`/`count`/`distinctCategories` so the three can't
 * drift apart (Presto review).
 * @param accountIds - Raw account IDs from the filter (may be unsanitized)
 * @returns OR-clause string for `.or(...)`, or null if no valid IDs
 */
function accountFilterClause(accountIds?: string[]): string | null {
  if (!accountIds?.length) return null
  const ids = accountIds.filter(isUuid).join(',')
  return ids
    ? `plaid_account_id.in.(${ids}),bank_account_id.in.(${ids})`
    : null
}

/**
 * Build the PostgREST OR-clause for the dashboard category filter, or null.
 * Category values are mixed, and every kind is OR-ed with the others (the
 * filter is a multi-select — picking two values means "either"):
 *   - UUIDs match `category_id`;
 *   - uppercase strings match legacy `plaid_category`;
 *   - {@link NO_CATEGORY_FILTER_VALUE} (DCP-123, AC#5) matches
 *     `category_id IS NULL` — the rows the table renders as "No Category".
 *     A legacy Plaid row that carries a `plaid_category` but no `category_id`
 *     is one of them on purpose: `mapTransactions` shows it as "No Category"
 *     too, so the filter selects exactly what the user sees under that label.
 * All three are validated / charset-restricted / a fixed literal, so no branch
 * can smuggle PostgREST filter operators. Shared by `bankTransactions.list` /
 * `count`, so the table and its pagination count can't disagree about what a
 * category selection means.
 * @param categories - Raw category values from the filter (may be unsanitized)
 * @returns OR-clause string for `.or(...)`, or null if nothing matches
 */
function categoryFilterClause(categories?: string[]): string | null {
  if (!categories?.length) return null
  const uuids = categories.filter(isUuid)
  const plaidKeys = categories.filter((c) => !isUuid(c) && PLAID_CATEGORY_REGEX.test(c))
  const parts: string[] = []
  if (categories.includes(NO_CATEGORY_FILTER_VALUE)) parts.push('category_id.is.null')
  if (uuids.length) parts.push(`category_id.in.(${uuids.join(',')})`)
  if (plaidKeys.length) parts.push(`plaid_category.in.(${plaidKeys.join(',')})`)
  return parts.length ? parts.join(',') : null
}

/**
 * Apply an optional importType filter to a PostgREST query builder, with
 * the shared runtime whitelist check (mirrors the CHECK constraint on
 * file_imports.import_type). Returns either the chained query OR an
 * INVALID_PARAM error — callers thread the result through their own
 * DbResult return path.
 *
 * Centralizing this keeps the two file_imports query sites (list / count)
 * from drifting on the validation message, error code, or the column name
 * used in the .eq() filter.
 */
function applyImportTypeFilter<T>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  query: any,
  importType: string | undefined,
): { query: T; error: null } | { query: null; error: DbError } {
  if (!importType) return { query, error: null }
  if (!isValidImportType(importType)) {
    return {
      query: null,
      error: { message: `Invalid import_type: ${importType}`, code: 'INVALID_PARAM' },
    }
  }
  return { query: query.eq('import_type', importType), error: null }
}

/**
 * Apply all ProjectionFilters to a Supabase query builder and return the modified query.
 * Handles date ranges, created-date (single day), amount ranges, scalar equality filters,
 * and the credit/debit inclusion toggles. Pagination (range) is intentionally excluded —
 * it only applies to data queries, not count queries.
 *
 * The query parameter is typed as `any` because the data query and count query return
 * different builder types from Supabase (different select signatures), so a shared
 * generic cannot be expressed without losing type safety elsewhere.
 *
 * @param query - A Supabase PostgREST query builder (data or count)
 * @param filters - Optional projection filter object
 * @returns The same query builder with all applicable filters chained
 * @throws {Error} If `filters.createdDate` is set but is not a valid `YYYY-MM-DD`
 *   string — `nextCalendarDay` validates its input and throws rather than
 *   silently querying a bogus window. Unreachable from the UI today (the only
 *   writer is a calendar-only DatePicker), but this function is exported, so a
 *   future caller passing a hand-built string must expect it. Note the throw
 *   happens inside a React Query `queryFn`, so it surfaces as a failed query,
 *   not as a toast.
 */
// Exported (in addition to being used internally below) so
// supabase.projections.test.ts can drive this real filter-building logic
// against a mock query-builder spy instead of re-implementing it locally.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function applyProjectionFilters<T extends Record<string, any>>(query: T, filters?: ProjectionFilters): T {
  if (!filters) return query

  // Date range filters on the projection date column. DCP-386: the UI now converts
  // the picked calendar day to a `YYYY-MM-DD` string at the edge (before it ever
  // reaches this function), so these are passed straight to PostgREST — no Date
  // conversion here, mirroring how `bankTransactions.list` (below) already bounds
  // `transaction_date` with plain strings.
  if (filters.startDate) query = query.gte('date', filters.startDate)
  if (filters.endDate) query = query.lte('date', filters.endDate)

  // Created-date filter: match a single calendar day as a half-open window
  // [createdDate, createdDate+1) against `created_at`. `nextCalendarDay` does
  // pure string arithmetic (no Date/timezone conversion), so the window's
  // literal bounds are exactly the two date strings.
  //
  // NOTE: `created_at` is a TIMESTAMPTZ, so comparing it to bare date strings
  // makes PostgREST/Postgres interpret both bounds as UTC instants — i.e. this
  // window is a "created day in UTC" filter, not a "created day in the viewer's
  // (or the business's) timezone" filter. Whether that's the intended semantics
  // is a separate, currently-open question for the BA; this comment only
  // documents the actual behavior, it does not attempt to fix it.
  if (filters.createdDate) {
    query = query
      .gte('created_at', filters.createdDate)
      .lt('created_at', nextCalendarDay(filters.createdDate))
  }

  // Amount range filter — DCP-250 Phase 1: projections.amount is a single signed column
  // (positive = inflow, negative = outflow).  min/max apply directly to `amount` via
  // gte/lte — the caller is responsible for passing sensible signed boundaries.
  // Values are validated as finite numbers before interpolating into PostgREST filter
  // strings to prevent injection of NaN, Infinity, or arbitrary text.
  const safeMin =
    filters.minAmount !== undefined && Number.isFinite(filters.minAmount) ? filters.minAmount : undefined
  const safeMax =
    filters.maxAmount !== undefined && Number.isFinite(filters.maxAmount) ? filters.maxAmount : undefined

  if (safeMin !== undefined) query = query.gte('amount', safeMin)
  if (safeMax !== undefined) query = query.lte('amount', safeMax)

  if (filters.facilityIds?.length) query = query.in('facility_id', filters.facilityIds)
  if (filters.categoryIds?.length) query = query.in('category_id', filters.categoryIds)
  // `type` filter retired in DCP-250 Phase 1 — categories.type is nullable and no UI sets it
  if (filters.status) query = query.eq('status', filters.status)
  if (filters.isPriority !== undefined) query = query.eq('priority', filters.isPriority)
  if (filters.includeInReport !== undefined) query = query.eq('include_in_report', filters.includeInReport)

  // Credit/debit toggles — map to signed-amount semantics (DCP-250 Phase 1):
  //   includeCredits=false, includeDebits=true  → show only outflows (amount < 0)
  //   includeCredits=true,  includeDebits=false → show only inflows  (amount > 0)
  //   both true or both false → no sign filter (show all)
  if (filters.includeCredits === false && filters.includeDebits === true) {
    query = query.lt('amount', 0)
  } else if (filters.includeCredits === true && filters.includeDebits === false) {
    query = query.gt('amount', 0)
  }

  return query
}

/**
 * DCP-406: rows requested per round trip when `projections.findPage` walks one
 * day of the table. Matches the API's `max_rows` (1000, supabase/config.toml);
 * findPage advances by the rows actually returned, so a smaller server cap only
 * costs extra round trips, never skips rows. Exported for the unit test.
 */
export const PROJECTION_DAY_CHUNK = 1000

/**
 * DCP-406: ids per `.in('id', …)` request when `projections.findPage` looks up
 * the earliest date of a batch of new rows. The filter goes into the request
 * URL, and a UUID with its separator is ~37 characters, so 100 ids add ~3.7 KB
 * — comfortably below the 8 KB URL/header limits common in proxies and
 * gateways, even next to the rest of the query string. A batch create or a
 * paste of a large selection would otherwise put every id in one URL. Exported
 * for the unit test.
 */
export const PROJECTION_ID_CHUNK = 100

// Re-export types used by tenant tables
export type { DbResult }

/**
 * Transaction sources reachable through the Manual Transactions UI.
 *
 * Both flow into the same `transactions` table — only the `source` column
 * distinguishes them:
 *   - `manual`      — rows the AP team enters by hand via the new-transaction dialog
 *   - `file_import` — rows brought in via the CSV / Excel importer
 *
 * Plaid-sourced rows live on a separate Bank Transactions surface and must
 * NOT be reachable by `manualTransactions.update/delete`: they auto-resync
 * on the next Plaid pull, so a delete would silently reappear in minutes
 * and an edit would be overwritten on the next sync.
 *
 * Bug DCP-131: the list/count helpers already used this pair, but update
 * and delete filtered on `source = 'manual'` alone, so the UI showed
 * file-imported rows yet every mutation effectively no-op'd. The two
 * failure shapes differed: `update` ends with `.single()`, so a zero-row
 * UPDATE surfaces PGRST116 — broken but at least visible; `delete` has no
 * `.single()`, so PostgREST returns 204 with zero rows and the caller
 * gets a clean success → user saw a "deleted" toast for a row that was
 * still there. Both paths now allow the file_import source. Keeping the
 * allow-list in one constant means the four *read/mutate* methods
 * (list, count, update, delete) stay aligned by construction.
 *
 * `create` / `createMany` intentionally do NOT consult this constant —
 * they hardcode `source: 'manual'` because new rows entered via the UI
 * are always manual entries; `file_import` rows are produced exclusively
 * by the importer pipeline (`process-file-import` task). Adding them to
 * the constant would expose `'file_import'` as a *creatable* source from
 * the UI, which is the wrong direction.
 *
 * Defined in the client-free `@/types/banking` (so the LOC drill-down dialog
 * can gate Notes editing on the same allow-list WITHOUT bundling the Supabase
 * client), imported above for the read/mutate methods here, and re-exported so
 * the regression test in `supabase.manualTransactions.test.ts` — and any other
 * consumer — keeps importing it from this module unchanged (DCP-169).
 */
export { MANUAL_PAGE_TRANSACTION_SOURCES }

/**
 * All valid values of `transactions.source` (mirrors the `transaction_source`
 * Postgres enum). Narrowed here so the `TenantTransaction.source` field gives
 * call sites real autocomplete and a typo like `'manula'` fails at compile
 * time instead of in production. If a new ingestion path is added (e.g. an
 * ERP source), this union and the DB enum must grow together.
 */
export type TransactionSource =
  | typeof MANUAL_PAGE_TRANSACTION_SOURCES[number]
  | 'plaid'
  | 'ledgersync'

/**
 * Default page size for `manualTransactions.list` when the caller passes an
 * `offset` but no explicit `limit`. Kept module-level (and exported) so the
 * regression test mirror in `supabase.manualTransactions.test.ts` can pin
 * the same value — drift between prod and test would silently let a future
 * tweak of the default slip past the suite.
 */
export const MANUAL_TRANSACTIONS_DEFAULT_PAGE_SIZE = 50

/**
 * DCP-324: the join path from a transaction to its FACILITY, in the INNER form.
 *
 * A transaction has no facility of its own any more. `transactions.facility_id`
 * was a denormalized copy of `bank_accounts.facility_id` stamped at INSERT and
 * never refreshed, so assigning a facility to an account left its existing rows
 * reading "Unassigned" forever and invisible to any facility filter (measured on
 * the local DB: 5/5 of the Line of Credit account's rows, 3/5 of the Credit
 * Card's). Migration 20260911000000 drops the column and its FK
 * `transactions_facility_id_fkey`; the facility is now DERIVED, always, through
 * the account.
 *
 * BOTH hops are `!inner`, and each one enforces a rule the product asked for:
 *   - `bank_accounts!inner` — `transactions.bank_account_id` is NULLABLE, and a
 *     row with no account has no facility to derive. Same answer as below:
 *     unassigned, therefore not shown.
 *   - `facilities!inner` — DCP-324 AC#22: an account assigned to NO facility is
 *     hidden from the UI, transactions included. This is the OPPOSITE of the
 *     DCP-292 rule for tables with their own `facility_id` ("unassigned is not
 *     inactive", see {@link applyActiveFacilityScope}), which still governs
 *     projections / balances / checks and is deliberately left alone.
 *
 * Both hops carry an explicit FK hint. `transactions` will have exactly one FK
 * to `bank_accounts` once the facility FK is gone, so the hint is redundant
 * there today — it is kept because it names the column the join runs on, which
 * is the thing a reader of a 200-character select string actually needs.
 *
 * VERIFIED, not assumed (PostgREST 14.17, local stack): the hint-plus-`!inner`
 * spelling `rel!fk_name!inner(cols)` parses, a nested embed may itself be
 * hinted and inner, and `count=exact` with `head` honours the inner joins
 * (33 rows → 28 with the unassigned account's 5 dropped).
 */
const TRANSACTION_FACILITY_JOIN = 'facilities!bank_accounts_facility_id_fkey!inner(name)'

/**
 * The `transactions.source` values an INTEGRATION wrote, as opposed to the ones
 * a person supplied ('manual' hand entry, 'file_import' spreadsheet upload).
 *
 * An allow-list on purpose: a source added later is kept rather than destroyed
 * until someone decides it belongs here, which is the safe direction for a
 * teardown that cannot be undone. 'plaid' is legacy — the integration is gone,
 * but rows it wrote can still sit on an account that is LedgerSync-linked today.
 */
const INTEGRATION_TRANSACTION_SOURCES = ['ledgersync', 'plaid'] as const

/**
 * The same rule for `balances.source`, which uses its own vocabulary
 * ('api' | 'plaid' | 'manual', see DCP-120) rather than `transaction_source`.
 */
const INTEGRATION_BALANCE_SOURCES = ['api', 'plaid'] as const

/**
 * The scoped `bank_accounts` embed for transaction reads that RENDER the
 * account (Banking Dashboard, Manual Transactions).
 *
 * MUST be paired with {@link applyTransactionFacilityScope}: the embed supplies
 * the join, the helper supplies the `is_active` / explicit-facility predicates
 * that filter through it. Selecting it without the helper silently drops the
 * active-facility half of the rule.
 */
const TRANSACTION_ACCOUNT_EMBED_SCOPED =
  `bank_accounts!transactions_bank_account_id_fkey!inner(name, institution_name, account_number, facility_id, ${TRANSACTION_FACILITY_JOIN})`

/**
 * The scoped embed with NO account columns, for reads that need the join only
 * — the `head: true` counts and the category-filter option reads. Those return
 * either no body at all or one row per transaction, so carrying four unread
 * account columns per row would be pure payload.
 */
const TRANSACTION_SCOPE_EMBED =
  `bank_accounts!transactions_bank_account_id_fkey!inner(${TRANSACTION_FACILITY_JOIN})`

/**
 * The LEFT (non-inner) variant, for the two surfaces that must NOT hide rows.
 *
 *   - the `.select()` a write returns through (`create` / `createMany` /
 *     `update`): an inner join there turns "the row you just wrote sits on an
 *     unassigned account" into a `.single()` error — the write succeeded and
 *     the client would report failure.
 *   - `fileImports.listTransactions`: an IMPORT-scoped drill-down keyed by
 *     `file_import_id`, not a facility-filtered view. Hiding rows there would
 *     make the modal disagree with the import's own "N rows imported" counter
 *     and with its sibling `countTransactions`, which has no embed to join
 *     through.
 *
 * The rendered shape is identical to {@link TRANSACTION_ACCOUNT_EMBED_SCOPED},
 * so a row read back after an edit lands in the same table cells — only the
 * join strictness differs.
 */
const TRANSACTION_ACCOUNT_EMBED =
  'bank_accounts!transactions_bank_account_id_fkey(name, institution_name, account_number, facility_id, facilities!bank_accounts_facility_id_fkey(name))'

/**
 * Alias of the extra embed {@link FACILITY_SORT_EMBED} (DCP-123, AC#6).
 * Referenced by the facility entry of {@link BANK_TRANSACTION_SORT_COLUMNS}, so
 * the select and the order can't name different embeds.
 */
const FACILITY_SORT_EMBED_ALIAS = 'sort_account'

/**
 * A second, aliased `bank_accounts` embed that exists ONLY so the Daily Banking
 * table can be sorted by facility name (DCP-123, AC#6). Selected by
 * `bankTransactions.list` only while that sort is active.
 *
 * Why not order through the embed the row already has: PostgREST orders a
 * parent by a TO-ONE embed one level deep only — `order=bank_accounts(name)`
 * works, `order=bank_accounts(facilities(name))` is rejected with PGRST100.
 * The spread `...facilities(facility_name:name)` hoists the facility name onto
 * this embed, which turns the two-hop path into a one-level
 * `sort_account(facility_name)`. Verified on local PostgREST 14.5.
 *
 * Deliberately a SEPARATE embed rather than a change to
 * {@link TRANSACTION_ACCOUNT_EMBED_SCOPED}: the facility / account-type scope
 * filters target that embed by its `bank_accounts.facilities.*` path, and its
 * shape is what every consumer renders. A LEFT join is enough here — the scoped
 * `!inner` embed already drops the rows without an (active) facility, so this
 * one never nulls out on a row that survives.
 */
const FACILITY_SORT_EMBED =
  `${FACILITY_SORT_EMBED_ALIAS}:bank_accounts!transactions_bank_account_id_fkey(...facilities!bank_accounts_facility_id_fkey(facility_name:name))`

/**
 * The PostgREST `order` terms for every sortable Daily Banking column (DCP-123,
 * AC#6), primary term first. A `Record` over {@link BankTransactionSortKey} so
 * a new sortable column fails to compile until it is mapped here.
 *
 *   - `accountName` / `accountNumber` / `category` order by a TO-ONE embed
 *     (`embed(column)`), which orders the PARENT rows. The look-alike
 *     `.order('name', { referencedTable })` would only order the rows INSIDE the
 *     embed and leave the table order untouched. The embed names are the ones
 *     `bankTransactions.list` actually selects (`bank_accounts` via
 *     {@link TRANSACTION_ACCOUNT_EMBED_SCOPED}, and the unaliased `categories`).
 *   - `facility` goes through {@link FACILITY_SORT_EMBED} — see there why.
 *   - `amount` is stored signed since DCP-250, so the stored order is the
 *     displayed order.
 *   - `sageStatus` (the "API Status" column) orders by `source`, then by
 *     `sync_status` so the status the column actually prints clusters within
 *     each source. BOTH are ENUMs and sort in declaration order, not
 *     alphabetically: `source` as (plaid, manual, file_import, ledgersync),
 *     `sync_status` as (pending, synced, error). So ascending puts the
 *     manual / file-import rows — which the column prints as "-" — BETWEEN the
 *     plaid and the ledgersync rows rather than at one end. Accepted: Plaid is
 *     abandoned, so in practice the "-" rows lead and the bank-feed rows
 *     follow; a true "-" bucket would need a computed column.
 */
const BANK_TRANSACTION_SORT_COLUMNS: Record<BankTransactionSortKey, readonly string[]> = {
  date: ['transaction_date'],
  amount: ['amount'],
  description: ['description'],
  sageStatus: ['source', 'sync_status'],
  accountName: ['bank_accounts(name)'],
  accountNumber: ['bank_accounts(account_number)'],
  category: ['categories(name)'],
  facility: [`${FACILITY_SORT_EMBED_ALIAS}(facility_name)`],
}

/**
 * Apply the Daily Banking table sort to a `bankTransactions.list` query
 * (DCP-123, AC#6). Absent sort = the historical default, newest first.
 *
 * Tie-breakers `transaction_date desc, id asc` are ALWAYS appended (just `id`
 * when the primary key already is the date): most sort keys have long runs of
 * equal values (one facility, one account, "No Category"), and without a total
 * order Postgres may return those runs in a different order per request, so
 * offset pagination would repeat some rows on page 2 and never show others.
 *
 * NULLs: Postgres puts them LAST ascending and FIRST descending. Category is
 * the one exception — uncategorized rows float to the TOP ascending (and to the
 * bottom descending), matching the prototype, where "No Category" rows are the
 * ones the user is there to fix.
 *
 * @param query - a PostgREST query over `transactions` whose select carries the
 *        embeds named in {@link BANK_TRANSACTION_SORT_COLUMNS} for `sort`
 * @param sort - the requested sort; an unknown key falls back to the default
 * @returns the same query, ordered.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function applyBankTransactionSort(query: any, sort?: BankTransactionSort): any {
  // Re-checked although the type already says so: `sort` crosses the URL
  // boundary upstream, and the shared guard is an array lookup, so a key like
  // `constructor` can never resolve to a prototype member of the map above.
  const active = sort && isBankTransactionSortKey(sort.key) ? sort : undefined
  const key: BankTransactionSortKey = active?.key ?? 'date'
  const ascending = active?.direction === 'asc'

  let ordered = query
  for (const column of BANK_TRANSACTION_SORT_COLUMNS[key]) {
    ordered = ordered.order(column, key === 'category' ? { ascending, nullsFirst: ascending } : { ascending })
  }
  if (key !== 'date') ordered = ordered.order('transaction_date', { ascending: false })
  return ordered.order('id', { ascending: true })
}

/**
 * Page size for exhaustive reads (see {@link fetchAllRows}).
 *
 * Matches PostgREST's `db-max-rows` on our projects (`supabase/config.toml:9`;
 * hosted projects inherit the same 1000 default from the dashboard). Asking for
 * a larger window cannot return more rows — the server caps the response either
 * way — so this is the largest page that still costs a single round trip.
 */
const EXHAUSTIVE_PAGE_SIZE = 1000

/**
 * Runaway backstops for {@link fetchAllRows}. NOT business rules.
 *
 * The walk's only natural terminator is an empty page, which trusts the server
 * to eventually send one. A server that keeps answering non-empty pages — a
 * proxy replaying a cached page, a filter silently dropped upstream, a mock that
 * ignores `.range()` — would otherwise spin the tab forever. Both ceilings sit
 * far above any realistic population of the tables these reads cover (bank
 * accounts, Sage accounts, facilities, one day's balances).
 *
 * The request ceiling is separate from the row ceiling because a server capping
 * pages far BELOW {@link EXHAUSTIVE_PAGE_SIZE} needs proportionally more
 * requests to deliver the same rows, and that case must be caught too.
 */
const EXHAUSTIVE_MAX_ROWS = 50_000
const EXHAUSTIVE_MAX_PAGES = 200

/**
 * Reads EVERY row a query matches, one page at a time.
 *
 * PostgREST caps each response at `db-max-rows` (1000 here) and does it
 * SILENTLY: the truncated response carries no error, only HTTP 206 and a
 * `Content-Range` header that `postgrest-js` does not surface as a failure. A
 * truncated read is therefore indistinguishable from a complete one at the call
 * site. That is not hypothetical: this exact truncation shipped as the live
 * DCP-333 defect.
 *
 * The reads wired through this helper are the ones whose result is used as a
 * POPULATION — a denominator, a coverage count, an "is anything missing?"
 * check — where a truncation turns into confident nonsense the moment the table
 * outgrows one page. Those reads must page. That is deliberately NOT a
 * file-wide rule: sibling reads here still take the single-page risk on
 * purpose, because paging them would feed an unbounded id list into an
 * un-chunked `.in()` filter and trade a silent truncation for a hard
 * URL-length failure (see the residual note in `bankBalance.getCoverage`).
 *
 * Three things make the loop safe, and each is easy to get wrong:
 *
 *   - It advances by the number of rows actually RETURNED and stops only on an
 *     EMPTY page, rather than stopping as soon as a page comes back shorter than
 *     it asked for. The short-page test looks equivalent and is not: if
 *     `db-max-rows` is ever configured BELOW {@link EXHAUSTIVE_PAGE_SIZE}, every
 *     page is short, and the very cap this helper exists to defeat would be read
 *     as "last page" — reintroducing the bug in the code written to prevent it.
 *     The price is one extra request per call that comes back empty; paid
 *     deliberately, because the alternative failure is silent.
 *   - The caller MUST end `page`'s sort with a stable, UNIQUE `.order(...)` key.
 *     Postgres guarantees no ordering without ORDER BY, and a non-total order
 *     lets the server arrange tied rows differently per request, so an offset
 *     walk can hand back one row twice and never hand back another.
 *   - It returns an ERROR when a backstop trips instead of returning what it has
 *     so far: a partial population silently mislabelled complete is the original
 *     defect, so the caller must be able to tell the two apart. That loudness
 *     stops at the return value — both current consumers log the error and
 *     degrade to showing no warning, so nothing surfaces to the user.
 *
 * @param page - issues one page of the query over the inclusive row range
 *   [from, to]; must apply the same filters and total order on every call
 * @returns every matching row, or the first error encountered
 */
async function fetchAllRows<T>(
  page: (
    from: number,
    to: number,
  ) => PromiseLike<{ data: T[] | null; error: { message: string; code: string } | null }>,
): Promise<DbResult<T[]>> {
  const all: T[] = []
  let from = 0
  let pages = 0

  for (;;) {
    const { data, error } = await page(from, from + EXHAUSTIVE_PAGE_SIZE - 1)
    if (error) {
      return { data: null, error: { message: error.message, code: error.code } }
    }

    const rows = data ?? []
    if (rows.length === 0) {
      return { data: all, error: null }
    }

    all.push(...rows)
    // Advance by what the server ACTUALLY sent, never by EXHAUSTIVE_PAGE_SIZE:
    // against a lower server cap the latter would step straight over the rows it
    // declined to send.
    from += rows.length
    pages += 1

    // The walk is cut off BEFORE the request that would have proved the table
    // exhausted, so completeness is genuinely unknown here — a table holding
    // exactly EXHAUSTIVE_MAX_ROWS rows trips this on its last FULL page even
    // though nothing was missed. The message says only what is knowable
    // ("cannot be treated as complete"); claiming the result IS incomplete
    // would be a guess that is sometimes plainly false.
    if (all.length >= EXHAUSTIVE_MAX_ROWS || pages >= EXHAUSTIVE_MAX_PAGES) {
      return {
        data: null,
        error: {
          message:
            `Reading this table was stopped after ${all.length.toLocaleString('en-US')} rows ` +
            `over ${pages} requests, at the safety limits of ` +
            `${EXHAUSTIVE_MAX_ROWS.toLocaleString('en-US')} rows and ` +
            `${EXHAUSTIVE_MAX_PAGES} requests. Whether more rows remain was never ` +
            `checked, so this result cannot be treated as complete.`,
          code: 'EXHAUSTIVE_READ_LIMIT',
        },
      }
    }
  }
}

/**
 * Batch size for an `.in()` filter whose id list grows with the data.
 *
 * A PostgREST `in.(...)` filter travels in the request URL, so a list sized by
 * the table itself eventually overflows the server's URL limit and the read
 * fails outright — which is precisely the new failure {@link fetchAllRows}
 * would otherwise introduce, by successfully reading MORE ids than the
 * truncated code ever could. A UUID costs ~39 characters inside the list
 * (36 + separator + encoding), so 100 ids is roughly 4 KB of query string:
 * comfortably inside the common 8 KB request-line limit (nginx's default
 * `large_client_header_buffers`) even after the path, schema header and other
 * filters are added, while keeping the round trips proportional to a realistic
 * account count rather than to one request per id.
 */
const IN_FILTER_CHUNK_SIZE = 100

/**
 * Splits a list into fixed-size batches, preserving order.
 *
 * @param items - the list to split
 * @param size - maximum batch length
 * @returns the batches; an EMPTY input yields no batches at all, so a caller
 *   looping over the result never issues a `.in([])` request. Modern PostgREST
 *   renders `in.()` as `= ANY('{}')`, which matches NOTHING, so such a request
 *   would be harmless but pointless — the callers here short-circuit on an
 *   empty scope to skip it rather than to stay correct
 */
function chunkIds(items: string[], size: number): string[][] {
  const batches: string[][] = []
  for (let index = 0; index < items.length; index += size) {
    batches.push(items.slice(index, index + size))
  }
  return batches
}

/** Shorthand types from the tenant database schema */
type Tables = TenantTables
type IntegrationRow = Tables['integrations']['Row']
type IntegrationInsert = Tables['integrations']['Insert']
type FacilityRow = Tables['facilities']['Row']
type FacilityInsert = Tables['facilities']['Insert']
type PackRow = Tables['packs']['Row']
type PackInsert = Tables['packs']['Insert']

type CategoryRow = Tables['categories']['Row']
type TransactionRow = Tables['transactions']['Row']
// PlaidItemRow and PlaidAccountRow are accessed via TenantPlaidItem/TenantPlaidAccount interfaces
type ProjectionRow = Tables['projections']['Row']
type ProjectionInsert = Tables['projections']['Insert']

/** Per-tenant Sage sync state row (tenant_<uuid>.sage_sync_state) */
export type SageSyncStateRow = Tables['sage_sync_state']['Row']
/** Per-tenant Sage Intacct bank account catalog row (tenant_<uuid>.sage_bank_accounts) */
export type SageBankAccountRow = Tables['sage_bank_accounts']['Row']

/**
 * Projection of `bank_accounts` used by `listByIdsIncludingDeleted`
 * (DCP-222): the subset of columns the edit-dialog needs to render
 * a soft-deleted account in the Bank/Account dropdowns. Kept as a
 * `Pick<>` over the schema row so column type changes flow through
 * automatically.
 */
type BankAccountIncludingDeletedRow = Pick<
  Tables['bank_accounts']['Row'],
  | 'id'
  | 'name'
  | 'institution_name'
  | 'account_number'
  | 'facility_id'
  | 'account_type'
  | 'integration_id'
  | 'deleted_at'
>
/**
 * Per-tenant Sage Intacct AP check register row (tenant_<uuid>.sage_checks).
 *
 * DCP-308: the denormalized `facility_id` column (nullable FK → facilities.id)
 * is declared here as an explicit intersection because the migration that adds
 * it is finalized separately and has NOT been applied to the generated tenant
 * types yet. Once `pnpm db:types` runs post-migration the column will already
 * exist on the base Row and this intersection collapses to a harmless no-op.
 * Filtering the Outstanding Checks list keys off THIS column directly (like
 * every other tenant table), replacing the old facility → sage_location_id →
 * bank account → financial_entity resolution chain.
 *
 * TODO(DCP-308): drop this `& { facility_id }` intersection once migration
 * 20260724010000 is applied on every environment and `pnpm db:types` has
 * regenerated the tenant types with `facility_id` natively.
 */
export type SageCheckRow = Tables['sage_checks']['Row'] & {
  /** Denormalized tenant facility this check belongs to; null when unmapped. */
  facility_id: string | null
}

/** Allowed values for the DCP-owned `dcp_check_status` override (mirrors the DB CHECK constraint). */
export const DCP_CHECK_STATUSES = ['issued', 'paid', 'voided'] as const

/** A user-assignable DCP check status override (DCP-213). */
export type DcpCheckStatus = (typeof DCP_CHECK_STATUSES)[number]

/**
 * Whitelist of sage_checks columns that can be used for server-side sorting
 * via the Outstanding Checks DataTable. Single source of truth — the
 * {@link SageCheckSortColumn} type is derived from this `as const` array
 * (`(typeof SAGE_CHECK_SORT_COLUMNS_LIST)[number]`), and the runtime Set
 * for `.has()` checks is built from the same array. Adding or removing
 * a column requires touching exactly one place; the type and the Set
 * can no longer drift.
 *
 * Restricting to a typed union prevents untrusted column names from
 * reaching `.order()`. Columns derived from joined tables (Bank Account
 * #, Facility) are sorted client-side or not at all.
 */
export const SAGE_CHECK_SORT_COLUMNS_LIST = [
  'when_paid',
  'when_due',
  'doc_number',
  'vendor_name',
  'payee',
  'vendor_id',
  'total_paid',
  'total_entered',
  'state',
  'dcp_check_status',
  'synced_at',
  'date_cashed',
  'record_no',
] as const

export type SageCheckSortColumn = (typeof SAGE_CHECK_SORT_COLUMNS_LIST)[number]

/**
 * Runtime allow-list mirroring {@link SageCheckSortColumn}. The TS type
 * gate alone is fine for our own callers, but `sageChecks.list` accepts
 * options that originate in URL query params / DataTable state, so a
 * defensive runtime check stops a malformed value (typo, deserialisation
 * glitch, future code path passing user input) from reaching
 * PostgREST's `.order()` clause.
 */
const SAGE_CHECK_SORT_COLUMNS: ReadonlySet<SageCheckSortColumn> = new Set(
  SAGE_CHECK_SORT_COLUMNS_LIST,
)

/**
 * Sage Intacct AP-Payment STATE codes we accept from callers of
 * `sageChecks.list({ states })`. Mirrors the documented STATE column
 * surface (`'A'` Approved, `'C'` Cleared/Posted, `'V'` Voided,
 * `'D'` Draft, `'S'` Submitted, `'R'` Reversed); a defensive whitelist
 * in addition to PostgREST's own server-side handling so a typo or
 * malicious value from URL params doesn't quietly degrade to "no
 * filter" inside `.in('state', ...)`.
 */
const VALID_SAGE_CHECK_STATES: ReadonlySet<string> = new Set([
  'A', 'C', 'V', 'D', 'S', 'R',
])

/**
 * Filter `states` against {@link VALID_SAGE_CHECK_STATES} and warn in
 * dev about any dropped values. Extracted from `sageChecks.list` /
 * `sageChecks.count` so the two helpers stay in lockstep — without
 * this, a future change to the validation rule (extra logging, a
 * stricter shape, a metric emit) would have to be applied in two
 * places, and the pagination math (count → totalPages) would diverge
 * the moment the rules drift.
 *
 * @param states - The optional states array from the caller.
 * @param callerName - Label included in the dev warn so the operator
 *                     can tell `list` vs `count` apart in the console.
 * @returns Filtered array (whitelist-only) or `undefined` when the
 *          caller passed nothing — preserves the "no filter" semantic.
 */
function validateSageCheckStates(
  states: readonly string[] | undefined,
  callerName: string,
): readonly string[] | undefined {
  if (!states) return undefined

  const allowed = states.filter((s) => VALID_SAGE_CHECK_STATES.has(s))
  if (allowed.length !== states.length && import.meta.env.DEV) {
    const dropped = states.filter((s) => !VALID_SAGE_CHECK_STATES.has(s))
    console.warn(
      `${callerName}: dropping unknown state codes ${JSON.stringify(dropped)} — not in VALID_SAGE_CHECK_STATES whitelist`,
    )
  }
  return allowed
}

// Plaid types (tenant versions — no organization_id)
export interface TenantPlaidItem {
  id: string
  facility_id: string
  user_id: string | null
  item_id: string
  institution_id: string | null
  institution_name: string | null
  status: 'connected' | 'disconnected' | 'error'
  /** Plaid error code when status is 'error', e.g. 'ITEM_LOGIN_REQUIRED' */
  error_code: string | null
  consent_expiration: string | null
  created_at: string
  updated_at: string
  /** Nested plaid_accounts from `select('*, plaid_accounts(id)')` join */
  plaid_accounts?: Array<{ id: string }>
  /**
   * Nested sync metadata from `plaid_sync_cursors` (1:1 via plaid_item_id).
   * Null when no sync cursor record exists yet for this item.
   */
  plaid_sync_cursors: {
    last_sync_at: string | null
    last_sync_source: 'manual' | 'scheduled' | null
    last_error: string | null
    sync_status: 'idle' | 'syncing' | 'completed' | 'failed'
  } | null
}

/**
 * Per-tenant LedgerSync v3 connection row (tenant_<uuid>.ledgersync_connections).
 * One per bank-link attempt; mirrors the generated tenant table type.
 *
 * The intersected fields are the connection-detail columns added by the
 * DCP-216 migration (logo, freshness, capability snapshots). They are declared
 * here as an explicit extension so the richer banking UI type-checks before the
 * generated `tenant-database.ts` is regenerated post-migration; once `pnpm
 * db:types` runs they will already exist on the base Row and this intersection
 * becomes a harmless no-op. `expected_capabilities` / `realized_capabilities`
 * are loose JSON snapshots from v3 (shape `{ <capability>: { status } }`), so
 * they are typed as `Record<string, unknown>` and guarded at the access site.
 */
export type TenantLedgerSyncConnection = Tables['ledgersync_connections']['Row'] & {
  /** Institution logo URL surfaced by v3; null when the source omits one. */
  logo_url: string | null
  /** ISO timestamp of the last successful refresh; null until first refresh. */
  last_refreshed_at: string | null
  /** Capabilities the source advertised at connect time (loose JSON). */
  expected_capabilities: Record<string, unknown> | null
  /** Capabilities actually realized after the pull (loose JSON, drives badges). */
  realized_capabilities: Record<string, unknown> | null
}
/**
 * Per-tenant LedgerSync v3 account row, now sourced from `bank_accounts`
 * (DCP-217 account-model collapse). A LedgerSync account is a `bank_accounts`
 * row whose `ledgersync_connection_id` is set; the EXPAND migration moved every
 * per-account LedgerSync column onto `bank_accounts`, so the UI reads them here
 * instead of the retired `ledgersync_accounts` table.
 *
 * All per-account LedgerSync columns (provider_type / balance / available_balance
 * / last_refreshed_at / last_transaction_sync_at / last_sync_* / last_balance_sync_*)
 * already live on the regenerated `bank_accounts` Row, so they need no
 * re-declaration here. The ONLY intersection is a narrowing of
 * `ledgersync_connection_id` from the base Row's `string | null` to `string`:
 * this type is only ever used for rows that ARE LedgerSync accounts (queried
 * with `ledgersync_connection_id IS NOT NULL`), so the client-side grouping
 * key is guaranteed present. `provider_type` (raw v3 type, e.g.
 * 'loan'/'investment') is preferred over the collapsed `account_type` enum;
 * the masked number is the base Row's `account_number`.
 */
export type TenantLedgerSyncAccount = Tables['bank_accounts']['Row'] & {
  /** Connection/login the account belongs to; the client-side grouping key.
   *  Narrowed to non-null — only ever populated for rows filtered to have it set. */
  ledgersync_connection_id: string
}

// =============================================================================
// DCP-324 — Manage Bank Connections read model
// =============================================================================
// The two lists on the connection-management screens are served by the RPCs in
// 20260910030000_dcp324_connection_read_model.sql. That migration's header
// explains why they are SQL at all (aggregates, sorting on derived values,
// keyset paging, a filter-matched total); this section is only the typed client
// for them.
//
// WHY THESE ROW TYPES ARE HAND-WRITTEN rather than taken from the generated
// `Functions[...]['Returns']`. Supabase's type generator emits every column of
// a RETURNS TABLE as non-nullable, because a function's row type carries no
// NOT NULL information for it to read. Trusting it would type
// `account_number`, `facility_name`, `balance`, `last_sync_at` and friends as
// `string`/`number` when the RPC genuinely returns null for an unassigned,
// never-synced or balance-less row — and the UI would then skip the null checks
// it actually needs. These interfaces restate the true nullability; the RPC
// results are cast to them at the call site.

/**
 * Sortable columns of the connection list, and the single source of truth for
 * the set. Must stay in lockstep with the whitelist inside
 * `public.get_bank_connections` — the RPC RAISES 22023 on anything it does not
 * recognise, so a value that gets past this type shows up as a failed query
 * rather than as a silently different ordering.
 *
 * `capabilities` sorts on the realized-capability LABELS as text (the same
 * strings the badges render), not on the raw JSON.
 */
export const BANK_CONNECTION_SORT_COLUMNS_LIST = [
  'display_name',
  'accounts_count',
  'status',
  'last_sync_at',
  'capabilities',
] as const

export type BankConnectionSortColumn = (typeof BANK_CONNECTION_SORT_COLUMNS_LIST)[number]

/**
 * Runtime allow-list mirroring {@link BankConnectionSortColumn}, built from the
 * same array so the two cannot drift. The compile-time union is enough for our
 * own callers, but the sort arrives from DataTable state and URL query params,
 * so a malformed value (typo, stale bookmark, bad deserialisation) is checked
 * here and reported as a clear error instead of being sent to the server.
 */
const BANK_CONNECTION_SORT_COLUMNS: ReadonlySet<BankConnectionSortColumn> = new Set(
  BANK_CONNECTION_SORT_COLUMNS_LIST,
)

/** Sortable columns of the per-connection accounts table (mirrors the RPC's whitelist). */
export const CONNECTION_ACCOUNT_SORT_COLUMNS_LIST = [
  'name',
  'account_number',
  'account_type',
  'facility_name',
  'include_in_cash_position',
  'balance',
] as const

export type ConnectionAccountSortColumn = (typeof CONNECTION_ACCOUNT_SORT_COLUMNS_LIST)[number]

/** Runtime allow-list mirroring {@link ConnectionAccountSortColumn}. */
const CONNECTION_ACCOUNT_SORT_COLUMNS: ReadonlySet<ConnectionAccountSortColumn> = new Set(
  CONNECTION_ACCOUNT_SORT_COLUMNS_LIST,
)

/** Sort directions accepted by both read RPCs. */
export const SORT_DIRECTIONS_LIST = ['asc', 'desc'] as const
export type SortDirection = (typeof SORT_DIRECTIONS_LIST)[number]
const SORT_DIRECTIONS: ReadonlySet<SortDirection> = new Set(SORT_DIRECTIONS_LIST)

/**
 * The account status filter (AC#10). `connected` is the DEFAULT everywhere: the
 * accounts table is a management surface for accounts in use, and a bank login
 * can expose dozens of closed or provider-dropped accounts that would bury the
 * handful the user came to configure.
 */
export const CONNECTION_ACCOUNT_STATUS_FILTERS_LIST = ['connected', 'disconnected', 'all'] as const
export type ConnectionAccountStatusFilter = (typeof CONNECTION_ACCOUNT_STATUS_FILTERS_LIST)[number]
const CONNECTION_ACCOUNT_STATUS_FILTERS: ReadonlySet<ConnectionAccountStatusFilter> = new Set(
  CONNECTION_ACCOUNT_STATUS_FILTERS_LIST,
)

/** Default rows per page for the per-connection accounts table. */
export const CONNECTION_ACCOUNTS_DEFAULT_PAGE_SIZE = 50

/**
 * Upper bound on the page size, mirroring the `1..200` guard inside
 * `public.get_connection_accounts`. Checked client-side too so an out-of-range
 * value is a clear local error rather than a 22023 from Postgres.
 */
export const CONNECTION_ACCOUNTS_MAX_PAGE_SIZE = 200

/**
 * Upper bound on the page size, mirroring the `1..100` guard inside
 * `public.get_credit_card_availability`. Checked client-side too so an
 * out-of-range value is a clear local error rather than a 22023 from Postgres.
 */
export const CREDIT_CARD_MAX_PAGE_SIZE = 100

/** Columns the Credit Card Availability table may be ordered by. */
export type CreditCardSortColumn =
  | 'facility'
  | 'name'
  | 'credit_limit'
  | 'balance'
  | 'available_credit'
  | 'statement_balance'
  | 'payment_due_date'
  | 'last_updated'

/**
 * One card on the Credit Card Availability page
 * (`public.get_credit_card_availability`).
 *
 * EVERY MONEY FIELD IS NULLABLE, and null is not zero. The vendor states that
 * an absent field means the bank did not publish it, so a card whose bank
 * stays quiet about its limit must render as "no limit reported" and never as
 * a limit of $0.00 — which would read as a card with no room left.
 */
export interface CreditCardAvailabilityRow {
  id: string
  facility_id: string
  facility_name: string
  pack_id: string | null
  pack_name: string | null
  name: string
  /**
   * What the column holds, which differs by source: for a card connected
   * through LedgerSync it is the vendor's `mask` (its API guarantees "never
   * more than 4 digits"), for a MANUAL card it is whatever the user typed in
   * Banking Settings. The page masks both to the last four per AC#4.3, so the
   * full value never reaches the screen.
   */
  account_number: string | null
  credit_limit: number | null
  /**
   * The card's current balance, signed as the vendor signs it: what is OWED is
   * NEGATIVE. Presentation decides how to show that; the row carries the fact.
   */
  balance: number | null
  /**
   * Remaining credit AS REPORTED. Deliberately not derived from limit minus
   * balance: the two do not reconcile while a charge is pending, and a figure
   * we compute would disagree with the card portal the user is checking.
   */
  available_credit: number | null
  /**
   * AC#8.2's figure: total credit limit minus usage. Computed by the RPC, NULL
   * whenever either operand is missing — a partial sum would report a card as
   * fully drawn on no evidence. A genuine zero is a value and arrives as 0.
   *
   * Distinct from {@link available_credit}, which is what the VENDOR reported.
   * The two do not reconcile while a charge is pending; this is the one the
   * page renders because it is the one the ticket defines.
   */
  availability: number | null
  /** Last closed statement, signed like `balance` (owed is negative). */
  statement_balance: number | null
  minimum_payment: number | null
  /**
   * The bank's own due date, only as current as the last refresh. A date in
   * the past means the cycle rolled over before our last pull, not an overdue
   * payment.
   */
  payment_due_date: string | null
  /**
   * When the figures for this account last CHANGED — the page's "Last
   * Updated". A database trigger owns it, so a sync that re-reads identical
   * numbers does not advance it, and it moves for a balance change just as it
   * does for a new credit limit.
   *
   * Deliberately NOT the vendor's `last_refreshed_at`, which advances on every
   * aggregation whether anything moved or not.
   */
  data_updated_at: string | null
  /**
   * Status of the card's LedgerSync connection; null for a MANUAL card. Raw
   * text: whether the figures stopped refreshing is decided by
   * `connectionNeedsAttention` (every status except `active`/`initiated`,
   * unknown values included).
   */
  connection_status: string | null
  /**
   * The vendor's `last_refreshed_at` for this card — when the bank last
   * delivered its figures. Null for manual cards and when never reported.
   */
  data_refreshed_at: string | null
}

/** Filters, search, ordering and page for {@link CreditCardAvailabilityRow}. */
export interface CreditCardAvailabilityQuery {
  /** `null` entry = the "No Pack" bucket; empty array = match nothing. */
  facilityIds?: (string | null)[]
  packIds?: (string | null)[]
  /**
   * Case-insensitive substring over card name and FULL account number.
   *
   * The number is searched in full even though the table renders it masked to
   * the last four — typing the first digits of a card you know finds it. This
   * is the app's existing behaviour, not a new choice: the DCP-324 connection
   * accounts table masks its display the same way and its RPC
   * (20260910030000) searches the whole column too, and the two pages list the
   * same accounts. Making only this one match the last four would mean the
   * same search string returned different rows on the two screens.
   *
   * The residual leak is small and already open: anyone who can reach this
   * page can open a manual account's edit dialog, which shows the number in full.
   */
  search?: string
  /**
   * Due-date window. Setting EITHER bound excludes cards whose bank publishes
   * no due date: "which cards are due in this window" cannot be answered yes
   * for a card with no due date.
   */
  dueFrom?: string
  dueTo?: string
  sort?: CreditCardSortColumn
  direction?: 'asc' | 'desc'
  /** 1-based, as shown in the pager. */
  page?: number
  pageSize?: number
}

/** The derived, never-stored connection state of a single account. */
export type ConnectionAccountStatus = 'connected' | 'disconnected'

/** One row of the connection list (`public.get_bank_connections`). */
export interface BankConnectionRow {
  id: string
  /** Canonical v3 connection id (`con_…`); null while the connect is in flight. */
  ledgersync_connection_id: string | null
  /** User-set name (AC#5); null when never renamed. */
  name: string | null
  institution_name: string | null
  /** `name` when set and non-blank, otherwise `institution_name`. */
  display_name: string | null
  source: string | null
  status: string
  logo_url: string | null
  last_error: string | null
  expected_capabilities: Record<string, unknown> | null
  realized_capabilities: Record<string, unknown> | null
  /** How far back the first sync should reach (AC#9/AC#23); null = provider default. */
  initial_import_from: string | null
  /** Live (non-soft-deleted) accounts behind this connection. */
  accounts_count: number
  /** Latest transaction OR balance sync across the accounts; null = never synced. */
  last_sync_at: string | null
  /**
   * When the BANK last delivered data, per the vendor (max of the accounts'
   * `last_refreshed_at`) — distinct from `last_sync_at`, the time we fetched.
   * Null when the vendor has reported no refresh.
   */
  data_refreshed_at: string | null
  /** The realized capability labels, comma-joined — the `capabilities` sort key. */
  capabilities_label: string | null
  /**
   * DCP-401: state of the TRANSACTIONS sync pipeline — see
   * `@dcp/shared/ledgersync/sync-state` for the vocabulary. Null when the row
   * was never touched by a tracked sync, and also when the database holds a
   * value this build does not know (normalised in {@link listForManagement}),
   * so a newer server can never crash an older tab's rendering.
   */
  sync_status: LedgerSyncSyncStatus | null
  /** When `sync_status` last changed — what the stall ceiling is measured from. */
  sync_status_at: string | null
  /** Raw reason of the last `failed` transactions sync; null otherwise. */
  sync_error: string | null
  /** DCP-401: state of the BALANCES sync pipeline; same rules as `sync_status`. */
  balance_sync_status: LedgerSyncSyncStatus | null
  /** When `balance_sync_status` last changed. */
  balance_sync_status_at: string | null
  /** Raw reason of the last `failed` balances sync; null otherwise. */
  balance_sync_error: string | null
}

/** One row of the per-connection accounts table (`public.get_connection_accounts`). */
export interface ConnectionAccountRow {
  id: string
  name: string
  account_number: string | null
  account_type: Tables['bank_accounts']['Row']['account_type']
  /**
   * The type the FEED last reported, mapped to our enum (AC#13). Display only —
   * `account_type` above is the effective value every figure and filter uses.
   * Null when the provider has never told us a type, which is different from it
   * having said "checking".
   */
  account_type_api: Tables['bank_accounts']['Row']['account_type'] | null
  /** Raw v3 `subtype`, kept because the enum collapses several real types. */
  provider_subtype: string | null
  facility_id: string | null
  /** Joined facility name; null when the account is Unassigned. */
  facility_name: string | null
  /** Pack of the account's FACILITY (there is no pack column on accounts). */
  pack_id: string | null
  pack_name: string | null
  include_in_cash_position: boolean
  balance: number | null
  available_balance: number | null
  oldest_transaction_date: string | null
  /** Derived from `sync_disabled_at` + `provider_removed_at`, never stored. */
  status: ConnectionAccountStatus
}

/**
 * An opaque keyset position: the sort value of the last row of a page, plus its
 * id. `value` is deliberately nullable — a page can legitimately end inside the
 * trailing NULL block of a nullable sort column (no account number, no
 * facility, no balance yet), and the RPC uses the presence of `id`, not of
 * `value`, to decide whether a cursor was supplied.
 *
 * Treat it as opaque: it comes from the server's `cursor_value` column, which
 * the RPC renders from the very column it sorted by. Do NOT reconstruct it from
 * a row's fields — that would be a second copy of the sort whitelist, and for
 * `balance` it would round through a JS number and land the cursor between rows.
 */
export interface ConnectionAccountsCursor {
  value: string | null
  id: string
}

/** One page of the accounts table, plus what the UI needs to render around it. */
export interface ConnectionAccountsPage {
  rows: ConnectionAccountRow[]
  /**
   * Total rows matching the CURRENT filter set (not the current page) — the
   * "N accounts" badge. Computed by the RPC from the same CTE that produces the
   * page, so it can never disagree with the list.
   *
   * 0 when the page came back empty, which happens either because nothing
   * matches the filter (correct) or because the caller paged past the end (in
   * which case it already had the real total from an earlier page and should
   * keep showing that).
   */
  totalCount: number
  /** Position to pass as `cursor` for the next page; null when this is the last. */
  nextCursor: ConnectionAccountsCursor | null
}

/** Filters/sort/page for {@link ConnectionAccountsPage}. */
export interface ConnectionAccountsQuery {
  /** Defaults to `connected` (AC#10). */
  status?: ConnectionAccountStatusFilter
  /**
   * Facility scope. `undefined` = every facility. A `null` ENTRY means
   * "Unassigned", which is a real option in the facility filter and cannot be
   * spelled as a uuid. An EMPTY array means the caller resolved zero
   * facilities, i.e. "no rows" — the house convention, never "no filter".
   */
  facilityIds?: (string | null)[]
  /** Pack scope, same conventions; a `null` entry means "No Pack". */
  packIds?: (string | null)[]
  /** Case-insensitive substring match on name OR account number (AC#11). */
  search?: string
  sortColumn?: ConnectionAccountSortColumn
  sortDirection?: SortDirection
  pageSize?: number
  cursor?: ConnectionAccountsCursor | null
}

/**
 * Pass an id filter whose NULL ENTRIES are meaningful to an RPC parameter the
 * generated types declare as `string[]`.
 *
 * The parameter really is `uuid[]` in SQL and a NULL ELEMENT is both legal and
 * load-bearing — it is how "Unassigned" / "No Pack" is expressed, matched in the
 * RPC with `array_position(..., NULL)`. The Supabase type generator does not
 * model element nullability (the same blind spot that makes it mark every RPC
 * OUTPUT column non-nullable — see the note on {@link BankConnectionRow}), so
 * `string[]` is the only shape the generated `Args` will accept.
 *
 * A cast is therefore unavoidable, and this is where it is spent so that:
 *   * the INPUT type stays `(string | null)[]`, which is the only thing standing
 *     between the meaningful null and someone "fixing" the type error with a
 *     `.filter(Boolean)` — dropping the null silently turns an "Unassigned"
 *     filter into a filter that matches no account at all;
 *   * the reason is written once instead of once per RPC argument.
 *
 * @param ids - The filter as the UI expresses it, `undefined` for "no filter"
 * @returns The same array, retyped for the generated RPC signature
 */
function nullableIdFilter(ids: (string | null)[] | undefined): string[] | undefined {
  return ids as string[] | undefined
}

/**
 * Derive the cursor for the NEXT page from the page just received.
 *
 * Exported and pure so the paging rule is testable on its own, and so every
 * caller ends pagination the same way. The rule is the standard keyset one:
 *
 *   * fewer rows than asked for  → this was the last page → null. There cannot
 *     be more, because the RPC only ever returns short when it ran out.
 *   * a FULL page → hand back the last row's server-rendered `cursor_value`
 *     and `id`. A full page that happens to be the exact end of the set costs
 *     one extra request that comes back empty; the alternative (guessing there
 *     is no more) would silently truncate the list, which is far worse.
 *   * no rows at all → null.
 *
 * @param rows - The page returned by `ledgerSyncAccounts.listByConnection`
 * @param cursorValues - `cursor_value` per row, positionally aligned with `rows`
 * @param pageSize - The page size that was requested
 * @returns The next keyset position, or null when the list is exhausted
 */
export function nextConnectionAccountsCursor(
  rows: readonly ConnectionAccountRow[],
  cursorValues: readonly (string | null)[],
  pageSize: number,
): ConnectionAccountsCursor | null {
  if (rows.length === 0 || rows.length < pageSize) return null
  const last = rows[rows.length - 1]
  return { value: cursorValues[rows.length - 1] ?? null, id: last.id }
}

export interface TenantPlaidAccount {
  id: string
  plaid_item_id: string
  facility_id: string
  account_id: string
  name: string | null
  official_name: string | null
  type: string | null
  subtype: string | null
  mask: string | null
  balance_available: number | null
  balance_current: number | null
  balance_limit: number | null
  iso_currency_code: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface TenantPlaidTransaction {
  id: string
  transaction_date: string
  description: string
  vendor: string | null
  amount: number
  plaid_category: string | null
  plaid_category_detailed: string | null
  // The BANK's pending flag — distinct from `sync_status`, which is about OUR
  // ingest. A row can be sync_status 'synced' and pending true at the same time
  // (DCP-355). Only bank-feed sources ever set it.
  //
  // Nullable to match the column, not out of caution: `pending BOOLEAN DEFAULT
  // false` carries NO `NOT NULL` (schema.sql:1208), unlike its neighbour
  // `is_reconciled BOOLEAN DEFAULT false NOT NULL`. No writer produces NULL
  // today — every mapper coalesces — but a DEFAULT only fills an OMITTED
  // insert, so an explicit NULL remains storable and PostgREST would hand it
  // straight back. Declaring plain `boolean` would assert something the schema
  // does not, which is what the `=== true` read in BankingDashboardPage guards
  // (PR #208 review #2).
  pending: boolean | null
  plaid_transaction_id: string | null
  plaid_item_id: string | null
  plaid_account_id: string | null
  type: string | null
  sync_status: 'pending' | 'synced' | 'error' | null
  // DCP-223: the Banking Dashboard query now joins on the user-managed
  // bank_accounts + categories tables so non-Plaid rows (manual entries,
  // file imports) render their real account name / number / category
  // instead of falling back to "Unknown Account" / "****" / "Other".
  bank_account_id?: string | null
  category_id?: string | null
  // DCP-173/217: LedgerSync category, read by the drill-down for display.
  // Optional because the generated `transactions` Row type may not carry it;
  // the `select('*')` in `bankTransactions.list` pulls it at runtime. The old
  // `ledgersync_account_id` field is gone — DCP-217 collapsed the LedgerSync
  // account model onto bank_accounts, so rows match by `bank_account_id`.
  ledgersync_category?: string | null
  // DCP-344: row provenance, the discriminator behind the Dashboard's
  // "API Status" column. Optional for the same reason as `ledgersync_category`
  // above — the generated Row type may not carry it, but the `select('*')` in
  // `bankTransactions.list` pulls it at runtime. The DB column is NOT NULL, so
  // the nullability here is defensive only.
  source?: TransactionSource | null
  /**
   * The linked account, with its facility nested (DCP-324). `facilities` is the
   * ONLY source of a transaction's facility now that `transactions.facility_id`
   * is gone — see {@link TRANSACTION_FACILITY_JOIN}.
   *
   * The whole object and the nested `facilities` are typed nullable even though
   * `bankTransactions.list` selects them through `!inner` (so at runtime a row
   * that reached the caller always has both): the same interface is the return
   * type of the LOC drill-down and the CSV export path, and asserting
   * non-nullability here would put the guarantee in the type system rather than
   * in the query that actually provides it.
   */
  bank_accounts?: {
    name: string | null
    institution_name: string | null
    account_number: string | null
    facility_id: string | null
    facilities: { name: string } | null
  } | null
  categories?: {
    name: string | null
    type: 'inflow' | 'outflow' | null
  } | null
  /**
   * DCP-123: present only when `bankTransactions.list` sorts by facility — the
   * embed exists to be ordered by (see FACILITY_SORT_EMBED) and nothing reads
   * it. Render the facility from `bank_accounts.facilities` instead.
   */
  sort_account?: { facility_name: string | null } | null
}

/**
 * Represents a manually entered transaction row from the transactions table.
 * Includes the joined bank_account (with ITS facility), category, and profile
 * names for display purposes.
 *
 * DCP-324: neither `facility_id` nor a top-level `facilities` embed is declared
 * any more. A transaction is not linked to a facility — its facility is always
 * its ACCOUNT's, read at `bank_accounts.facilities.name`. See
 * {@link TRANSACTION_FACILITY_JOIN}.
 */
export interface TenantTransaction {
  id: string
  transaction_date: string
  description: string
  vendor: string | null
  amount: number
  source: TransactionSource | null
  bank_account_id: string | null
  category_id: string | null
  user_id: string | null
  type: string | null
  sync_status: 'pending' | 'synced' | 'error' | null
  created_at: string
  updated_at: string
  /**
   * The linked account. `facility_id` / `facilities` are the transaction's
   * facility: the id is what the edit dialog seeds its Facility picker from,
   * the name is what the table renders. Nullable throughout because
   * `transactions.bank_account_id` and `bank_accounts.facility_id` both are,
   * and because the write-return and file-import paths read this shape through
   * a LEFT embed ({@link TRANSACTION_ACCOUNT_EMBED}).
   */
  bank_accounts: {
    name: string
    institution_name: string | null
    account_number: string | null
    facility_id: string | null
    facilities: { name: string } | null
  } | null
  /**
   * Joined category. `type` is retained for backward compatibility only and is
   * nullable (DCP-250): display sign now comes from the amount value itself, not
   * the category type. `type` is null when unset, and the whole object is null
   * when the row has no category or the category was deleted.
   */
  categories: { name: string; type: 'inflow' | 'outflow' | null } | null
  profiles: { full_name: string } | null
  /**
   * Free-text notes (DCP-169). There is no dedicated `notes` column on the
   * tenant `transactions` table — the value is stored at `metadata.notes`
   * (the convention established by the DCP-290 `get_loc_transactions` RPC).
   * `list` surfaces it as a first-class string (empty string when unset) so
   * consumers never have to reach into the raw `metadata` JSON.
   */
  notes: string
}

/**
 * Row shape returned by `tenant.projections.listProjections(fileImportId)`.
 *
 * Pulled out as a named type so the `listProjections` method signature
 * stays terse (the inline form was duplicated verbatim between the
 * `Promise<Array<...>>` return type and the trailing `as unknown as
 * Array<...>` cast, which made structural drift between the two ends
 * a real risk during edits). Keeping the joined-FK shape narrowed to
 * `{ name }` (rather than reusing a generated full-row type) is
 * deliberate: this is the *display* projection, only the fields the
 * details modal renders, so a wider type would invite over-selection
 * and column-bloat creep.
 */
export interface FileImportProjectionRow {
  id: string
  date: string
  description: string | null
  /** Single signed amount: positive = inflow, negative = outflow (DCP-250 Phase 1). */
  amount: number
  notes: string | null
  categories: { name: string } | null
  facilities: { name: string } | null
}

/** Represents a row from plaid_sync_cursors with fields relevant to sync status monitoring */
export interface TenantSyncCursor {
  id: string
  plaid_item_id: string
  sync_status: 'idle' | 'syncing' | 'completed' | 'failed'
  last_sync_at: string | null
  last_sync_source: 'manual' | 'scheduled' | null
  transactions_synced: number | null
  last_error: string | null
}

/**
 * Flatten a transaction row's `metadata.notes` into a first-class `notes`
 * string (DCP-169). Notes have no dedicated column — they live at
 * `metadata.notes` (same convention as the DCP-290 `get_loc_transactions` RPC).
 * `TenantTransaction.notes` is a REQUIRED string, so EVERY method returning a
 * TenantTransaction (list / create / createMany / update) must run this —
 * otherwise a caller reading `.notes` off a create/update result gets
 * `undefined` while the type promises `string` (the `as unknown as` casts hide
 * it from the compiler). Mutates and returns the row so it can be mapped/chained.
 */
function flattenNotes<T>(row: T): T {
  const metadata = (row as unknown as { metadata?: { notes?: string } | null }).metadata
  ;(row as unknown as { notes: string }).notes = metadata?.notes ?? ''
  return row
}

/**
 * Collapse concurrent calls of a zero-arg async reader into ONE in-flight
 * request: while a call is pending every caller gets the same promise; as soon
 * as it settles the slot is released, so the next call hits the network again.
 *
 * DCP-292 (Presto #3): one Banking Dashboard KPI load fans out to four reads of
 * the same tiny `facilities` table (getTotal, getCoverage, sumIntacctBalance,
 * getIntacctFreshness — one each). They all run inside a single `Promise.all`,
 * so deduping the in-flight promise collapses those four round-trips into TWO —
 * one per distinct SELECT, since the two memoised readers ask different
 * questions: `activeFacilityIds` (id WHERE is_active) backs the balance tiles,
 * `facilitySageLocationIndex` (the sage_location_id catalog) backs the Intacct
 * ones. Merging them into a single snapshot read would get to one, but couples
 * two unrelated call sites for one saved round-trip on a tens-of-rows table —
 * deliberately not done (Presto #14).
 *
 * Deliberately NOT a time- or value-cache: `createTenantDb` memoises the wrapper
 * per orgId for the lifetime of the module (see `tenantDbCache`), so anything
 * retained across calls would keep serving a stale facilities list after a
 * facility is (de)activated — exactly the bug this ticket fixes. Releasing on
 * settle keeps the window to the concurrency of a single logical operation.
 *
 * @param fn - the reader to protect; it must be safe to share one result among
 *             all concurrent callers (our readers return immutable snapshots)
 * @returns a drop-in replacement for `fn` with the dedup applied
 */
function memoizeInFlight<T>(fn: () => Promise<T>): () => Promise<T> {
  let inFlight: Promise<T> | null = null
  return () => {
    if (inFlight) return inFlight
    const p = fn()
    inFlight = p
    // Release on settle, whichever way it settles. `then(clear, clear)` (rather
    // than `.finally`) also swallows a rejection on this bookkeeping branch, so
    // a failing read can't surface as an unhandled rejection here — the real
    // caller still sees the original promise reject.
    const clear = () => {
      if (inFlight === p) inFlight = null
    }
    void p.then(clear, clear)
    return p
  }
}

/**
 * Build the tenant DB wrapper object for a given orgId.
 * Separated from createTenantDb so ReturnType<typeof _buildTenantDb> can be used
 * for the cache without circular inference.
 */
function _buildTenantDb(orgId: string) {
  const client = getTenantClient(orgId)

  /**
   * DCP-218: resolve the latest known bank balance per bank account from the
   * integration-agnostic tenant `balances` table (Plaid is abandoned — the old
   * `plaid_balances_history` source is dead).
   *
   * PostgREST has no DISTINCT ON, so we fetch all candidate rows ordered by
   * (date desc, updated_at desc) and keep the first row seen per
   * `bank_account_id` — that's the newest snapshot, with `updated_at` breaking
   * same-day ties. This mirrors the proven merge in `balances.listImportStatus`.
   *
   * @param endDate        Upper-bound date (YYYY-MM-DD).
   * @param opts.facilityIds  Optional facility scope (skipped when empty/undefined).
   * @param opts.accountIds   Optional bank-account scope. When provided AND
   *        empty, short-circuits to an empty Map without querying (same
   *        convention as listImportStatus / sageChecks). NB: `.in([])` does NOT
   *        leak — postgrest-js appends `col=in.()`, which PostgREST answers with
   *        zero rows (measured on this stack, PR #183 review). The short-circuit
   *        is kept because not issuing a request that can only return nothing is
   *        cheaper and states the intent, not because the filter is unsafe.
   * @returns Map keyed by `bank_account_id` → `{ balance, date }`, or a DbResult
   *          error when the underlying query fails.
   */
  /**
   * DCP-292: resolve the ACTIVE facilities' ids for the current tenant. Used to
   * scope balance / transaction / check / projection reads so a DEACTIVATED
   * facility's records are excluded from KPI tiles and tables when the user has
   * not picked an explicit facility filter.
   *
   * Kept as a tiny standalone read (the facilities catalog is small — tens of
   * rows per tenant) rather than a PostgREST embedded join, because callers must
   * KEEP rows whose `facility_id IS NULL` (unassigned — NOT inactive), which an
   * `!inner` embed cannot express.
   *
   * @returns the active facilities' ids (empty array = tenant has none active).
   */
  const activeFacilityIds = memoizeInFlight(async (): Promise<DbResult<string[]>> => {
    const { data, error } = await client
      .from('facilities')
      .select('id')
      .eq('is_active', true)
    if (error) {
      return { data: null, error: { message: error.message, code: error.code } }
    }
    return { data: (data ?? []).map((r) => (r as { id: string }).id), error: null }
  })

  /**
   * DCP-292 (Presto #1): narrow an EXPLICIT `facilityIds` filter to the ones that
   * are still active.
   *
   * The scope helpers used to trust the caller outright ("these ids come from the
   * active-only selector"). That holds for the current UI — the dashboard filters
   * live in component `useState`, so a selection never outlives a page load — but
   * it is an assumption about the call site, not a guarantee of this layer. A
   * facility deactivated in another tab (or by another user) while a filter is
   * held would keep feeding its rows into the tables and tiles until the operator
   * happened to touch the filter.
   *
   * Anchoring the rule here instead makes "inactive is hidden" a property of the
   * data layer, so no present or future caller can opt out of it by accident.
   *
   * @param facilityIds - the caller's explicit, non-empty facility filter
   * @returns the subset that is still active — EMPTY means every requested
   *          facility was deactivated, which callers must render as "no rows"
   *          (never as "no filter"; see {@link applyActiveFacilityScope}).
   */
  async function narrowToActiveFacilityIds(facilityIds: string[]): Promise<DbResult<string[]>> {
    const { data: activeIds, error } = await activeFacilityIds()
    if (error) return { data: null, error }
    const active = new Set(activeIds)
    return { data: facilityIds.filter((id) => active.has(id)), error: null }
  }

  /**
   * DCP-292: apply the default "active facilities only" facility scope to a
   * PostgREST query over a table that has a `facility_id` column. An explicit
   * non-empty `facilityIds` filter is honoured but narrowed to the ids that are
   * still active (Presto #1 — see {@link narrowToActiveFacilityIds}). Rows with
   * `facility_id IS NULL` (unassigned — NOT inactive) are always kept when no
   * explicit filter is given.
   *
   * Callers that treat an EMPTY `facilityIds` array as "zero facilities → no
   * rows" MUST short-circuit that case before calling this helper: here an
   * empty/absent array both mean "no explicit filter → active scope".
   *
   * @returns the narrowed query (error null), or an error DbResult when resolving
   *          the active facilities failed.
   */
  async function applyActiveFacilityScope(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    query: any,
    facilityIds?: string[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<{ query: any; error: DbError | null }> {
    if (facilityIds?.length) {
      const { data: stillActive, error } = await narrowToActiveFacilityIds(facilityIds)
      if (error) return { query: null, error }
      // Every requested facility was deactivated → render as "no rows".
      // Expressed as a contradiction (`facility_id IS NULL AND facility_id IS
      // NOT NULL`, ANDed by PostgREST) rather than `.in('facility_id', [])`.
      // Correction to an earlier version of this comment (PR #183 review):
      // `.in([])` is NOT a leak — postgrest-js appends `col=in.()` and PostgREST
      // answers zero rows, measured on this project's stack. Both forms are
      // therefore correct; the contradiction is kept because it says "match
      // nothing" outright instead of relying on how an empty IN-list is parsed.
      if (stillActive.length === 0) {
        return { query: query.is('facility_id', null).not('facility_id', 'is', null), error: null }
      }
      return { query: query.in('facility_id', stillActive), error: null }
    }
    const { data: activeIds, error } = await activeFacilityIds()
    if (error) return { query: null, error }
    const scoped = activeIds.length
      ? query.or(`facility_id.is.null,facility_id.in.(${activeIds.join(',')})`)
      : query.is('facility_id', null)
    return { query: scoped, error: null }
  }

  /**
   * DCP-324 / DCP-292: the facility scope for TRANSACTION reads.
   *
   * This is the transaction-side replacement for
   * {@link applyActiveFacilityScope}, which cannot be used here any more: it
   * keys on the table's own `facility_id`, and `transactions` no longer has one
   * (migration 20260911000000 — see {@link TRANSACTION_FACILITY_JOIN} for why
   * the column had to go). Every predicate below therefore addresses the
   * account's facility THROUGH the embed, using PostgREST's dotted
   * embedded-resource filter syntax.
   *
   * TWO RULES AT ONCE, both enforced server-side in a single request:
   *   (a) DCP-292 — a DEACTIVATED facility's transactions are not shown. Under
   *       the old helper this was a client-side `IN` over ids fetched by a
   *       separate `activeFacilityIds()` round trip; `is_active` is now read on
   *       the joined row, so there is no second read and no window in which the
   *       two disagree.
   *   (b) DCP-324 AC#22 — an account assigned to NO facility is hidden. That is
   *       carried by the `!inner` hops of the embed itself, not by a predicate
   *       here, which is precisely why selecting the embed and applying this
   *       helper are a package deal.
   *
   * AND THE PACKAGE DEAL IS NOT A STYLE POINT. A filter on an embedded column
   * does NOT filter the PARENT rows — it filters the embedded ones, nulling a
   * to-one embed that fails; only `!inner` turns "embed is null" into "drop the
   * parent". So an embed downgraded to LEFT does not merely stop hiding
   * unassigned accounts, it silently deletes rule (a) as well, because the
   * `is_active` predicate stops reaching the rows. Measured on this stack
   * (PostgREST 14.17) over 33 transactions: LEFT + the filter → 33 rows,
   * `!inner` + the same filter → 28.
   *
   * `narrowToActiveFacilityIds` is deliberately NOT used for the explicit
   * filter. Its job was to intersect the caller's ids with the active set in
   * JS; here `is_active = true` AND `facility_id IN (requested)` compute the
   * same intersection in SQL, so a filter naming a since-deactivated facility
   * yields zero rows with no extra round trip and no stale-snapshot race.
   *
   * An ABSENT or EMPTY `facilityIds` means "no explicit filter" — the house
   * convention this file uses everywhere (see {@link applyActiveFacilityScope}).
   * Callers that mean "zero facilities → zero rows" must short-circuit first.
   *
   * Unlike its predecessor this helper is SYNCHRONOUS and returns the query
   * directly rather than a `DbResult`: with nothing to pre-fetch there is no
   * error to propagate.
   *
   * @param query - a PostgREST query over `transactions` whose select already
   *        includes {@link TRANSACTION_ACCOUNT_EMBED_SCOPED} or
   *        {@link TRANSACTION_SCOPE_EMBED}
   * @param facilityIds - the caller's explicit facility filter, if any
   * @returns the same query, narrowed.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function applyTransactionFacilityScope(query: any, facilityIds?: string[]): any {
    const scoped = query.eq('bank_accounts.facilities.is_active', true)
    return facilityIds?.length ? scoped.in('bank_accounts.facility_id', facilityIds) : scoped
  }

  /**
   * DCP-400: narrow a PostgREST query over `transactions` to the rows whose
   * account is NOT one of {@link DAILY_BANKING_EXCLUDED_ACCOUNT_TYPES} (today:
   * `credit_card`).
   *
   * The predicate is embedded — `bank_accounts.account_type` — over the same
   * `!inner` hop already carried by {@link TRANSACTION_ACCOUNT_EMBED_SCOPED} /
   * {@link TRANSACTION_SCOPE_EMBED}, so a card's transaction row is DROPPED by
   * PostgREST rather than merely losing its embedded account, exactly like
   * {@link applyTransactionFacilityScope}.
   *
   * `NOT IN` is safe here for the same reason it is in
   * {@link applyBankBalanceAccountTypeScope}: `account_type` is `NOT NULL`, so
   * there is no NULL branch to silently lose, and the interpolated list is
   * built from compile-time enum literals, never user input.
   *
   * @param query - a PostgREST query over `transactions` whose select already
   *        includes {@link TRANSACTION_ACCOUNT_EMBED_SCOPED} or
   *        {@link TRANSACTION_SCOPE_EMBED}
   * @returns the same query, narrowed.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function applyDailyBankingAccountTypeScope(query: any): any {
    return query.not('bank_accounts.account_type', 'in', `(${DAILY_BANKING_EXCLUDED_ACCOUNT_TYPES.join(',')})`)
  }

  /**
   * DCP-400: the single entry point for scoping a Daily Banking query — facility
   * scope ({@link applyTransactionFacilityScope}) composed with account-type
   * scope ({@link applyDailyBankingAccountTypeScope}). Used by every TRANSACTION
   * read behind the Daily Banking page (`bankTransactions.list`/`count`/both
   * branches of `distinctCategories`) so the table, its pagination count, its
   * CSV export and the Category-filter options cannot drift from one another.
   * The page's KPI tiles read other namespaces and exclude cards on their own
   * (DCP-371, {@link applyBankBalanceAccountTypeScope}).
   *
   * @param query - a PostgREST query over `transactions` whose select already
   *        includes {@link TRANSACTION_ACCOUNT_EMBED_SCOPED} or
   *        {@link TRANSACTION_SCOPE_EMBED}
   * @param facilityIds - the caller's explicit facility filter, if any
   * @returns the same query, narrowed.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function applyDailyBankingScope(query: any, facilityIds?: string[]): any {
    return applyDailyBankingAccountTypeScope(applyTransactionFacilityScope(query, facilityIds))
  }

  /**
   * DCP-338: narrow a PostgREST query over `bank_accounts` to the accounts that
   * still BELONG TO A BANK — i.e. everything except the ORPHANS a pre-fix
   * disconnect left behind. An account qualifies when it is EITHER
   *   (a) manual — `integration_id IS NULL`, or
   *   (b) integration-backed AND still carrying a link,
   *       `ledgersync_connection_id IS NOT NULL`.
   *
   * WHAT THIS DROPS, AND WHY IT IS THE WHOLE POINT. The excluded shape is
   * `integration_id` set + `ledgersync_connection_id IS NULL`. That combination
   * is produced by exactly one thing: deleting a `ledgersync_connections` row
   * while its accounts survive — the FK is `ON DELETE SET NULL` (DCP-217
   * contract migration) and `integration_id` is deliberately NOT cleared, so it
   * remains a valid "this row came from an API" signal for the UI. Such an
   * account has no bank behind it at all: nothing will ever refresh it, and its
   * last `balances` row is frozen at the moment the connection went away. On
   * staging five of them sat under El Campo and overstated its Bank Balance by
   * $583,614.33. Migration `20260818010000` deleted that backlog.
   *
   * THIS PREDICATE IS LOAD-BEARING AGAIN AS OF DCP-324. Deleting a connection
   * (`delete-connection` edge action, AC#7,
   * `_shared/ledgersync-connection-delete.ts`) now HARD-deletes the
   * `ledgersync_connections` row, so `ON DELETE SET NULL` DOES fire and every
   * account of that connection ends up in exactly the excluded shape. That is
   * intended, and it is belt-and-braces rather than the only guard: the same
   * accounts are soft-deleted in the same teardown, and every money read filters
   * `deleted_at IS NULL` first. This predicate is what still holds if an account
   * reaches the NULL-link state WITHOUT a stamp — a sync racing a delete, or a
   * future path that removes a connection without going through the teardown.
   *
   * WHY A BROKEN CONNECTION IS *NOT* DROPPED (product decision on DCP-338,
   * confirmed 2026-08-18). Statuses
   * `failed` (credentials/consent died at the bank) and `disconnected` (the user
   * revoked access upstream) both stop the data flowing while the connection row
   * — and therefore the link — survives. Their last known balance IS still
   * counted in Bank Balance and in the Cash Position Summary, and the account
   * stays listed in the Bank Balance Update table carrying a visible "connection
   * lost" marker (see `listImportStatus` → `connectionStatus`). The rejected
   * alternative was to exclude them: it makes a facility's cash silently drop
   * with no explanation and no user action other than reconnecting, which is a
   * worse failure than a figure that is visibly stale.
   *
   * `requires_action` is treated the same way: an existing connection can fall
   * back to it (the bank wants a fresh sign-in or MFA, or a Reconnect is in
   * progress), its accounts stay linked and listed, and the marker reads
   * "Action required". `initiated` needs no casing: accounts are only
   * materialised by `finalizeConnection`, which flips the connection to `active`
   * in the same pass, so an `initiated` connection owns none.
   *
   * NB: this adds a SECOND `.or=` param on call sites that also apply the
   * active-facility scope ({@link applyActiveFacilityScope}). PostgREST ANDs
   * repeated logical-operator params, so the two OR groups compose as
   * `(facility scope) AND (linked scope)` — verified against PostgREST, not
   * assumed.
   *
   * Unlike the facility scope this needs NO pre-query: the rule reads two columns
   * of `bank_accounts` itself, so it is one predicate with no round trip, no
   * id-list in the URL and nothing to memoise.
   *
   * @param query - a PostgREST query builder over `bank_accounts`
   * @returns the same query, narrowed.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function applyLinkedAccountScope(query: any): any {
    return query.or('integration_id.is.null,ledgersync_connection_id.not.is.null')
  }

  /**
   * DCP-371: narrow a PostgREST query over `bank_accounts` to the accounts that
   * hold CASH — i.e. everything except the types listed in
   * {@link BANK_BALANCE_EXCLUDED_ACCOUNT_TYPES} (today: `credit_card`).
   *
   * WHY. A credit card is a LIABILITY, not a cash position: its balance is what
   * the tenant owes, so adding it to Bank Balance reported money that does not
   * exist and, through the same chain as DCP-338, corrupted every KPI derived
   * from it (Book Balance, Coverage). The card also appeared in the "Bank
   * Balance Update" table, inviting the operator to keep a figure current that
   * must never be summed in the first place.
   *
   * READ-SIDE ONLY, AND DELIBERATELY SO. Nothing is deleted or hidden from the
   * places that MANAGE accounts: `bankAccounts.list` (selectors, filters,
   * Settings) and the edit/delete paths still return cards untouched. This is
   * NO LONGER true of every transaction surface, though: Daily Banking now
   * excludes card transactions too, via the separate DCP-400
   * {@link applyDailyBankingAccountTypeScope} / {@link DAILY_BANKING_EXCLUDED_ACCOUNT_TYPES}
   * (same `credit_card` scope, applied over `transactions` instead of
   * `bank_accounts`). This predicate itself is applied at exactly the three
   * read surfaces the ticket names — {@link scopedActiveAccountIds} (the single
   * source of truth behind the Bank Balance / Book Balance / Coverage reads),
   * `getCoverageByFacility` (which owns a SEPARATE `bank_accounts` read) and
   * both halves of `balances.listImportStatus`.
   *
   * The rule is source-agnostic: it reads `account_type`, so a card behaves the
   * same whether LedgerSync classified it or a user picked the type by hand.
   *
   * Like {@link applyLinkedAccountScope} this needs NO pre-query — it is one
   * predicate over a column of `bank_accounts` itself.
   *
   * `NOT IN` IS SAFE HERE. In SQL `NULL NOT IN (...)` is NULL, i.e. the row is
   * dropped — but `bank_accounts.account_type` is `NOT NULL` (schema.sql, and
   * the DCP-217 collapse defaults an unmapped LedgerSync type to `'other'`), so
   * there is no NULL branch to lose. The interpolated list is likewise safe to
   * build by hand: the values are compile-time enum literals (`[a-z_]+`), never
   * user input, so they carry no PostgREST grammar characters to escape — same
   * shape as the `not.in` list in `facilities.clearPackExcept`.
   *
   * @param query - a PostgREST query builder over `bank_accounts`
   * @returns the same query, narrowed.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function applyBankBalanceAccountTypeScope(query: any): any {
    return query.not(
      'account_type',
      'in',
      `(${BANK_BALANCE_EXCLUDED_ACCOUNT_TYPES.join(',')})`,
    )
  }

  /**
   * DCP-324 AC#19 (Predicate A): narrow a PostgREST query over `bank_accounts`
   * to the accounts whose balance may reach a REPORTED CASH FIGURE.
   *
   * An account qualifies only when ALL of these hold:
   *
   *   - `deleted_at IS NULL` — not soft-deleted (DCP-222). Also the state a
   *     DELETED CONNECTION leaves its accounts in (AC#21): the connection row
   *     is soft-deleted and every account under it with it, their `balances`
   *     rows are hard-deleted, and their transactions stay.
   *   - `sync_disabled_at IS NULL` — no human pressed Disconnect on this
   *     account (AC#16).
   *   - `provider_removed_at IS NULL` — the aggregator still returns it (AC#8).
   *     Those two columns together are the "Connected" status the accounts
   *     table renders; it is DERIVED, never stored, so this is the only correct
   *     way to ask the question.
   *   - `facility_id IS NOT NULL` — assigned to a facility (AC#22).
   *   - `include_in_cash_position` — the explicit opt-in (AC#2 / AC#15).
   *
   * WHY. Before this, `include_in_cash_position` was read by exactly one thing —
   * the per-connection accounts table that writes it — so the toggle the AC#2
   * UI offered did nothing to any number on any screen, and a Disconnected or
   * Unassigned account kept contributing the last balance it happened to be
   * left with. This helper is what makes section C of the ticket real.
   *
   * TWO CHANGES OF BEHAVIOUR WORTH NAMING, both intended:
   *
   *   1. `facility_id IS NOT NULL` SUPERSEDES the DCP-292 decision to keep
   *      unassigned (NULL-facility) accounts in {@link scopedActiveAccountIds}.
   *      That decision was about INACTIVE facilities — "unassigned is not
   *      inactive" — and it predates AC#22, which says an unassigned account is
   *      reported nowhere. The happy side effect is that the tiles now describe
   *      the SAME population as `get_cash_position_summary`, whose
   *      `latest_balance` CTE has required a facility since DCP-143: a
   *      facility-keyed report never had anywhere to put an unattributed
   *      balance, so the tile and the report used to disagree by exactly those
   *      accounts.
   *   2. It is the opposite of the DCP-338 ruling that a BROKEN connection
   *      keeps counting. That still stands — a break is an accident whose only
   *      remedy is reconnecting, and silently hiding the cash would be
   *      unexplainable. A DISCONNECT is the user's own instruction, and
   *      `provider_removed_at` means there is no bank behind the row at all.
   *
   * Like {@link applyLinkedAccountScope} and
   * {@link applyBankBalanceAccountTypeScope} this needs NO pre-query: all five
   * conjuncts read columns of `bank_accounts` itself, so it is one predicate
   * with no round trip and nothing to memoise.
   *
   * `deleted_at IS NULL` is included even though all three call sites already
   * apply it. Repeating a filter is free in PostgREST (identical predicates
   * AND together) and it keeps the helper a COMPLETE statement of the rule —
   * the reason this is one shared function and not five open-coded chains is
   * that the surfaces must not be able to drift.
   *
   * COMPOSES WITH THE FACILITY SCOPES, verified rather than assumed:
   * `.not('facility_id', 'is', null)` ANDs with the DCP-292 `.or(...)` group
   * (`facility_id.is.null,facility_id.in.(…)`) to leave exactly "in an active
   * facility", and with the empty-active-set form `.is('facility_id', null)` it
   * forms the deliberate contradiction that means "no rows" — the same shape
   * {@link applyActiveFacilityScope} already uses.
   *
   * `include_in_cash_position` is matched with `.eq(col, true)` rather than
   * `.is(col, true)`: the column is `BOOLEAN NOT NULL`, so there is no third
   * state, and `eq` is the form the rest of this file uses for booleans.
   *
   * NOT APPLIED where accounts are MANAGED or where transactions are listed.
   * `bankAccounts.list` (selectors, Settings, the add/edit dialogs), the
   * per-connection accounts table and every transaction surface keep returning
   * these rows untouched — AC#20 is explicit that a disconnected account's
   * transactions are still displayed. Only the AGGREGATES change.
   *
   * @param query - a PostgREST query builder over `bank_accounts`
   * @returns the same query, narrowed.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function applyCashPositionScope(query: any): any {
    return query
      .is('deleted_at', null)
      .is('sync_disabled_at', null)
      .is('provider_removed_at', null)
      .not('facility_id', 'is', null)
      .eq('include_in_cash_position', true)
  }

  /**
   * DCP-292 / DCP-335: one read of the facility↔Sage-location catalog, projected
   * two ways.
   *
   *   - `inactiveKeys` — UPPER-cased `sage_location_id`s whose WINNING facility is
   *     inactive (the original DCP-292 deny set, semantics unchanged).
   *   - `keyByFacilityId` — every mapped facility's own UPPER-cased key, used by
   *     {@link intacctLocationScope} to resolve an explicit `facilityIds` filter
   *     for the Intacct Balance tile (DCP-335).
   *
   * Both come from the SAME single SELECT that DCP-292 already issued — the rows
   * needed to answer "which key does facility X own" were always on the wire, so
   * scoping the tile added no round-trip. (This is not the merge declined as
   * Presto #14 above: that one would have fused two DIFFERENT selects,
   * `activeFacilityIds` and this one. Here both consumers are the same two Intacct
   * readers that already shared this reader.)
   *
   * `sage_bank_accounts` has NO `facility_id` column — it links to a facility
   * only through `location_id → facilities.sage_location_id` (case-insensitively,
   * per DCP-262). The Intacct Balance tile/table therefore exclude a deactivated
   * facility's accounts by matching their `location_id` against `inactiveKeys`. An
   * account whose location maps to NO facility (unmapped) is not in the set and
   * is kept — same "NULL ≠ inactive" rule used elsewhere in DCP-292.
   *
   * WHY THE WHOLE CATALOG IS READ, NOT JUST THE INACTIVE ROWS (PR #183 review).
   * `sage_location_id` is `TEXT UNIQUE` — case-SENSITIVELY — and its format CHECK
   * permits mixed case, so `LOC-1` and `loc-1` can both exist, on two different
   * facilities. Collecting only the inactive rows and upper-casing them put the
   * key `LOC-1` in the deny set as soon as the `loc-1` facility was switched off,
   * which silently removed the ACTIVE `LOC-1` facility's Sage accounts from the
   * Intacct tile and table: money missing from a facility that is switched on,
   * with nothing on screen to explain it.
   *
   * The winner is resolved with the SAME rule the rest of the product already
   * uses for this exact collision — active first, then lowest id — spelled out in
   * `20260730000000_dcp308_facility_id_single_source.sql`
   * (`ORDER BY sba.financial_entity_id, f.is_active DESC, f.id`) and mirrored by
   * the sync tasks. A location is denied only when its winner is inactive, so one
   * active owner is enough to keep the money visible.
   */
  const facilitySageLocationIndex = memoizeInFlight(
    async (): Promise<
      DbResult<{ inactiveKeys: Set<string>; keyByFacilityId: Map<string, string> }>
    > => {
      const { data, error } = await client
        .from('facilities')
        .select('id, sage_location_id, is_active')
        .not('sage_location_id', 'is', null)
      if (error) return { data: null, error: { message: error.message, code: error.code } }

      // location key → the facility that owns it under `is_active DESC, id ASC`.
      const winnerByKey = new Map<string, { id: string; isActive: boolean }>()
      // DCP-335: facility id → its OWN location key, winner or not. Selection is
      // plain membership on purpose; only denial is winner-resolved (see JSDoc).
      const keyByFacilityId = new Map<string, string>()
      for (const row of (data ?? []) as Array<{
        id: string
        sage_location_id: string | null
        is_active: boolean
      }>) {
        if (!row.sage_location_id) continue
        const key = row.sage_location_id.toUpperCase()
        keyByFacilityId.set(row.id, key)
        const isActive = row.is_active === true
        const current = winnerByKey.get(key)
        if (
          current === undefined ||
          (isActive && !current.isActive) ||
          (isActive === current.isActive && row.id < current.id)
        ) {
          winnerByKey.set(key, { id: row.id, isActive })
        }
      }

      const inactiveKeys = new Set<string>()
      for (const [key, winner] of winnerByKey) {
        if (!winner.isActive) inactiveKeys.add(key)
      }
      return { data: { inactiveKeys, keyByFacilityId }, error: null }
    },
  )

  /**
   * DCP-335: build the row-level predicate that decides whether a
   * `sage_bank_accounts` row belongs to the caller's facility scope.
   *
   * Shared by `sumIntacctBalance` and `getIntacctFreshness` so the Intacct tile's
   * NUMBER and its WARNING can never be computed over different populations —
   * the invariant DCP-161 established for this pair. Both used to inline the
   * same deny-set test; now there is one place to change.
   *
   * The three steps are ordered, and the order is load-bearing:
   *   1. DENY FIRST — a location whose winning facility is inactive is dropped
   *      whether or not a filter is active (unchanged DCP-292 behaviour). This is
   *      also what makes a stale filter naming a deactivated facility read as $0
   *      without a second `activeFacilityIds` round-trip.
   *   2. NO FILTER → keep everything else, including accounts whose location maps
   *      to no facility at all ("unmapped ≠ inactive", DCP-292).
   *   3. FILTER ACTIVE → keep only accounts on a SELECTED facility's location key.
   *      Unmapped accounts therefore DROP OUT under a filter, which is why the
   *      per-facility totals need not sum to the unfiltered total: the gap is
   *      exactly the unmapped GL money. This is intentional — the Intacct table
   *      shows no row for those accounts, and the tile must match the table.
   *
   * Selection is PLAIN MEMBERSHIP, not the `DISTINCT ON` winner attribution used
   * by `get_cash_position_summary`'s `sba_facility` CTE. The two differ only when
   * two facilities share a `sage_location_id` up to case (possible: the column is
   * `TEXT UNIQUE` case-SENSITIVELY and its CHECK permits mixed case). Winner
   * attribution would make this tile show $0 for a row the Intacct table still
   * lists — re-creating the very same-screen contradiction DCP-335 fixes. Parity
   * with the table on the same page wins; the Cash Position divergence is a known,
   * documented residual.
   *
   * Deliberately does NOT narrow `facilityIds` through {@link narrowToActiveFacilityIds}
   * (contrast `applyActiveFacilityScope`): step 1 already removes a deactivated
   * facility's money, and skipping it both saves a round-trip and keeps parity with
   * `IntacctBalanceTable`, which applies inactivity ONLY through the deny set.
   *
   * @param facilityIds - explicit facility scope. Absent OR EMPTY means "no
   *        filter" (the house convention, see {@link applyActiveFacilityScope});
   *        a non-empty list that resolves to no location keys means "nothing in
   *        scope" and yields a zero total — never a silent fall back to org-wide.
   * @returns a `DbResult` wrapping the predicate, or the facilities read's error.
   */
  async function intacctLocationScope(
    facilityIds?: string[],
  ): Promise<DbResult<(locationId: string | null) => boolean>> {
    const { data: index, error } = await facilitySageLocationIndex()
    if (error) return { data: null, error }

    const filterActive = Boolean(facilityIds?.length)
    const selectedKeys = new Set<string>()
    if (filterActive) {
      for (const id of facilityIds ?? []) {
        const key = index.keyByFacilityId.get(id)
        // No entry = the facility has no `sage_location_id` (or the id is stale).
        // It contributes no key, so it can never match a row → total 0.
        if (key) selectedKeys.add(key)
      }
    }

    const inScope = (locationId: string | null): boolean => {
      // `location_id` is TEXT NOT NULL in the schema, so the null branch is
      // defensive only — do not build behaviour on it.
      const key = locationId ? locationId.toUpperCase() : null
      if (key && index.inactiveKeys.has(key)) return false
      if (!filterActive) return true
      return key !== null && selectedKeys.has(key)
    }
    return { data: inScope, error: null }
  }

  /**
   * DCP-218: resolve the set of bank-account IDs that should contribute to the
   * KPI balances — not soft-deleted (DCP-222), optionally narrowed by
   * facility / explicit account scope. This is the single source of truth for
   * "which accounts count", shared by getTotal and getCoverage so
   * a soft-deleted or out-of-scope account can never leak into one but not
   * another (Presto review #1/#3).
   *
   * DCP-324: the `bank_accounts.status` conjunct that used to sit alongside
   * `deleted_at IS NULL` here is GONE, and no predicate replaces it. The column
   * was NOT NULL DEFAULT 'active' and nothing in the product ever wrote another
   * value, so the filter was true for every row that has ever existed — it
   * described an intent ("this account is live") that it never actually
   * enforced. 20260910050000 drops it. The live/not-live question is answered
   * for real by the three scopes named below plus, per account, the DCP-324
   * link state (`sync_disabled_at` / `provider_removed_at`) — see
   * `get_connection_accounts`, which derives the connected/disconnected status
   * from those two columns rather than storing a status of its own.
   *
   * DCP-292: when NO explicit facility filter is given, the default scope is now
   * ACTIVE facilities only — a bank account under a DEACTIVATED facility must not
   * inflate the Bank / Book Balance tiles. Accounts with no facility
   * (`facility_id IS NULL` — unassigned, not inactive) are still counted. An
   * explicit `facilityIds` scope is honoured but first narrowed to the ids that
   * are still active (see {@link narrowToActiveFacilityIds}); when NONE of them
   * survive, the result is an empty set — "nothing in scope", never "no filter".
   *
   * DCP-338: the scope additionally drops ORPHANED accounts — integration-backed
   * rows whose connection is gone (see {@link applyLinkedAccountScope}). Their
   * `balances` row survives the disconnect frozen forever, and counting it made
   * the Bank Balance tile (and every KPI derived from it — Book Balance,
   * Coverage) report money the tenant no longer has. An account whose connection
   * merely BROKE (`failed` / `disconnected`) keeps its link and therefore keeps
   * counting, per the decision recorded on {@link applyLinkedAccountScope}.
   *
   * DCP-371: the scope also drops CREDIT-CARD accounts — their balance is a
   * liability rather than cash on hand, so counting it overstated Bank Balance
   * and everything derived from it (see
   * {@link applyBankBalanceAccountTypeScope}). The rows are untouched in the DB
   * and stay visible wherever accounts are MANAGED.
   *
   * DCP-324 (AC#19): the scope now also requires the account to be CONNECTED
   * (`sync_disabled_at` / `provider_removed_at` both NULL), ASSIGNED to a
   * facility and OPTED IN (`include_in_cash_position`) — see
   * {@link applyCashPositionScope}, which states the whole rule and is shared
   * with `getCoverageByFacility` and both halves of `balances.listImportStatus`
   * so the four surfaces cannot drift.
   *
   * THIS SUPERSEDES the DCP-292 sentence above about unassigned accounts: they
   * used to be "still counted" here, and AC#22 says they are reported nowhere.
   * The two facility branches below are left exactly as they are — they answer
   * the separate question of which facilities are ACTIVE — and the new conjunct
   * narrows whichever of them applied.
   *
   * @returns the in-scope account IDs (empty array = nothing in scope).
   */
  async function scopedActiveAccountIds(
    opts: { facilityIds?: string[]; accountIds?: string[] }
  ): Promise<DbResult<string[]>> {
    // Explicit empty account filter means "no accounts match" — return that
    // answer directly instead of issuing `.in([])`, which modern PostgREST
    // renders as `= ANY('{}')` and which therefore matches nothing anyway. The
    // short-circuit saves a pointless round trip; it is not a correctness guard.
    if (opts.accountIds && opts.accountIds.length === 0) {
      return { data: [], error: null }
    }

    let query = client
      .from('bank_accounts')
      .select('id')
      .is('deleted_at', null)
    if (opts.facilityIds?.length) {
      // DCP-292 (Presto #1): honour the explicit scope, but only for facilities
      // that are still active — a filter held while a facility is deactivated
      // elsewhere must stop contributing to the tiles immediately. An empty
      // intersection means "nothing in scope", which is this helper's existing
      // convention for an empty result (never "no filter").
      const { data: stillActive, error: narrowError } = await narrowToActiveFacilityIds(
        opts.facilityIds,
      )
      if (narrowError) return { data: null, error: narrowError }
      if (stillActive.length === 0) return { data: [], error: null }
      query = query.in('facility_id', stillActive)
    } else {
      // DCP-292: no explicit facility scope → exclude accounts under an inactive
      // facility while keeping unassigned (NULL-facility) accounts.
      const { data: activeIds, error: facError } = await activeFacilityIds()
      if (facError) {
        return { data: null, error: facError }
      }
      query = activeIds.length
        ? query.or(`facility_id.is.null,facility_id.in.(${activeIds.join(',')})`)
        : query.is('facility_id', null)
    }
    if (opts.accountIds?.length) {
      query = query.in('id', opts.accountIds)
    }

    // DCP-338: drop orphaned accounts (integration-backed, connection gone).
    // Applied LAST so it narrows whatever facility / explicit-account scope was
    // resolved above.
    query = applyLinkedAccountScope(query)
    // DCP-371: drop credit cards — a card balance is debt, not cash, and this
    // helper is the single source of truth for Bank Balance / Book Balance /
    // Coverage, so excluding it here fixes all three at once instead of leaving
    // one tile able to disagree with the others.
    query = applyBankBalanceAccountTypeScope(query)
    // DCP-324 AC#19: and the account must be CONNECTED, ASSIGNED and OPTED IN.
    // Applied last, like the two above, so it narrows whatever facility /
    // explicit-account scope was resolved earlier. Note the interaction with
    // the DCP-292 branches above: this adds `facility_id IS NOT NULL`, which
    // collapses the "keep unassigned rows" OR group to "in an active facility"
    // and turns the no-active-facilities branch into an explicit contradiction
    // — both correct, see applyCashPositionScope.
    query = applyCashPositionScope(query)

    const { data, error } = await query
    if (error) {
      return { data: null, error: { message: error.message, code: error.code } }
    }
    return { data: (data ?? []).map((r) => (r as { id: string }).id), error: null }
  }

  async function latestBalancesPerAccount(
    endDate: string,
    opts: { facilityIds?: string[]; accountIds?: string[] }
  ): Promise<DbResult<Map<string, { balance: number; date: string }>>> {
    // DCP-218 (Presto #1): only ACTIVE, non-deleted accounts contribute to the
    // balance sums. We resolve them first and key the balances read on those
    // IDs, so a soft-deleted account's lingering `balances` rows never inflate
    // Bank Balance / Book Balance.
    const { data: scopedIds, error: scopeError } = await scopedActiveAccountIds({
      facilityIds: opts.facilityIds,
      accountIds: opts.accountIds,
    })
    if (scopeError) {
      return { data: null, error: scopeError }
    }
    if (scopedIds.length === 0) {
      return { data: new Map(), error: null }
    }

    const query = client
      .from('balances')
      .select('bank_account_id, bank_balance, date, updated_at')
      .in('bank_account_id', scopedIds)
      // Inclusive: the as-of-date semantics of getTotal.
      .lte('date', endDate)
      .order('date', { ascending: false })
      .order('updated_at', { ascending: false })

    const { data, error } = await query
    if (error) {
      return { data: null, error: { message: error.message, code: error.code } }
    }

    // First-wins per account = newest snapshot, thanks to the date/updated_at
    // ordering above.
    const latestByAccount = new Map<string, { balance: number; date: string }>()
    for (const row of (data ?? []) as Array<{
      bank_account_id: string
      bank_balance: number | string | null
      date: string
      updated_at: string
    }>) {
      if (latestByAccount.has(row.bank_account_id)) continue

      // `bank_balance` is a DECIMAL column — PostgREST surfaces it as a number
      // for finite values but as a string when the driver can't safely fit it
      // into a JS number; coerce defensively. The isFinite guard prevents a
      // bad value (null / non-numeric string from schema drift or a corrupt
      // mock) from propagating as NaN through the KPI sums.
      const rawBalance = Number(row.bank_balance)
      const safeBalance = Number.isFinite(rawBalance) ? rawBalance : 0
      if (!Number.isFinite(rawBalance) && import.meta.env.DEV) {
        console.warn(
          `[latestBalancesPerAccount] Non-finite bank_balance for account ${row.bank_account_id}: ${row.bank_balance} — falling back to 0`,
        )
      }

      latestByAccount.set(row.bank_account_id, { balance: safeBalance, date: row.date })
    }

    return { data: latestByAccount, error: null }
  }

  /**
   * DCP-324 (code review): prove that every id in `ids` really belongs to an
   * account behind a LEDGERSYNC CONNECTION (`ledgersync_connection_id IS NOT
   * NULL`) before any `ledgerSyncAccounts` mutation touches it. Shared by all
   * six of them, so the rule cannot drift between one bulk action and the next.
   *
   * WHY IT EXISTS. Those six constrained nothing but the id set, which was safe
   * only by call-site accident: the single caller is the connection-accounts
   * screen, which hands back ids it has just listed for ONE connection. This
   * layer, though, could not tell such an id from any other `bank_accounts.id`
   * in the tenant — and one of the six is `permanentlyDelete`, whose FIRST
   * statement hard-deletes `transactions`. A manual or file-import account's id
   * reaching it would irreversibly destroy that account and its entire history
   * with nothing in the path able to notice, and with no soft-delete to restore
   * from. Anchoring the rule in the data layer is the only version of it a
   * future caller cannot opt out of by accident (the argument
   * {@link narrowToActiveFacilityIds} makes for the active-facility scope).
   *
   * THE MIRROR OF `bankAccounts.update`. That helper carries
   * `.is('integration_id', null)` so the MANUAL Settings dialogs cannot write
   * to integration-managed rows; these six deliberately do NOT reuse it (see
   * the note on {@link assignFacility}) because every account behind a
   * connection is integration-linked. Until this guard they had only the half
   * that lets integration rows IN, never the half that keeps manual rows OUT.
   *
   * WHY REFUSE INSTEAD OF NARROWING. Dropping the offending ids and applying
   * the rest would half-perform the request and report success, hiding the
   * caller bug that produced the id while still doing work nobody asked for.
   * All-or-nothing with the ids named is the same choice — for the same reason
   * — as the credit-card refusal in {@link setIncludeInCashPosition}.
   *
   * AN ID THAT RESOLVES TO NO ROW IS REFUSED TOO. The tenant client sees only
   * this tenant's schema, so a missing row means the id is another tenant's,
   * was already hard-deleted, or never existed — none of which can be PROVEN to
   * be a LedgerSync account, and a guard that passes the unprovable is not a
   * guard. No mutation here removes an account row any more, so the only ids
   * that reach this case are genuinely foreign ones; a repeated
   * `permanentlyDelete` (double submit, stale tab) finds its tombstone, passes
   * the guard, and is a no-op success via that statement's own
   * `.is('deleted_at', null)`.
   *
   * SOFT DELETE IS DELIBERATELY NOT FILTERED HERE. The question is ownership,
   * and a `deleted_at` stamp does not change it: a soft-deleted LedgerSync
   * account is still a LedgerSync account. Excluding those rows would promote
   * "soft-deleted" from a no-op (all six skip them via their own
   * `.is('deleted_at', null)`) to an error, and would make a repeated
   * `permanentlyDelete` fail on the tombstone it just wrote.
   *
   * @param ids - `bank_accounts.id` values the caller intends to mutate; every
   *        caller short-circuits the empty case before reaching this
   * @returns `null` when every id checks out, otherwise the failure result to
   *          return unchanged from the mutation.
   */
  async function refuseNonLedgerSyncAccounts(ids: string[]): Promise<DbFailure | null> {
    const { data, error } = await client
      .from('bank_accounts')
      .select('id, ledgersync_connection_id')
      .in('id', ids)
    if (error) return { data: null, error: { message: error.message, code: error.code } }

    const linked = new Set(
      (data ?? [])
        .filter((row) => (row.ledgersync_connection_id ?? null) !== null)
        .map((row) => row.id),
    )
    // Driven off the CALLER's array, not off the rows read back, so that an id
    // with no row at all lands in `rejected` instead of disappearing.
    const rejected = ids.filter((id) => !linked.has(id))
    if (rejected.length === 0) return null
    return {
      data: null,
      error: {
        message: `Refusing to operate on accounts that are not LedgerSync-linked: ${rejected.join(', ')}`,
        code: 'NOT_LEDGERSYNC_ACCOUNT',
      },
    }
  }

  /**
   * Refuse to INCLUDE credit cards in Cash Position (ADR 0017): a card's balance
   * is what the tenant owes, so counting it would report debt as cash. Shared
   * by the connected and the manual `setIncludeInCashPosition`, which call it
   * only in the include direction — excluding anything is always allowed.
   *
   * Loud rather than a filtered update that skips the cards: a silent skip would
   * leave the UI showing a toggle the database never accepted.
   *
   * @param ids - `bank_accounts.id` values about to be included (non-empty)
   * @returns `null` when none is a live credit card, otherwise the failure
   *          result naming them, to return unchanged from the mutation.
   */
  async function refuseCreditCardInclusion(ids: string[]): Promise<DbFailure | null> {
    const { data, error } = await client
      .from('bank_accounts')
      .select('id, name, account_type')
      .in('id', ids)
      .is('deleted_at', null)
    if (error) return { data: null, error: { message: error.message, code: error.code } }
    const cards = (data ?? []).filter((row) => row.account_type === 'credit_card')
    if (cards.length === 0) return null
    return {
      data: null,
      error: {
        message: `Credit card accounts cannot be included in Cash Position: ${cards
          .map((row) => row.name)
          .join(', ')}`,
        code: 'CREDIT_CARD_NOT_ALLOWED',
      },
    }
  }

  /**
   * Where a connection's INITIAL transaction import stands, and how far back its
   * history reaches (DCP-403) — both from ONE read of its accounts, so the lock
   * and the floor that `setInitialImportFrom` enforces are judged against the
   * same snapshot.
   *
   * `started` follows the sync task's own rule: `initial_import_from` bounds an
   * account's pull only while that account has no `last_transaction_sync_at`
   * anchor (the backfill branch of `read-transactions-connection`). It is also
   * true while an account is `syncing`: the reader reads the date when it
   * STARTS and stamps the anchor only when it SUCCEEDS, so for the whole first
   * import an edit would be accepted and then silently ignored.
   *
   * NOT `last_sync_at` from the connection list: that is the later of the
   * transaction AND balance stamps, and a balance sync ("Sync all balances")
   * can run before the first transaction import — locking the field while its
   * value still matters.
   *
   * Soft-deleted accounts are out of the user's world (AC#21) and count for
   * neither answer — the history they once reached must not loosen the floor.
   * Disabled and provider-removed accounts DO count: they are still listed on
   * the screen, and the date is a property of the connection's history, not of
   * whether a job currently pulls it.
   *
   * @param connectionId - `ledgersync_connections.id`
   * @returns `started` — the first import has run or is running; `floor` — the
   *          oldest date the provider reports for the connection, null if none yet
   */
  const readConnectionImportState = async (
    connectionId: string,
  ): Promise<DbResult<{ started: boolean; floor: string | null }>> => {
    const { data, error } = await client
      .from('bank_accounts')
      .select('last_transaction_sync_at, last_sync_status, oldest_transaction_date')
      .eq('ledgersync_connection_id', connectionId)
      .is('deleted_at', null)
    if (error) {
      return {
        data: null,
        error: {
          message: `Failed to read the connection's import state: ${error.message}`,
          code: error.code,
        },
      }
    }
    const rows = data ?? []
    return {
      data: {
        started: rows.some(
          (row) => row.last_transaction_sync_at !== null || row.last_sync_status === 'syncing',
        ),
        floor: oldestTransactionDate(rows),
      },
      error: null,
    }
  }

  /**
   * DCP-406: put a projections query in the order the Projections table is
   * served in — Projected Payment Date → Category → Facility, all ascending,
   * rows without a category/facility last (`nullsFirst: false`), closed by `id`.
   *
   * `id` closes the order: without a unique last key, rows that tie on every
   * column — and a whole file import shares one `created_at`, which the
   * previous sort used — come back in physical order, which an UPDATE changes.
   * That is why editing just the amount used to move a row elsewhere, often to
   * another page. Now only a change to one of the sort columns moves it.
   *
   * Kept in ONE place because two readers depend on it: `projections.list`
   * serves the pages, and `projections.findPage` works out on which of those
   * pages a row lands by walking a day in this same order. If the two drifted,
   * findPage would open a page that does not hold the row.
   *
   * The query must embed `category:categories(...)` and
   * `facility:facilities(...)` — PostgREST orders by an embedded column only
   * when that relation is part of the select.
   *
   * @param query - a projections SELECT builder (typed loosely for the same
   *   reason as {@link applyProjectionFilters}: data and count builders differ)
   * @returns the same builder with the four `.order()` calls chained
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function orderProjectionsAsTable<T extends Record<string, any>>(query: T): T {
    return query
      .order('date', { ascending: true })
      .order('category(name)', { ascending: true, nullsFirst: false })
      .order('facility(name)', { ascending: true, nullsFirst: false })
      .order('id', { ascending: true })
  }

  /**
   * The row scope of the Projections table under a given set of filters, as
   * resolved by {@link resolveProjectionScope}.
   *
   * `empty` — the scope provably holds no rows (every requested facility was
   * deactivated, or the search matched nothing); callers answer "no rows"
   * without querying. Otherwise `apply` narrows ANY projections builder (data,
   * count, lookup) to exactly the table's rows; it adds filters only — no
   * order, no range.
   */
  type ProjectionScope =
    | { empty: true }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | { empty: false; apply: <T extends Record<string, any>>(query: T) => T }

  /**
   * Resolve which projections the table shows under `filters`, once, so every
   * query that has to agree with the table — the page, its count, and
   * `findPage`'s position lookup — applies the identical scope.
   *
   * Three parts, each an async lookup that is done here a single time:
   *   1. DCP-292 (Presto #1): an explicit facility filter is narrowed to the
   *      ids that are still active BEFORE it reaches either consumer — the
   *      search RPC and applyProjectionFilters both read `facilityIds`, and
   *      neither goes through applyActiveFacilityScope. Every requested
   *      facility deactivated → empty, never unfiltered.
   *   2. A search term goes through the `search_projection_ids` RPC
   *      (server-side ILIKE across description, facility.name and
   *      category.name); the matched ids narrow the scope, none → empty.
   *   3. DCP-292: with NO explicit facility filter, the scope defaults to
   *      ACTIVE facilities (keeping NULL-facility rows) so a deactivated
   *      facility's projections drop out. An explicit filter took part 1 and
   *      is applied inside applyProjectionFilters instead.
   *
   * @param filters - the table's filters; `page`/`pageSize` are ignored here
   * @returns the scope, or a DbResult error when one of the lookups failed
   */
  async function resolveProjectionScope(
    filters: ProjectionFilters | undefined,
  ): Promise<DbResult<ProjectionScope>> {
    // `scopedFilters` is used from here down; the raw `filters` parameter
    // must not be read again.
    let scopedFilters = filters
    if (filters?.facilityIds?.length) {
      const { data: stillActive, error: narrowError } = await narrowToActiveFacilityIds(
        filters.facilityIds,
      )
      if (narrowError) return { data: null, error: narrowError }
      if (stillActive.length === 0) return { data: { empty: true }, error: null }
      scopedFilters = { ...filters, facilityIds: stillActive }
    }

    let searchIds: string[] | null = null
    if (scopedFilters?.search?.trim()) {
      const { data: ids, error: rpcError } = await getSupabaseClient().rpc('search_projection_ids', {
        p_org_id: orgId,
        p_search: scopedFilters.search.trim(),
        p_facility_ids: scopedFilters.facilityIds?.length ? scopedFilters.facilityIds : undefined,
        p_category_ids: scopedFilters.categoryIds?.length ? scopedFilters.categoryIds : undefined,
        // DCP-386: same string contract as applyProjectionFilters above — pass
        // the caller's YYYY-MM-DD strings through unchanged, no UTC conversion.
        p_start_date: scopedFilters.startDate,
        p_end_date: scopedFilters.endDate,
        p_created_date: scopedFilters.createdDate,
      })
      if (rpcError) return { data: null, error: { message: rpcError.message, code: rpcError.code } }
      searchIds = ids || []
      // No matches — nothing to query
      if (searchIds.length === 0) return { data: { empty: true }, error: null }
    }

    // Default active-facility scope, as a single filter to add to each query.
    let facilityScope: { kind: 'or'; filter: string } | { kind: 'unassigned-only' } | null = null
    if (!scopedFilters?.facilityIds?.length) {
      const { data: activeIds, error: facError } = await activeFacilityIds()
      if (facError) return { data: null, error: facError }
      facilityScope = activeIds.length
        ? { kind: 'or', filter: `facility_id.is.null,facility_id.in.(${activeIds.join(',')})` }
        : { kind: 'unassigned-only' }
    }

    const finalFilters = scopedFilters
    const finalSearchIds = searchIds
    const finalFacilityScope = facilityScope
    return {
      data: {
        empty: false,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        apply: <T extends Record<string, any>>(query: T): T => {
          let scoped = applyProjectionFilters(query, finalFilters)
          if (finalSearchIds) scoped = scoped.in('id', finalSearchIds)
          if (finalFacilityScope?.kind === 'or') scoped = scoped.or(finalFacilityScope.filter)
          else if (finalFacilityScope?.kind === 'unassigned-only') scoped = scoped.is('facility_id', null)
          return scoped
        },
      },
      error: null,
    }
  }

  return {
    integrations: {
      /** List all integrations ordered by creation date (newest first) */
      async list(): Promise<DbResult<IntegrationRow[]>> {
        const { data, error } = await client
          .from('integrations')
          .select('*')
          .order('created_at', { ascending: false })
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: data || [], error: null }
      },

      /** Get a single integration by ID */
      async getById(id: string): Promise<DbResult<IntegrationRow>> {
        const { data, error } = await client
          .from('integrations')
          .select('*')
          .eq('id', id)
          .single()
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data, error: null }
      },

      /**
       * Create a new integration
       * @param integration - Integration data (no organization_id needed — schema provides isolation)
       */
      async create(integration: IntegrationInsert): Promise<DbResult<IntegrationRow>> {
        const { data, error } = await client
          .from('integrations')
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .insert(integration as any)
          .select()
          .single()
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data, error: null }
      },

      /** Update an existing integration by ID */
      async update(id: string, updateData: Partial<IntegrationRow>): Promise<DbResult<IntegrationRow>> {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await (client.from('integrations') as any)
          .update(updateData)
          .eq('id', id)
          .select()
          .single()
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data, error: null }
      },

      /** Delete an integration by ID */
      async delete(id: string): Promise<DbResult<null>> {
        const { error } = await client.from('integrations').delete().eq('id', id)
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: null, error: null }
      },
    },

    bankAccounts: {
      /**
       * List active (non-deleted) bank accounts with facility info.
       *
       * Soft-deleted rows (`deleted_at IS NOT NULL`, DCP-222) are hidden
       * from every UI surface that consumes this helper — Manual Bank
       * Accounts section, Add/Edit dialogs, dropdowns — while the
       * underlying row stays in the table so `transactions.bank_account_id`
       * still resolves through PostgREST joins and historical
       * transactions keep displaying their Bank / Account Name /
       * Account Number columns.
       */
      async list(options: { activeOnly?: boolean } = {}): Promise<DbResult<{ id: string; name: string; institution_name: string | null; account_number: string | null; facility_id: string | null; account_type: string | null; integration_id: string | null; include_in_cash_position: boolean }[]>> {
        let query = client
          .from('bank_accounts')
          .select('id, name, institution_name, account_number, facility_id, account_type, integration_id, include_in_cash_position')
          .is('deleted_at', null)
          .order('name')
        if (options.activeOnly) {
          // DCP-292: opt-in scope — exclude accounts under a DEACTIVATED facility
          // (unassigned/NULL-facility accounts kept) so selectors/lists that pass
          // activeOnly don't surface inactive-facility banks. Callers that need
          // every account (add/edit dialogs) simply omit the flag.
          const scope = await applyActiveFacilityScope(query, undefined)
          if (scope.error) return { data: null, error: scope.error }
          query = scope.query
        }
        const { data, error } = await query
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: data || [], error: null }
      },

      /**
       * Create multiple bank accounts in a single batch.
       *
       * This is the ONLY manual-account creator in the codebase (the Add Bank
       * dialog, via `useCreateBankAccounts`); API accounts are materialised by
       * `ledgersync-finalize` / the sync task instead.
       *
       * New accounts take the column default, `include_in_cash_position =
       * false`: like a connected account, a manual one counts towards Bank
       * Balance, the Cash Position Summary and the Bank Balance Update table
       * ({@link applyCashPositionScope}) only once someone opts it in with the
       * toggle in its bank's accounts table (`bankAccounts.setIncludeInCashPosition`).
       * Manual accounts that predate the toggle were opted in by migration
       * 20260910070000 and keep that.
       *
       * @param inputs - Array of bank account data
       */
      async createMany(inputs: { name: string; institution_name: string; account_type: string; account_number?: string | null; facility_id?: string | null }[]): Promise<DbResult<{ id: string; name: string; institution_name: string | null }[]>> {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await (client as any)
          .from('bank_accounts')
          .insert(inputs)
          .select('id, name, institution_name')
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: data || [], error: null }
      },

      /**
       * Fetch bank account rows by id INCLUDING soft-deleted ones (DCP-222).
       *
       * The default `bankAccounts.list()` hides soft-deleted records so
       * the Settings UI doesn't surface them — but the Manual
       * Transactions edit dialog needs the opposite: transactions that
       * reference a soft-deleted bank must still display the original
       * Bank / Account Name / Account Number in their Select widgets,
       * with the option shown as "(deleted)" and locked from re-selection.
       *
       * Intended for SMALL id sets only — typically one id (the single
       * deleted ref opened in the edit dialog). PostgREST `.in()` is
       * fine on a few dozen entries but degrades for thousands; if a
       * future caller needs batch resolution, add explicit pagination
       * + chunking rather than expanding this helper.
       *
       * @param ids - bank_account.id values to look up (small set)
       */
      async listByIdsIncludingDeleted(ids: string[]): Promise<DbResult<BankAccountIncludingDeletedRow[]>> {
        if (ids.length === 0) {
          return { data: [], error: null }
        }
        const { data, error } = await client
          .from('bank_accounts')
          .select('id, name, institution_name, account_number, facility_id, account_type, integration_id, deleted_at')
          .in('id', ids)
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: data || [], error: null }
      },

      /**
       * Update a manual bank account by ID (only active accounts without integration).
       *
       * Re-typing an account to `credit_card` also takes it out of Cash Position
       * in the same statement: a card may never be included (ADR 0017), and a
       * stale `true` would leave it behind a disabled toggle reading "Yes".
       * Connected accounts get the same effect from `applyAccountType` in
       * `ConnectionAccountsTable`.
       *
       * @param id - Bank account ID to update
       * @param updateData - Fields to update
       */
      async update(id: string, updateData: { name?: string; institution_name?: string; account_type?: string; account_number?: string | null; facility_id?: string | null }): Promise<DbResult<{ id: string; name: string; institution_name: string | null }>> {
        const values =
          updateData.account_type === 'credit_card'
            ? { ...updateData, include_in_cash_position: false }
            : updateData
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await (client as any)
          .from('bank_accounts')
          .update(values)
          .eq('id', id)
          .is('integration_id', null)
          // Don't allow updates on soft-deleted rows — they're gone from
          // the UI's perspective and any rename/move would silently
          // resurrect them in JOINs against historical transactions.
          .is('deleted_at', null)
          .select('id, name, institution_name')
          .single()
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data, error: null }
      },

      /**
       * Soft-delete a manual bank account (only accounts without integration).
       *
       * DCP-222 changed this from a hard DELETE to a `deleted_at` stamp.
       * Rationale: existing transactions reference this row through
       * `transactions.bank_account_id` (FK with ON DELETE SET NULL). A
       * hard delete would null those references and the Manual
       * Transactions table would lose the Bank / Account Name / Account
       * Number columns on the affected rows. Soft-deleting keeps the
       * row intact so the PostgREST join keeps resolving; the Settings
       * UI hides the row via the `deleted_at IS NULL` filter in
       * `bankAccounts.list()`.
       *
       * **Idempotent by design.** Calling delete on an already-soft-deleted
       * row is treated as a no-op success (no error, original `deleted_at`
       * preserved). The `.is('deleted_at', null)` guard ensures repeat
       * calls don't re-stamp the timestamp; the caller doesn't need to
       * special-case "already deleted" — that's the same successful end
       * state as "just deleted".
       *
       * The `integration_id IS NULL` guard is preserved — Plaid-managed
       * accounts are deleted through their own disconnect flow, not here.
       * @param id - Bank account ID to soft-delete
       */
      async delete(id: string): Promise<DbResult<null>> {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (client as any)
          .from('bank_accounts')
          .update({ deleted_at: new Date().toISOString() })
          .eq('id', id)
          .is('integration_id', null)
          // Treat double-delete as a no-op rather than re-stamping the
          // timestamp — preserves the original deletion time.
          .is('deleted_at', null)
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: null, error: null }
      },

      /**
       * Rename an institution across all active manual bank accounts.
       * Only updates accounts without an integration (manual accounts)
       * and excludes soft-deleted rows.
       *
       * **The soft-deleted exclusion is intentional, not an oversight.**
       * Historical transactions joined to a deleted bank should keep
       * showing the institution name as it was at the moment of
       * deletion — that's the audit-trail invariant the soft-delete
       * pattern preserves. If we rewrote the name on deleted rows, a
       * later display of a years-old transaction would mention a bank
       * the user never actually transacted with under that name.
       *
       * If a restore flow is ever added, the restored account will
       * carry the pre-rename name; the caller is expected to decide
       * whether to forward-update it or leave it as-is at that point.
       *
       * @param oldName - Current institution name to match
       * @param newName - New institution name to set
       */
      async renameInstitution(oldName: string, newName: string): Promise<DbResult<null>> {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (client as any)
          .from('bank_accounts')
          .update({ institution_name: newName })
          .eq('institution_name', oldName)
          .is('integration_id', null)
          .is('deleted_at', null)
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: null, error: null }
      },

      /**
       * Soft-delete several manual accounts in one UPDATE — the "Delete
       * Financial Account" action on a manual bank, which passes every live
       * account of the bank from the full list it loaded, inactive facilities
       * included.
       *
       * By ids rather than by name, so the delete removes exactly the rows the
       * user confirmed and not an account created under the name meanwhile.
       * Guards and idempotency as in {@link delete}: linked accounts are never
       * touched and already-deleted rows keep their original `deleted_at`.
       *
       * Meant for one bank's accounts (tens of ids) — a single `in` list.
       *
       * @param ids - Ids of the manual accounts to soft-delete
       */
      async deleteMany(ids: string[]): Promise<DbResult<null>> {
        if (ids.length === 0) return { data: null, error: null }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (client as any)
          .from('bank_accounts')
          .update({ deleted_at: new Date().toISOString() })
          .in('id', ids)
          .is('integration_id', null)
          .is('deleted_at', null)
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: null, error: null }
      },

      /**
       * Toggle whether MANUAL accounts count towards Bank Balance, the Bank
       * Balance Update table and the Cash Position Summary
       * ({@link applyCashPositionScope}, `get_cash_position_summary`).
       *
       * The manual twin of `ledgerSyncAccounts.setIncludeInCashPosition`, with
       * the same directional credit-card rule ({@link refuseCreditCardInclusion}):
       * including a card is refused with an error naming it, excluding is always
       * allowed — so a card with a stale `true` can still be cleared. The UPDATE
       * repeats the card check on the include path to close the window after the
       * read; it filters on `account_type`, not the column it sets, and returns
       * no body, so the PostgREST same-column 42703 bug does not apply.
       *
       * Guards as in {@link update}: linked accounts and soft-deleted rows are
       * never touched. Unlike the connected twin there is no ownership refusal
       * ({@link refuseNonLedgerSyncAccounts}'s counterpart): an id that is not a
       * live manual account is filtered out silently, as `update`/`delete` do,
       * and the call still succeeds.
       *
       * @param ids - Ids of the manual accounts (empty = no-op success)
       * @param include - Whether the accounts should count towards Cash Position
       */
      async setIncludeInCashPosition(ids: string[], include: boolean): Promise<DbResult<null>> {
        if (ids.length === 0) return { data: null, error: null }
        if (include) {
          const cardRefusal = await refuseCreditCardInclusion(ids)
          if (cardRefusal) return cardRefusal
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let update = (client as any)
          .from('bank_accounts')
          .update({ include_in_cash_position: include })
          .in('id', ids)
          .is('integration_id', null)
          .is('deleted_at', null)
        if (include) {
          update = update.neq('account_type', 'credit_card')
        }
        const { error } = await update
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: null, error: null }
      },
    },

    facilities: {
      /** List all facilities ordered by name */
      async list(): Promise<DbResult<FacilityRow[]>> {
        const { data, error } = await client
          .from('facilities')
          .select('*')
          .order('name', { ascending: true })
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: data || [], error: null }
      },

      /** Get a single facility by ID */
      async getById(id: string): Promise<DbResult<FacilityRow>> {
        const { data, error } = await client
          .from('facilities')
          .select('*')
          .eq('id', id)
          .single()
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data, error: null }
      },

      /**
       * Create a new facility
       * @param facility - Facility data (no organization_id needed — schema provides isolation)
       */
      async create(facility: FacilityInsert): Promise<DbResult<FacilityRow>> {
        const { data, error } = await client
          .from('facilities')
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .insert(facility as any)
          .select()
          .single()
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data, error: null }
      },

      /** Update an existing facility by ID */
      async update(id: string, updateData: Partial<FacilityRow>): Promise<DbResult<FacilityRow>> {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await (client.from('facilities') as any)
          .update(updateData)
          .eq('id', id)
          .select()
          .single()
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data, error: null }
      },

      /** Delete a facility by ID */
      async delete(id: string): Promise<DbResult<null>> {
        const { error } = await client.from('facilities').delete().eq('id', id)
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: null, error: null }
      },

      /**
       * Assign the given facilities to a pack by stamping `pack_id` on each.
       * A no-op when `ids` is empty.
       *
       * The `(pack_id IS NULL OR pack_id = packId)` precondition enforces AC #4
       * at the data layer: a facility that some other pack grabbed after the UI
       * loaded its options is left untouched rather than silently stolen. We
       * then compare the rows actually updated against the request and report a
       * `PACK_CONFLICT` when any facility was skipped, so the caller can surface
       * it instead of quietly dropping the facility.
       */
      async assignToPack(ids: string[], packId: string): Promise<DbResult<null>> {
        // Dedup first so the membership check below compares against distinct ids.
        const uniqueIds = [...new Set(ids)]
        if (uniqueIds.length === 0) return { data: null, error: null }
        if (!isUuid(packId)) {
          return { data: null, error: { message: 'Invalid pack id', code: 'INVALID_INPUT' } }
        }
        if (!uniqueIds.every(isUuid)) {
          return { data: null, error: { message: 'Invalid facility id', code: 'INVALID_INPUT' } }
        }

        // Pre-check membership BEFORE writing: read each chosen facility's
        // current pack (with its name) so a conflict can tell the user WHICH
        // pack already owns it instead of failing opaquely. A facility that is
        // unassigned (pack_id IS NULL) or already in THIS pack is fine — the
        // edit flow re-asserts membership; only one sitting in a DIFFERENT pack
        // blocks the assignment (AC #4).
        //
        // This check is intentionally NOT atomic with the UPDATE below:
        // facility↔pack is a low-contention settings action and the UI only
        // offers unassigned facilities, so a best-effort check is enough. It
        // also lets the UPDATE filter on `id` ONLY — filtering on `pack_id` (the
        // column being SET) while returning a body makes PostgREST 14 fail with
        // `42703 column facilities.pack_id does not exist`.
        const { data: current, error: checkError } = await client
          .from('facilities')
          .select('id, name, pack_id, packs(name)')
          .in('id', uniqueIds)
        if (checkError) {
          return { data: null, error: { message: checkError.message, code: checkError.code } }
        }
        if (!current || current.length !== uniqueIds.length) {
          return {
            data: null,
            error: { message: 'One or more facilities no longer exist', code: 'PACK_CONFLICT' },
          }
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const conflict = (current as any[]).find(
          (f) => f.pack_id !== null && f.pack_id !== packId,
        )
        if (conflict) {
          // `packs` is a to-one embed (FK facilities.pack_id → packs.id), but
          // guard for an array shape in case the client returns one.
          const pack = Array.isArray(conflict.packs) ? conflict.packs[0] : conflict.packs
          const otherPackName = pack?.name ?? 'another pack'
          return {
            data: null,
            error: {
              message: `"${conflict.name}" is already in pack "${otherPackName}"`,
              code: 'PACK_CONFLICT',
            },
          }
        }

        // Assign. Filter on `id` ONLY (never on `pack_id`) to avoid PostgREST 42703.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (client.from('facilities') as any)
          .update({ pack_id: packId })
          .in('id', uniqueIds)
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: null, error: null }
      },

      /**
       * Clear pack membership for facilities currently in `packId` EXCEPT those
       * in `keepIds` (sets `pack_id` back to NULL). Used by the edit-reconcile
       * flow: the new selection is assigned first, then this releases the
       * de-selected members — so the pack is never momentarily left empty even
       * if the second call fails.
       */
      async clearPackExcept(packId: string, keepIds: string[]): Promise<DbResult<null>> {
        if (!isUuid(packId)) {
          return { data: null, error: { message: 'Invalid pack id', code: 'INVALID_INPUT' } }
        }
        // `keepIds` is interpolated into the PostgREST `not.in` list. Reject the
        // whole call if any id is malformed rather than silently dropping it —
        // a dropped keepId would wrongly release that facility from the pack.
        if (!keepIds.every(isUuid)) {
          return { data: null, error: { message: 'Invalid facility id', code: 'INVALID_INPUT' } }
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let query = (client.from('facilities') as any)
          .update({ pack_id: null })
          .eq('pack_id', packId)
        if (keepIds.length > 0) {
          query = query.not('id', 'in', `(${keepIds.join(',')})`)
        }
        const { error } = await query
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: null, error: null }
      },
    },

    /**
     * Packs — named groupings of facilities (DCP-171). The facility ↔ pack
     * link lives on `facilities.pack_id`; membership is therefore changed via
     * the `facilities.assignToPack` / `facilities.clearPackExcept` helpers above,
     * orchestrated by the `useCreatePack` / `useUpdatePack` hooks.
     */
    packs: {
      /** List all packs ordered by name */
      async list(): Promise<DbResult<PackRow[]>> {
        const { data, error } = await client
          .from('packs')
          .select('*')
          .order('name', { ascending: true })
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: data || [], error: null }
      },

      /** Get a single pack by ID */
      async getById(id: string): Promise<DbResult<PackRow>> {
        const { data, error } = await client
          .from('packs')
          .select('*')
          .eq('id', id)
          .single()
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data, error: null }
      },

      /** Create a new pack (membership is assigned separately by the caller) */
      async create(pack: PackInsert): Promise<DbResult<PackRow>> {
        const { data, error } = await client
          .from('packs')
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .insert(pack as any)
          .select()
          .single()
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data, error: null }
      },

      /** Update a pack by ID (e.g. rename) */
      async update(id: string, updateData: Partial<PackRow>): Promise<DbResult<PackRow>> {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await (client.from('packs') as any)
          .update(updateData)
          .eq('id', id)
          .select()
          .single()
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data, error: null }
      },

      /**
       * Delete a pack by ID. The `facilities.pack_id` FK is `ON DELETE SET
       * NULL`, so member facilities are automatically un-assigned and NOT
       * deleted (AC #6).
       */
      async delete(id: string): Promise<DbResult<null>> {
        const { error } = await client.from('packs').delete().eq('id', id)
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: null, error: null }
      },
    },

    categories: {
      /**
       * List all categories with pagination and optional type filter.
       * Returns both active and inactive categories ordered by creation date (newest first).
       * @param options - Pagination and filter options
       */
      async list(options?: {
        page?: number
        pageSize?: number // 0 = fetch all (no pagination)
        type?: 'inflow' | 'outflow'
      }): Promise<DbResult<{ items: CategoryRow[]; total: number }>> {
        const pageSize = options?.pageSize ?? 25

        // Get total count with optional type filter
        let countQuery = client.from('categories').select('*', { count: 'exact', head: true })
        if (options?.type) countQuery = countQuery.eq('type', options.type)
        const { count, error: countError } = await countQuery
        if (countError) return { data: null, error: { message: countError.message, code: countError.code } }

        // Get data with optional type filter and pagination
        let dataQuery = client.from('categories').select('*')
        if (options?.type) dataQuery = dataQuery.eq('type', options.type)
        dataQuery = dataQuery.order('created_at', { ascending: false })

        // Apply pagination only when pageSize > 0; pageSize=0 fetches all rows
        if (pageSize > 0) {
          const page = options?.page ?? 1
          const from = (page - 1) * pageSize
          dataQuery = dataQuery.range(from, from + pageSize - 1)
        }

        const { data, error } = await dataQuery
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: { items: data || [], total: count ?? 0 }, error: null }
      },

      /**
       * Create a new category
       * @param input - Category data (no organization_id needed — schema provides isolation)
       */
      async create(input: {
        name: string
        /** Optional after DCP-250 Phase 1 — categories.type column is now nullable. */
        type?: 'inflow' | 'outflow'
        description?: string | null
        sage_gl_account?: string | null
        default_priority?: boolean
      }): Promise<DbResult<CategoryRow>> {
        const { data, error } = await client
          .from('categories')
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .insert({ ...input, is_active: true } as any)
          .select()
          .single()
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data, error: null }
      },

      /** Update an existing category by ID */
      async update(id: string, updateData: Partial<CategoryRow>): Promise<DbResult<CategoryRow>> {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await (client.from('categories') as any).update(updateData).eq('id', id).select().single()
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data, error: null }
      },

      /**
       * Delete a category by ID
       * Note: Will fail if category has associated transactions due to FK constraints
       */
      async delete(id: string): Promise<DbResult<null>> {
        const { error } = await client.from('categories').delete().eq('id', id)
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: null, error: null }
      },
    },

    transactions: {
      /**
       * List transactions with optional filters.
       *
       * DCP-324: `facilityId` filters on the row's ACCOUNT
       * ({@link applyTransactionFacilityScope}), not on a column of
       * `transactions` — the denormalized `facility_id` this used to read is
       * gone. The `!inner` embed that carries the join also applies AC#22, so an
       * unassigned account's rows never come back, filter or no filter.
       *
       * @param params - Filter params for facility, date range, and limit
       */
      async list(params?: {
        facilityId?: string
        startDate?: string
        endDate?: string
        limit?: number
      }): Promise<DbResult<TransactionRow[]>> {
        let query = client.from('transactions').select(`*, ${TRANSACTION_SCOPE_EMBED}`)
        query = applyTransactionFacilityScope(
          query,
          params?.facilityId ? [params.facilityId] : undefined,
        )
        if (params?.startDate) query = query.gte('transaction_date', params.startDate)
        if (params?.endDate) query = query.lte('transaction_date', params.endDate)
        query = query.order('transaction_date', { ascending: false })
        if (params?.limit) query = query.limit(params.limit)
        const { data, error } = await query
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: data || [], error: null }
      },

      /**
       * Get facility-category aggregation for bar chart.
       * Runs a SQL GROUP BY on the server — returns pre-aggregated rows.
       * Uses the public schema RPC client (not the tenant schema client) because
       * the function lives in the public schema.
       *
       * DCP-223: the RPC groups by the user-defined `categories` table
       * (transactions.category_id → categories.name), not the Plaid-only
       * `plaid_category` enum.
       *
       * DCP-223 part 2: `total` is the SIGNED net (SUM(amount)) per facility ×
       * category, so the displayed sign comes from the summed numbers — NOT from
       * the category's inflow/outflow type. The category type is intentionally
       * not returned: DCP-250 moved the sign onto the signed `amount` and made
       * `categories.type` a deprecated, nullable column.
       *
       * @param params.startDate - Date string in YYYY-MM-DD format (inclusive)
       * @param params.endDate - Date string in YYYY-MM-DD format (inclusive)
       * @returns Aggregated rows with facility_name, category, total (signed net)
       */
      async facilityCategoryChart(params?: {
        startDate?: string
        endDate?: string
      }): Promise<DbResult<Array<{
        facility_name: string
        category: string
        total: number
      }>>> {
        const { data, error } = await getSupabaseClient().rpc('get_facility_category_chart', {
          p_org_id: orgId,
          p_start_date: params?.startDate,
          p_end_date: params?.endDate,
        })
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        const rows: Array<{
          facility_name: string
          category: string
          total: number
        }> = (data ?? []).map((row) => ({
          facility_name: row.facility_name,
          category: row.category,
          total: row.total,
        }))
        return { data: rows, error: null }
      },
    },

    plaidItems: {
      /** List all Plaid items with account counts and sync cursor data, ordered by creation date (newest first) */
      async list(): Promise<DbResult<TenantPlaidItem[]>> {
        const { data, error } = await client
          .from('plaid_items')
          .select('*, plaid_accounts(id), plaid_sync_cursors(*)')
          .order('created_at', { ascending: false })
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: (data || []) as unknown as TenantPlaidItem[], error: null }
      },

      /** Get a single Plaid item by ID, including sync cursor data */
      async getById(id: string): Promise<DbResult<TenantPlaidItem | null>> {
        const { data, error } = await client
          .from('plaid_items')
          .select('*, plaid_sync_cursors(*)')
          .eq('id', id)
          .single()
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: data as unknown as TenantPlaidItem | null, error: null }
      },

      /**
       * List Plaid items filtered by facility, including sync cursor data
       * @param facilityId - Facility ID to filter by
       */
      async listByFacility(facilityId: string): Promise<DbResult<TenantPlaidItem[]>> {
        const { data, error } = await client
          .from('plaid_items')
          .select('*, plaid_accounts(id), plaid_sync_cursors(*)')
          .eq('facility_id', facilityId)
          .order('created_at', { ascending: false })
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: (data || []) as unknown as TenantPlaidItem[], error: null }
      },
    },

    plaidAccounts: {
      /** List all Plaid accounts ordered by name */
      async list(): Promise<DbResult<TenantPlaidAccount[]>> {
        const { data, error } = await client.from('plaid_accounts').select('*').order('name')
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: (data || []) as unknown as TenantPlaidAccount[], error: null }
      },

      /**
       * List Plaid accounts filtered by Plaid item
       * @param itemId - Plaid item ID to filter by
       */
      async listByItem(itemId: string): Promise<DbResult<TenantPlaidAccount[]>> {
        const { data, error } = await client.from('plaid_accounts').select('*').eq('plaid_item_id', itemId).order('name')
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: (data || []) as unknown as TenantPlaidAccount[], error: null }
      },

      /**
       * List Plaid accounts filtered by facility
       * @param facilityId - Facility ID to filter by
       */
      async listByFacility(facilityId: string): Promise<DbResult<TenantPlaidAccount[]>> {
        const { data, error } = await client.from('plaid_accounts').select('*').eq('facility_id', facilityId).order('name')
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: (data || []) as unknown as TenantPlaidAccount[], error: null }
      },
    },

    ledgerSyncConnections: {
      /**
       * List every LedgerSync connection for the tenant, newest first.
       *
       * A connection is an ORGANIZATION-level object (DCP-324): the tenant
       * client already scopes to the org via RLS/schema, so a plain select with
       * no facility narrowing returns every connection. Used by
       * `BankConnectionsSection` for each row's raw `institution_id` (what
       * Reconnect needs to re-open the widget), which the enriched
       * {@link listForManagement} read model deliberately does not carry.
       */
      async list(): Promise<DbResult<TenantLedgerSyncConnection[]>> {
        const { data, error } = await client
          .from('ledgersync_connections')
          .select('*')
          .order('created_at', { ascending: false })
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: (data || []) as unknown as TenantLedgerSyncConnection[], error: null }
      },

      /**
       * DCP-324: the connection list for Manage Bank Connections.
       *
       * Server-side via `public.get_bank_connections` rather than a PostgREST
       * select, because every column the list actually sorts on is derived:
       * `display_name` is a COALESCE, `accounts_count` and `last_sync_at` are
       * reductions over `bank_accounts`, and `capabilities` sorts on labels
       * pulled out of JSON. Embedding the accounts through PostgREST would
       * return one row per ACCOUNT and make the client download every account of
       * every connection just to show a count.
       *
       * Soft-deleted connections (AC#7) are excluded by the RPC.
       *
       * Distinct from {@link list} above, which stays as-is: that one returns
       * RAW `ledgersync_connections` rows for callers that just need the
       * records (e.g. counting connected banks), while this one returns the
       * enriched, sorted read model the management screen renders. They are not
       * interchangeable — this one cannot be used to write back, and that one
       * has no accounts_count / last_sync_at / display_name.
       *
       * @param opts.sortColumn - defaults to `display_name`
       * @param opts.sortDirection - defaults to `asc`
       */
      async listForManagement(
        opts: { sortColumn?: BankConnectionSortColumn; sortDirection?: SortDirection } = {},
      ): Promise<DbResult<BankConnectionRow[]>> {
        const sortColumn = opts.sortColumn ?? 'display_name'
        const sortDirection = opts.sortDirection ?? 'asc'
        // Checked here as well as in the RPC. The values originate in DataTable
        // state / URL params, and catching a bad one locally gives an
        // actionable message instead of a 22023 round trip.
        if (!BANK_CONNECTION_SORT_COLUMNS.has(sortColumn)) {
          return {
            data: null,
            error: { message: `Invalid sort column: ${sortColumn}`, code: 'INVALID_SORT' },
          }
        }
        if (!SORT_DIRECTIONS.has(sortDirection)) {
          return {
            data: null,
            error: { message: `Invalid sort direction: ${sortDirection}`, code: 'INVALID_SORT' },
          }
        }
        const { data, error } = await getSupabaseClient().rpc('get_bank_connections', {
          p_org_id: orgId,
          p_sort: sortColumn,
          p_dir: sortDirection,
        })
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        // Cast rather than trust the generated Returns type: the generator
        // marks every RPC output column non-nullable (see the note on
        // BankConnectionRow).
        const rows = (data ?? []) as unknown as BankConnectionRow[]
        // DCP-401: narrow the two status columns to the known vocabulary. The
        // column is plain TEXT on the wire, and a value added server-side
        // before this bundle knows it must read as "no known state", not flow
        // into a `Record<LedgerSyncSyncStatus, …>` lookup as `undefined`.
        return {
          data: rows.map((row) => ({
            ...row,
            sync_status: isLedgerSyncSyncStatus(row.sync_status) ? row.sync_status : null,
            balance_sync_status: isLedgerSyncSyncStatus(row.balance_sync_status)
              ? row.balance_sync_status
              : null,
          })),
          error: null,
        }
      },

      /**
       * AC#5: rename a connection.
       *
       * A plain PostgREST update, per the project rule that ordinary writes do
       * NOT get an RPC or a wrapping transaction. Errors are surfaced with the
       * raw message so the caller keeps the actionable text.
       *
       * The name is trimmed before storing, and a blank one is REFUSED rather
       * than written: a whitespace-only name would leave the card with no
       * visible label. Clearing the name back to the institution default is
       * intentionally not offered here — the read model's COALESCE fallback
       * exists to keep legacy rows renderable, not as a reset feature.
       *
       * The LENGTH CAP is enforced here as well as in the dialog's Zod schema,
       * and for the same reason the blank check is: the form is one door to this
       * write, not the only one. `ledgersync_connections.name` is PostgreSQL
       * `TEXT`, so the database accepts an unbounded paste happily, and a caller
       * that bypasses the form (another screen, a script, a future bulk action)
       * would store a wall of text that every Bank column then has to truncate.
       * The bound itself is IMPORTED from the form module rather than retyped —
       * two copies of 255 would drift the first time either side changed.
       * Measured AFTER the trim, so surrounding whitespace never costs a
       * character the user can see.
       *
       * @param id - `ledgersync_connections.id`
       * @param name - New display name
       */
      async rename(id: string, name: string): Promise<DbResult<null>> {
        const trimmed = name.trim()
        if (trimmed.length === 0) {
          return {
            data: null,
            error: { message: 'Connection name cannot be empty', code: 'INVALID_NAME' },
          }
        }
        if (trimmed.length > MAX_CONNECTION_NAME_LENGTH) {
          return {
            data: null,
            error: {
              message: `Connection name must be ${MAX_CONNECTION_NAME_LENGTH} characters or fewer`,
              code: 'INVALID_NAME',
            },
          }
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (client as any)
          .from('ledgersync_connections')
          .update({ name: trimmed })
          .eq('id', id)
          // Renaming a deleted connection would be invisible work on a row the
          // UI no longer shows.
          .is('deleted_at', null)
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: null, error: null }
      },

      /**
       * DCP-403: has this connection's initial transaction import started (run or
       * running)?
       * Locks the "Initial import from" field; the write path below applies the
       * same test.
       *
       * @param id - `ledgersync_connections.id`
       */
      async hasImportedTransactions(id: string): Promise<DbResult<boolean>> {
        const { data, error } = await readConnectionImportState(id)
        if (error) return { data: null, error }
        return { data: data.started, error: null }
      },

      /**
       * AC#9/AC#23: set how far back the first sync should reach.
       *
       * `null` clears it, meaning "the computed default" — January 1st of the
       * current year, raised to the connection's oldest available date. That is
       * a real choice, so it is accepted rather than treated as a missing
       * argument, and it is no longer a synonym for "unbounded": the sync task
       * resolves a NULL through the same `@dcp/shared` rule this screen renders.
       *
       * A FUTURE date is refused. It is not merely odd: the sync treats this as
       * the lower bound of the window it requests, so a future value asks for a
       * range that cannot contain anything and would present as a connection
       * that syncs successfully and imports nothing. Compared against the
       * BUSINESS day, the same primitive the rest of the banking layer uses, so
       * "today" means the same thing here as everywhere else.
       *
       * A date BELOW THE CONNECTION'S FLOOR is refused too, and this is the
       * write path's own job rather than the picker's: `DateField.min` only greys
       * out days in the calendar, while a TYPED value reaches the change handler
       * untouched. Without the check here the field would accept a date the
       * provider cannot honour, the first sync would quietly import less than the
       * field claims, and the user's only evidence would be missing months.
       *
       * THE FLOOR IS THE MINIMUM `oldest_transaction_date` across the
       * connection's non-deleted accounts — the DEEPEST history the connection
       * can reach, since the user may legitimately want that one account's full
       * span. Accounts the provider never dated contribute nothing (see
       * {@link oldestTransactionDate}, the same reduction the screen's `min`
       * uses, so the refusal and the greyed-out days can never disagree).
       *
       * READ AT WRITE TIME, NEVER CACHED. LedgerSync moves
       * `oldest_transaction_date` BACKWARD as the aggregator backfills, so the
       * floor loosens over the first days of a connection's life; a value cached
       * at page load would keep refusing dates that have since become reachable.
       *
       * WHEN NO ACCOUNT HAS A DATE YET the write is ALLOWED. There is no floor to
       * enforce — the provider has not yet said how far back it can go (this is
       * the state of a connection whose balances have never been read), and
       * inventing one would mean guessing. Refusing here would break the field
       * exactly when it is most likely to be used, right after connecting, and
       * it cannot cause an over-wide pull either: the sync raises each account's
       * `from` to that account's own floor once the provider reports one.
       *
       * ONCE THE INITIAL IMPORT HAS RUN the write is refused outright
       * (`INITIAL_IMPORT_DONE`, DCP-403) — see `hasImportedTransactions`.
       *
       * @param id - `ledgersync_connections.id`
       * @param date - ISO `YYYY-MM-DD`, or null to clear
       */
      async setInitialImportFrom(id: string, date: string | null): Promise<DbResult<null>> {
        if (date !== null) {
          const parsed = dayjs(date, 'YYYY-MM-DD', true)
          if (!parsed.isValid()) {
            return {
              data: null,
              error: { message: `Invalid date: ${date} (expected YYYY-MM-DD)`, code: 'INVALID_DATE' },
            }
          }
          if (parsed.isAfter(dayjs(businessDate(), 'YYYY-MM-DD', true))) {
            return {
              data: null,
              error: {
                message: `Initial import date cannot be in the future: ${date}`,
                code: 'INVALID_DATE',
              },
            }
          }
        }
        // DCP-403: the date only bounds a connection's FIRST import, so once
        // that has started it can no longer change anything — and letting it be
        // edited would tell the user it had. Refused for a clear too. Read
        // AFTER the checks above, so a malformed or future value never costs a
        // round trip; a failed read refuses rather than allows (enforcement
        // must not silently stop enforcing).
        //
        // A reader that starts between this read and the update below would
        // still see the old date. That window is one round trip; closing it
        // would need the lock and the write in one statement across two tables,
        // i.e. an RPC, which a single-field write does not justify.
        const { data: state, error: stateError } = await readConnectionImportState(id)
        if (stateError) return { data: null, error: stateError }
        if (state.started) {
          return {
            data: null,
            error: {
              message: 'The initial import has already started for this connection, so its start date can no longer be changed',
              code: 'INITIAL_IMPORT_DONE',
            },
          }
        }
        // ISO dates compare lexicographically exactly as they compare
        // chronologically, so no Date object is built and no timezone can shift
        // the boundary by a day. A clear has no floor to respect.
        if (date !== null && state.floor !== null && date < state.floor) {
          return {
            data: null,
            error: {
              message: `Initial import date ${date} is earlier than the oldest transactions available for this connection (${state.floor})`,
              code: 'INVALID_DATE',
            },
          }
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (client as any)
          .from('ledgersync_connections')
          .update({ initial_import_from: date })
          .eq('id', id)
          .is('deleted_at', null)
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: null, error: null }
      },

      // DELETING A CONNECTION IS NOT HERE, on purpose (AC#7). It is the one
      // connection mutation with a REMOTE half: the bank link has to be revoked
      // at LedgerSync (`DELETE /connections/{id}`), which needs the API key,
      // which cannot live in a browser. It used to be four PostgREST calls in
      // this object and told the aggregator nothing — the connection vanished
      // from our UI while the link stayed authorized, kept refreshing, kept
      // counting against billing, and the customer's consent was never
      // withdrawn. Both halves now run in ONE edge call so they cannot
      // disagree: `ledgersync-link` → `delete-connection`, implemented in
      // `supabase/functions/_shared/ledgersync-connection-delete.ts` and reached
      // from `useDeleteBankConnection`. The local semantics: HARD-delete the
      // `ledgersync_connections` row, soft-delete its `bank_accounts`,
      // hard-delete those accounts' `balances`, KEEP the transactions — all
      // pinned by that module's Deno tests. The connection row goes because
      // AC#7 makes the delete terminal; the account rows stay because the money
      // reads exclude a deleted account by its own `deleted_at` and the kept
      // transactions still point at them.
    },

    ledgerSyncAccounts: {
      /**
       * DCP-324: one keyset page of the accounts table inside a connection.
       *
       * Server-side via `public.get_connection_accounts`. A single bank login
       * routinely exposes 100+ accounts and this screen mutates the very
       * columns it filters and sorts on, so offset paging (`.range()`) would
       * skip and repeat rows as the user works; see the migration header.
       *
       * @param connectionId - `ledgersync_connections.id`
       * @param query - Filters, sort and keyset position ({@link ConnectionAccountsQuery})
       * @returns The page, the filter-set total, and the next cursor (null at the end)
       */
      async listByConnection(
        connectionId: string,
        query: ConnectionAccountsQuery = {},
      ): Promise<DbResult<ConnectionAccountsPage>> {
        const status = query.status ?? 'connected'
        const sortColumn = query.sortColumn ?? 'name'
        const sortDirection = query.sortDirection ?? 'asc'
        const pageSize = query.pageSize ?? CONNECTION_ACCOUNTS_DEFAULT_PAGE_SIZE

        // Validate locally as well as in the RPC: these values come from
        // DataTable state and URL params, and a local error names the bad input
        // instead of surfacing a 22023 from Postgres.
        if (!CONNECTION_ACCOUNT_STATUS_FILTERS.has(status)) {
          return { data: null, error: { message: `Invalid status filter: ${status}`, code: 'INVALID_FILTER' } }
        }
        if (!CONNECTION_ACCOUNT_SORT_COLUMNS.has(sortColumn)) {
          return { data: null, error: { message: `Invalid sort column: ${sortColumn}`, code: 'INVALID_SORT' } }
        }
        if (!SORT_DIRECTIONS.has(sortDirection)) {
          return { data: null, error: { message: `Invalid sort direction: ${sortDirection}`, code: 'INVALID_SORT' } }
        }
        // `Number.isInteger` before the range test, deliberately: every
        // comparison with NaN is false, so a bare `pageSize < 1` check lets NaN
        // and ±Infinity through to the RPC (same trap documented on
        // `sageChecks.list`).
        if (
          !Number.isInteger(pageSize) ||
          pageSize < 1 ||
          pageSize > CONNECTION_ACCOUNTS_MAX_PAGE_SIZE
        ) {
          return {
            data: null,
            error: {
              message: `Invalid pageSize=${query.pageSize}: must be an integer between 1 and ${CONNECTION_ACCOUNTS_MAX_PAGE_SIZE}`,
              code: 'INVALID_PAGINATION',
            },
          }
        }

        // An EMPTY filter array means "the caller resolved zero facilities /
        // packs", whose correct answer is no rows — never "no filter". The RPC
        // reads a NULL array as "no filter", so an empty one must NOT be
        // forwarded as null; it is answered here instead. (Same convention as
        // `sageChecks.list` and `applyActiveFacilityScope`.)
        if (query.facilityIds?.length === 0 || query.packIds?.length === 0) {
          return { data: { rows: [], totalCount: 0, nextCursor: null }, error: null }
        }

        const { data, error } = await getSupabaseClient().rpc('get_connection_accounts', {
          p_org_id: orgId,
          p_connection_id: connectionId,
          p_status: status,
          // `undefined` → the RPC's DEFAULT NULL → no filter. A `null` ENTRY
          // inside the array survives the JSON round trip and is how
          // "Unassigned" / "No Pack" is expressed; the RPC matches it with
          // array_position(..., NULL). {@link nullableIdFilter} is what keeps
          // those nulls typed on the way in — see its note.
          p_facility_ids: nullableIdFilter(query.facilityIds),
          p_pack_ids: nullableIdFilter(query.packIds),
          p_search: query.search,
          p_sort: sortColumn,
          p_dir: sortDirection,
          p_limit: pageSize,
          // The cursor VALUE is legitimately null at the boundary of a nullable
          // sort column's trailing NULL block; the RPC keys "is there a cursor"
          // off the ID, so both are passed independently.
          p_cursor_value: query.cursor?.value ?? undefined,
          p_cursor_id: query.cursor?.id,
        })
        if (error) return { data: null, error: { message: error.message, code: error.code } }

        const raw = (data ?? []) as unknown as Array<
          ConnectionAccountRow & { total_count: number; cursor_value: string | null }
        >
        // `total_count` and `cursor_value` are transport, not row data: strip
        // them so the UI's row type stays the shape of an account.
        const rows: ConnectionAccountRow[] = raw.map((row) => ({
          id: row.id,
          name: row.name,
          account_number: row.account_number,
          account_type: row.account_type,
          account_type_api: row.account_type_api,
          provider_subtype: row.provider_subtype,
          facility_id: row.facility_id,
          facility_name: row.facility_name,
          pack_id: row.pack_id,
          pack_name: row.pack_name,
          include_in_cash_position: row.include_in_cash_position,
          balance: row.balance,
          available_balance: row.available_balance,
          oldest_transaction_date: row.oldest_transaction_date,
          status: row.status,
        }))
        return {
          data: {
            rows,
            // Every row carries the same total; an empty page carries none, so
            // 0 (see the note on ConnectionAccountsPage.totalCount).
            totalCount: raw.length > 0 ? Number(raw[0].total_count) : 0,
            nextCursor: nextConnectionAccountsCursor(
              rows,
              raw.map((row) => row.cursor_value),
              pageSize,
            ),
          },
          error: null,
        }
      },

      /**
       * The connection of every live connection-backed account assigned to one
       * facility — one entry per account, for the facility page to count its
       * accounts per connection (the list's `accounts_count` is org-wide).
       *
       * Same "live" rule as that count (`deleted_at IS NULL`, connected or
       * disconnected). No active-facility scope: the facility is named
       * explicitly, and an inactive facility's page shows its records
       * read-only (ADR 0016 exception). Read exhaustively because the result
       * is a population — a truncated read would hide whole connections.
       *
       * @param facilityId - `facilities.id`
       * @returns `ledgersync_connections.id` per account
       */
      async connectionIdsAtFacility(facilityId: string): Promise<DbResult<string[]>> {
        const { data, error } = await fetchAllRows<{ id: string; ledgersync_connection_id: string | null }>(
          (from, to) =>
            client
              .from('bank_accounts')
              .select('id, ledgersync_connection_id')
              .eq('facility_id', facilityId)
              .is('deleted_at', null)
              .not('ledgersync_connection_id', 'is', null)
              // `id` is the primary key — the unique tiebreak `fetchAllRows` needs.
              .order('id')
              .range(from, to),
        )
        if (error) return { data: null, error }
        return {
          data: data.flatMap((row) => (row.ledgersync_connection_id ? [row.ledgersync_connection_id] : [])),
          error: null,
        }
      },

      /**
       * AC#13/AC#18: set `account_type` on one or many accounts.
       *
       * Bulk and single are the same call — the ids array is the whole
       * difference — so the row filter cannot diverge between the two paths.
       *
       * NOTE on the interaction with `include_in_cash_position`: switching an
       * account TO `credit_card` does not clear an existing inclusion flag, and
       * that is safe rather than an oversight. DCP-371 excludes credit cards
       * from Bank Balance by ACCOUNT TYPE at read time
       * ({@link applyBankBalanceAccountTypeScope} and the RPC's own conjunct),
       * so a stale `true` on a card cannot reach a reported figure. Clearing it
       * here would be an invisible second write to a column the user did not
       * touch, and it would lose their setting if they switched the type back.
       *
       * OWNERSHIP IS CHECKED FIRST, for every one of these six mutations:
       * {@link refuseNonLedgerSyncAccounts} refuses the whole call when an id
       * is not an account behind a LedgerSync connection. See its doc for why
       * an id set is not, on its own, a safe scope.
       *
       * The same predicate is then repeated in the UPDATE's own filter, which
       * is NOT decoration (the lesson of DCP-368 was about a second lock that
       * enforces nothing — this one is a row filter in the statement that does
       * the writing). It buys two things: it closes the window between the
       * check and the write, where a concurrent connection hard-delete would
       * null the link out from under us (`ON DELETE SET NULL` — the orphan
       * shape {@link applyLinkedAccountScope} describes), and it still holds if
       * some future caller reaches this statement without the pre-check.
       * Filter and SET touch DIFFERENT columns and nothing is returned, so this
       * does not trip the PostgREST "update + filter on the same column" 42703
       * bug.
       *
       * @param ids - `bank_accounts.id` values (empty = no-op success)
       * @param accountType - Target `account_type` enum value
       */
      async setAccountType(
        ids: string[],
        accountType: Tables['bank_accounts']['Row']['account_type'],
      ): Promise<DbResult<null>> {
        if (ids.length === 0) return { data: null, error: null }
        const refusal = await refuseNonLedgerSyncAccounts(ids)
        if (refusal) return refusal
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (client as any)
          .from('bank_accounts')
          .update({ account_type: accountType })
          .in('id', ids)
          .is('deleted_at', null)
          .not('ledgersync_connection_id', 'is', null)
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: null, error: null }
      },

      /**
       * AC#14/AC#18: assign (or clear) the facility of one or many accounts.
       *
       * `null` sets the account back to Unassigned, which is a real state on
       * this screen and not a missing argument — the whole point of DCP-324 is
       * that the facility is chosen PER ACCOUNT, so it must be un-choosable too.
       *
       * WHY THIS DOES NOT REUSE `bankAccounts.update`. That helper carries
       * `.is('integration_id', null)`, which restricts it to MANUAL accounts.
       * That guard is correct there — it protects Plaid/LedgerSync-managed rows
       * from the manual Settings dialogs — but it excludes precisely the
       * accounts this ticket exists to manage: every account behind a bank
       * connection is integration-linked by definition, so reusing that helper
       * would silently update zero rows and report success. Hence a separate
       * path with no such guard. The soft-delete guard is kept: reassigning a
       * deleted account is invisible work that would also resurface it in
       * historical joins.
       *
       * What DOES take the place of that guard is the opposite one, which this
       * method used to be missing entirely: {@link refuseNonLedgerSyncAccounts}
       * up front, plus `ledgersync_connection_id IS NOT NULL` in the UPDATE
       * itself (rationale for the pair on {@link setAccountType}). Together
       * they say precisely what this path is for — connection-backed accounts,
       * and nothing else — instead of merely not saying it is for manual ones.
       *
       * @param ids - `bank_accounts.id` values (empty = no-op success)
       * @param facilityId - Target facility, or null for Unassigned
       */
      async assignFacility(ids: string[], facilityId: string | null): Promise<DbResult<null>> {
        if (ids.length === 0) return { data: null, error: null }
        const refusal = await refuseNonLedgerSyncAccounts(ids)
        if (refusal) return refusal
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (client as any)
          .from('bank_accounts')
          .update({ facility_id: facilityId })
          .in('id', ids)
          .is('deleted_at', null)
          .not('ledgersync_connection_id', 'is', null)
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: null, error: null }
      },

      /**
       * AC#15: toggle whether an account counts towards Cash Position.
       *
       * CREDIT CARDS ARE REFUSED. A card's balance is what the tenant OWES, so
       * including it reports a liability as cash on hand (DCP-371). The refusal
       * is loud — an error naming the offending accounts — rather than a
       * filtered update that skips them, because a silent skip would leave the
       * UI showing a toggle the database never accepted.
       *
       * Enforced here as well as in the UI, and deliberately NOT with a
       * database trigger or a second column-level lock: this project has
       * already learned (DCP-368) that a decorative second lock adds
       * maintenance surface without adding a guarantee. The read-time
       * account-type exclusion is what makes the FIGURES safe regardless; this
       * check is what keeps the stored flag honest.
       *
       * THE RULE IS DIRECTIONAL: a card may never be INCLUDED, but EXCLUDING
       * anything is always allowed. Both halves of the check — the read in
       * {@link refuseCreditCardInclusion} and the UPDATE's own filter — therefore apply only when `include` is
       * true. Making them unconditional (as they were until DCP-324 review)
       * breaks two things:
       *   * `applyAccountType` in ConnectionAccountsTable forces `false` before
       *     turning a selection into cards. With a card already in that
       *     selection the refusal killed the WHOLE batch, so nothing was
       *     written at all and the toast read "cannot be included" for an
       *     action whose point was to exclude;
       *   * a card whose stored flag is wrongly `true` could never be cleaned
       *     up, because the only statement that could fix it filtered itself
       *     out. Read-time exclusion keeps the FIGURES right in that state, but
       *     the row stays wrong, and nothing could correct it.
       *
       * The type is re-checked in the UPDATE's own filter as well as in that
       * read. Not belt-and-braces for its own sake — it closes the window
       * between the two statements, where a concurrent `setAccountType` could
       * turn an account into a card after the read said it was not one. That
       * race only matters in the INCLUDE direction, which is the other reason
       * the filter is conditional. Filter and SET touch DIFFERENT columns, so
       * this does not trip the PostgREST "update + filter on the same column"
       * 42703 bug.
       *
       * The ownership guard runs BEFORE the account-type read, and stays a
       * separate round trip rather than being folded into it as one more
       * selected column. Two refusals with different meanings ("that is not
       * this screen's account" vs "that account is a credit card") are worth
       * more than one saved request on a hand-driven bulk action, and folding
       * them together would leave the other five mutations with a copy of the
       * rule instead of a call to it ({@link refuseNonLedgerSyncAccounts}).
       *
       * @param ids - `bank_accounts.id` values (empty = no-op success)
       * @param include - Whether the accounts should count towards Cash Position
       */
      async setIncludeInCashPosition(ids: string[], include: boolean): Promise<DbResult<null>> {
        if (ids.length === 0) return { data: null, error: null }
        const refusal = await refuseNonLedgerSyncAccounts(ids)
        if (refusal) return refusal

        // INCLUDE direction only — see the note on directionality above. The
        // read is skipped entirely when excluding, so the safe direction costs
        // one round trip instead of two.
        if (include) {
          const cardRefusal = await refuseCreditCardInclusion(ids)
          if (cardRefusal) return cardRefusal
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let update = (client as any)
          .from('bank_accounts')
          .update({ include_in_cash_position: include })
          .in('id', ids)
          .is('deleted_at', null)
          .not('ledgersync_connection_id', 'is', null)
        // Same directional rule as the read: the anti-race filter belongs to the
        // include path. Applying it when excluding would make the statement skip
        // the very rows it is meant to correct.
        if (include) {
          update = update.neq('account_type', 'credit_card')
        }
        const { error } = await update
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: null, error: null }
      },

      /**
       * AC#16/AC#18: disconnect one or many accounts — stop syncing them.
       *
       * Stamps `sync_disabled_at`, which is the HUMAN half of the derived
       * connected/disconnected status. `provider_removed_at` is never touched
       * here: that column means "the provider stopped returning this account"
       * and is owned exclusively by the sync/finalize path. Writing it from a
       * user action would forge a statement about the upstream bank, and a
       * later reconnect could not tell the two causes apart.
       *
       * Idempotent: `.is('sync_disabled_at', null)` makes a repeat call a no-op
       * that preserves the original timestamp.
       *
       * Ownership-guarded like its siblings ({@link refuseNonLedgerSyncAccounts}
       * + the same predicate in the UPDATE). `sync_disabled_at` means "this
       * SYNC is switched off", which is meaningless on an account no sync ever
       * visits: stamping a manual row would give it a status the
       * connection-accounts screen derives but nothing in the manual flow shows
       * or can clear.
       *
       * @param ids - `bank_accounts.id` values (empty = no-op success)
       */
      async disconnect(ids: string[]): Promise<DbResult<null>> {
        if (ids.length === 0) return { data: null, error: null }
        const refusal = await refuseNonLedgerSyncAccounts(ids)
        if (refusal) return refusal
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (client as any)
          .from('bank_accounts')
          .update({ sync_disabled_at: new Date().toISOString() })
          .in('id', ids)
          .is('deleted_at', null)
          .is('sync_disabled_at', null)
          .not('ledgersync_connection_id', 'is', null)
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: null, error: null }
      },

      /**
       * AC#16/AC#18: reconnect one or many accounts — resume syncing.
       *
       * Clears ONLY `sync_disabled_at`. An account the PROVIDER dropped
       * (`provider_removed_at` set) therefore stays `disconnected` after this
       * call, which is correct: the user can undo their own disconnect, but
       * they cannot make a bank hand back an account it no longer returns.
       * Clearing both would flip such a row to `connected` and leave it there
       * permanently stale, with nothing to refresh it.
       *
       * Ownership-guarded like its siblings ({@link refuseNonLedgerSyncAccounts}
       * + the same predicate in the UPDATE), for the reason spelled out on
       * {@link disconnect}: the column this clears only has a meaning on an
       * account a sync actually visits.
       *
       * @param ids - `bank_accounts.id` values (empty = no-op success)
       */
      async reconnect(ids: string[]): Promise<DbResult<null>> {
        if (ids.length === 0) return { data: null, error: null }
        const refusal = await refuseNonLedgerSyncAccounts(ids)
        if (refusal) return refusal
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (client as any)
          .from('bank_accounts')
          .update({ sync_disabled_at: null })
          .in('id', ids)
          .is('deleted_at', null)
          .not('ledgersync_connection_id', 'is', null)
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: null, error: null }
      },

      /**
       * AC#17/AC#18: permanently delete one or many accounts.
       *
       * The destructive counterpart to a connection delete: the account's DATA
       * really goes, so the order is children-before-parent —
       *
       *   1. `transactions` of those accounts — hard-deleted;
       *   2. `balances` of those accounts — hard-deleted;
       *   3. the `bank_accounts` rows — stamped `deleted_at`, NOT removed.
       *
       * WHY STEP 3 IS A TOMBSTONE AND NOT A ROW DELETE. Removing the row is
       * what made AC#17's "the action cannot be undone" false. LedgerSync goes
       * on reporting the account — we never told it anything, and it has no
       * per-account disable — and the vendor auto-refreshes every connection
       * ~6am ET, which fires `account.refresh.completed`, which finalises the
       * connection (`ledgersync-webhook/index.ts`). `finalizeConnection` skips
       * an account only when `ineligibleExternalAccountIds` finds a row for it;
       * with the row gone there is nothing to find, so `ensureBankAccount`
       * inserts it afresh. The account the user permanently deleted came back
       * by itself the next morning, unassigned and empty.
       *
       * The tombstone is what that skip needs: `isConnectionAccountSyncable`
       * rejects a row with `deleted_at` set, so the account is filtered out of
       * `eligible` before `ensureBankAccount` is ever called, and it stays
       * gone. Keeping `ledgersync_connection_id` on the row is load-bearing —
       * that deny-list reads `.eq('ledgersync_connection_id', connectionId)`,
       * so a row that lost its link would be invisible to it.
       *
       * This does NOT re-open the delete → reconnect dead-end (AC#7). There the
       * CONNECTION row is hard-deleted, which fires `ON DELETE SET NULL` on
       * this column, so those tombstones lose their link on purpose: the
       * deny-list cannot see them, `ensureBankAccount`'s `deleted_at IS NULL`
       * lookup misses them, and a reconnected bank gets fresh account rows with
       * the AC#2 defaults. Two intents, separated by whether the row still
       * belongs to a live connection: purge one account = never come back;
       * remove the whole bank and re-add it = come back as new.
       *
       * WHICH GUARD IS LOAD-BEARING DIFFERS BY PATH, and that matters to anyone
       * changing either of them (raised in review on the PR carrying this
       * change). The two paths reach the same outcome today through a DIFFERENT
       * number of guards:
       *   * a tombstone from THIS method keeps its `ledgersync_connection_id`,
       *     so BOTH guards apply — `ineligibleExternalAccountIds` sees the row
       *     and denies it, and `ensureBankAccount`'s lookup filter misses it;
       *   * a tombstone from a CONNECTION delete has that column nulled by
       *     `ON DELETE SET NULL`, and the deny-list reads
       *     `.eq('ledgersync_connection_id', connectionId)` — so it cannot see
       *     the row at all, and the lookup filter is the ONLY thing standing
       *     between the user and a resurrected account.
       * So removing the deny-list check would be invisible here and fatal there;
       * removing the lookup filter breaks both. Neither is redundant.
       *
       * Step 1 is NOT optional. `transactions.bank_account_id` is ON DELETE SET
       * NULL, so deleting the account first would not remove its transactions —
       * it would strand them with a null bank reference, where they keep
       * counting towards dashboards and cross-checking while displaying no
       * Bank / Account Name at all. Deleting them explicitly, first, is what
       * makes "permanently delete" mean what it says.
       *
       * STEPS 1 AND 2 DELETE ONLY WHAT THE INTEGRATION WROTE
       * ({@link INTEGRATION_TRANSACTION_SOURCES} /
       * {@link INTEGRATION_BALANCE_SOURCES}). This action removes an account's
       * API data; rows a PERSON supplied — hand-entered ('manual') or uploaded
       * from a spreadsheet ('file_import') — are a different population and
       * stay. The asymmetry is about recoverability: API history comes back
       * from the vendor on the next connect, and nothing brings back a figure
       * somebody typed.
       *
       * Both can really sit on an integration-linked account.
       * `ManualTransactionFormDialog` picks its account from
       * `bankAccounts.list()`, which applies no provenance filter; neither
       * `manualTransactions.create` nor any CHECK constraint couples
       * `transactions.source` to the account's linkage; and the file-import
       * path maps a spreadsheet row onto whatever account matches. Without the
       * `source` predicate all of it was destroyed irreversibly, by an action
       * the user reaches through the INTEGRATION's own screen.
       *
       * The survivors stay READABLE because the account is TOMBSTONED rather
       * than deleted and `TRANSACTION_ACCOUNT_EMBED_SCOPED` does not filter
       * `deleted_at` — the same state the connection-delete path already
       * relies on, so they keep rendering their Bank / Account Name columns.
       *
       * {@link refuseNonLedgerSyncAccounts} does NOT cover this: it stops a
       * manual ACCOUNT's id, a different population from manual or imported
       * TRANSACTIONS on an API account.
       *
       * Step 2 is LOAD-BEARING, not belt-and-braces. `balances.bank_account_id`
       * is ON DELETE CASCADE, but step 3 is an UPDATE — the account row
       * survives as a tombstone, so the cascade never fires and nothing else
       * would remove these rows.
       *
       * Contrast with the `delete-connection` edge action
       * (`_shared/ledgersync-connection-delete.ts`), which KEEPS transactions:
       * that removes a connection while its history stays on the books. This is
       * the explicit "remove the data" action (AC#17), so history is not
       * preserved and the caller is expected to have confirmed. Both leave an
       * account tombstone; only this one empties it first.
       *
       * THE OWNERSHIP CHECK RUNS BEFORE ALL THREE DELETES, and on this method
       * it is the protection rather than a second opinion. Step 1 destroys
       * `transactions` outright: by the time a filter on the third statement
       * could reject an id, that id's history is already gone and nothing can
       * restore it — the step-3 tombstone preserves the ROW, never its data. The two child deletes CANNOT carry
       * the guard themselves — they filter `bank_account_id` on tables that do
       * not have `ledgersync_connection_id`, and PostgREST cannot qualify a
       * DELETE by a joined table — so {@link refuseNonLedgerSyncAccounts} is
       * the single point where a manual account's id is stopped. It is called
       * first, and its failure returns before any statement is issued.
       *
       * @param ids - `bank_accounts.id` values (empty = no-op success)
       */
      async permanentlyDelete(ids: string[]): Promise<DbResult<null>> {
        if (ids.length === 0) return { data: null, error: null }
        const refusal = await refuseNonLedgerSyncAccounts(ids)
        if (refusal) return refusal

        // SCOPED TO WHAT AN INTEGRATION WROTE — see the doc block. Rows a
        // person supplied (hand-entered or uploaded from a file) are left
        // alone: they are not what this action exists to remove, and no
        // re-sync can bring them back.
        const { error: txnError } = await client
          .from('transactions')
          .delete()
          .in('bank_account_id', ids)
          .in('source', INTEGRATION_TRANSACTION_SOURCES)
        if (txnError) {
          return { data: null, error: { message: txnError.message, code: txnError.code } }
        }

        // Same rule, plus NULL. `balances.source` is NULLABLE because rows
        // predating DCP-120 record no origin — and that migration is what
        // INTRODUCED hand-entered balances, so a row older than it cannot be
        // one: its only possible writer was the integration sync of the day.
        // The disjunction is needed because PostgREST's `in` does not match
        // NULL, which would strand those rows on a "permanently deleted"
        // account forever.
        const { error: balancesError } = await client
          .from('balances')
          .delete()
          .in('bank_account_id', ids)
          .or(`source.is.null,source.in.(${INTEGRATION_BALANCE_SOURCES.join(',')})`)
        if (balancesError) {
          return { data: null, error: { message: balancesError.message, code: balancesError.code } }
        }

        // A TOMBSTONE, NOT A ROW DELETE — see the doc block. Sets `deleted_at`
        // and FILTERS on it in the same request, which is only safe because
        // nothing is returned: no `.select()`, so supabase-js sends
        // `Prefer: return=minimal`. Asking for a body or a count here would
        // fail with PostgREST 42703 (same trap as `bankAccounts.delete`).
        // The `.is('deleted_at', null)` guard preserves the original stamp on a
        // repeat call, and `ledgersync_connection_id` is deliberately left
        // INTACT: it is what keeps the row visible to the provider-side skip.
        const { error: accountsError } = await client
          .from('bank_accounts')
          .update({ deleted_at: new Date().toISOString() })
          .in('id', ids)
          .not('ledgersync_connection_id', 'is', null)
          .is('deleted_at', null)
        if (accountsError) {
          return { data: null, error: { message: accountsError.message, code: accountsError.code } }
        }
        return { data: null, error: null }
      },
    },

    creditCards: {
      /**
       * One page of the Credit Card Availability table (DCP-374).
       *
       * Thin by design: every rule that decides WHICH cards appear lives in
       * `get_credit_card_availability`, because the page pages server-side and
       * a client holding twenty of two hundred rows cannot filter, sort or
       * count the rest. What is left here is the argument marshalling and the
       * two conventions the RPC cannot express for itself.
       *
       * @param query - filters, search, sort and the page to fetch
       * @returns the page's rows plus the total the filter set matches
       */
      async list(query: CreditCardAvailabilityQuery = {}): Promise<
        DbResult<{ rows: CreditCardAvailabilityRow[]; totalCount: number }>
      > {
        const pageSize = query.pageSize ?? CREDIT_CARD_PAGE_SIZE
        if (
          !Number.isInteger(pageSize) ||
          pageSize < 1 ||
          pageSize > CREDIT_CARD_MAX_PAGE_SIZE
        ) {
          return {
            data: null,
            error: {
              message: `Invalid pageSize=${pageSize}: must be an integer between 1 and ${CREDIT_CARD_MAX_PAGE_SIZE}`,
              code: 'INVALID_PAGINATION',
            },
          }
        }

        const page = query.page ?? 1
        if (!Number.isInteger(page) || page < 1) {
          return {
            data: null,
            error: {
              message: `Invalid page=${page}: must be an integer >= 1`,
              code: 'INVALID_PAGINATION',
            },
          }
        }

        // An EMPTY filter array means "the caller resolved zero facilities /
        // packs", whose correct answer is no rows — never "no filter". The RPC
        // reads a NULL array as "no filter", so an empty one must NOT be
        // forwarded as null; it is answered here instead. (Same convention as
        // `ledgerSyncAccounts.listByConnection` and `sageChecks.list`.)
        if (query.facilityIds?.length === 0 || query.packIds?.length === 0) {
          return { data: { rows: [], totalCount: 0 }, error: null }
        }

        const { data, error } = await getSupabaseClient().rpc('get_credit_card_availability', {
          p_org_id: orgId,
          // `undefined` → the RPC's DEFAULT NULL → no filter. A `null` ENTRY
          // inside the array survives the JSON round trip and is how "No Pack"
          // is expressed; the RPC matches it with array_position(..., NULL).
          p_facility_ids: nullableIdFilter(query.facilityIds),
          p_pack_ids: nullableIdFilter(query.packIds),
          p_search: query.search,
          p_due_from: query.dueFrom,
          p_due_to: query.dueTo,
          p_sort: query.sort ?? 'facility',
          p_dir: query.direction ?? 'asc',
          p_limit: pageSize,
          // Pages are 1-based on screen and 0-based in SQL. Converting here
          // rather than in the hook keeps every caller on the page number the
          // user actually sees.
          p_offset: (page - 1) * pageSize,
        })
        if (error) return { data: null, error: { message: error.message, code: error.code } }

        const raw = (data ?? []) as unknown as Array<
          CreditCardAvailabilityRow & { total_count: number }
        >
        // `total_count` is transport, not row data: it repeats on every row, so
        // it is lifted out once and stripped from the rows the table renders.
        const rows: CreditCardAvailabilityRow[] = raw.map((row) => ({
          id: row.id,
          facility_id: row.facility_id,
          facility_name: row.facility_name,
          pack_id: row.pack_id,
          pack_name: row.pack_name,
          name: row.name,
          account_number: row.account_number,
          credit_limit: row.credit_limit,
          balance: row.balance,
          available_credit: row.available_credit,
          availability: row.availability,
          statement_balance: row.statement_balance,
          minimum_payment: row.minimum_payment,
          payment_due_date: row.payment_due_date,
          data_updated_at: row.data_updated_at,
          connection_status: row.connection_status,
          data_refreshed_at: row.data_refreshed_at,
        }))
        // An empty page carries no row to read the total off. That is only
        // correct as zero for the FIRST page; past the end it means "you paged
        // beyond the set", which the caller detects by its own page number.
        return { data: { rows, totalCount: raw[0]?.total_count ?? 0 }, error: null }
      },
    },

    plaidSyncCursors: {
      /**
       * List all sync cursors with fields relevant to sync status monitoring.
       * Returns id, plaid_item_id, sync_status, last_sync_at, last_sync_source, transactions_synced, and last_error.
       */
      async list(): Promise<DbResult<TenantSyncCursor[]>> {
        const { data, error } = await client
          .from('plaid_sync_cursors')
          .select('id, plaid_item_id, sync_status, last_sync_at, last_sync_source, transactions_synced, last_error')
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: (data || []) as unknown as TenantSyncCursor[], error: null }
      },
    },

    bankTransactions: {
      /**
       * List transactions for the Banking Dashboard table.
       *
       * Name is historical — despite the namespace label the query is
       * source-agnostic now (DCP-223). The Banking Dashboard table is
       * meant to surface manual / file_import / Plaid rows together,
       * so the SELECT joins on `bank_accounts` (for the real account
       * name + last-4 digits of its account number) and `categories`
       * (for the user-defined category name + inflow/outflow type)
       * via the FK columns added when those tables were introduced.
       *
       * Render layer (`mapTransactions` in BankingDashboardPage) reads
       * these joined fields directly — no more fallback to
       * "Unknown Account" / "****" / "Other" for non-Plaid rows.
       *
       * DCP-400: transactions of `credit_card` accounts are excluded — see
       * {@link applyDailyBankingScope}.
       *
       * No organizationId filter needed — schema provides isolation.
       */
      async list(filters?: {
        startDate?: string
        endDate?: string
        facilityIds?: string[]
        accountIds?: string[]
        categories?: string[]
        search?: string
        limit?: number
        offset?: number
        /** DCP-123 (AC#6): server-side table sort; absent = newest first. */
        sort?: BankTransactionSort
      }): Promise<DbResult<TenantPlaidTransaction[]>> {
        let query = client
          .from('transactions')
          .select(
            // DCP-223: join bank_accounts + categories so the table renders
            // the real account name/number/category for every source.
            // DCP-324: the account embed also carries the row's FACILITY, which
            // the transaction no longer holds itself, and its `!inner` hops are
            // half of the scope applied just below.
            // DCP-123: the facility sort needs one more embed to order by — see
            // FACILITY_SORT_EMBED. Only selected while that sort is active.
            `*, ${TRANSACTION_ACCOUNT_EMBED_SCOPED}, categories(name, type)${
              filters?.sort?.key === 'facility' ? `, ${FACILITY_SORT_EMBED}` : ''
            }`,
          )
        if (filters?.startDate) query = query.gte('transaction_date', filters.startDate)
        if (filters?.endDate) query = query.lte('transaction_date', filters.endDate)
        // DCP-292 (AC2) + DCP-324 (AC#22): scope the Banking Dashboard through
        // the row's ACCOUNT — a deactivated facility's transactions leave the
        // table, its row count and the CSV export, and so do the transactions of
        // an account assigned to no facility at all. (These methods read the
        // whole `transactions` table — the "plaid" naming is historical; Plaid
        // ingestion is abandoned.)
        // DCP-400: also drop credit_card account transactions — see applyDailyBankingScope.
        query = applyDailyBankingScope(query, filters?.facilityIds)
        // Account + category filters share validated clause-builders with
        // count()/distinctCategories so the three can't drift (Presto review).
        const accountClause = accountFilterClause(filters?.accountIds)
        if (accountClause) query = query.or(accountClause)
        const categoryClause = categoryFilterClause(filters?.categories)
        if (categoryClause) query = query.or(categoryClause)
        if (filters?.search) {
          const safe = escapeOrFilterValue(filters.search)
          query = query.or(`description.ilike.${safe},vendor.ilike.${safe}`)
        }
        // DCP-123 (AC#6): user-selected sort + deterministic tie-breakers, so
        // pagination is stable (see applyBankTransactionSort).
        query = applyBankTransactionSort(query, filters?.sort)
        // Apply pagination: use range() if offset provided, otherwise use limit()
        if (filters?.offset !== undefined) {
          query = query.range(filters.offset, filters.offset + (filters.limit || 50) - 1)
        } else if (filters?.limit) {
          query = query.limit(filters.limit)
        }
        const { data, error } = await query
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: (data || []) as unknown as TenantPlaidTransaction[], error: null }
      },

      /**
       * Count Plaid transactions with optional filters (for pagination)
       */
      async count(filters?: {
        startDate?: string
        endDate?: string
        facilityIds?: string[]
        accountIds?: string[]
        categories?: string[]
        search?: string
      }): Promise<DbResult<number>> {
        let query = client
          .from('transactions')
          // DCP-324: the embed is selected for its JOIN, not its columns — a
          // `head` count returns no body, and the `!inner` hops are what make
          // the count agree with the rows `list` actually returns. Verified on
          // PostgREST 14.17: `count=exact` honours an inner embed.
          .select(`id, ${TRANSACTION_SCOPE_EMBED}`, { count: 'exact', head: true })
        if (filters?.startDate) query = query.gte('transaction_date', filters.startDate)
        if (filters?.endDate) query = query.lte('transaction_date', filters.endDate)
        // DCP-292 (AC2) + DCP-324 (AC#22): the same account-derived scope
        // `list` applies, so the pagination count can never count a row the
        // table does not show.
        // DCP-400: also drop credit_card account transactions — see applyDailyBankingScope.
        query = applyDailyBankingScope(query, filters?.facilityIds)
        // Account + category filters share validated clause-builders with
        // count()/distinctCategories so the three can't drift (Presto review).
        const accountClause = accountFilterClause(filters?.accountIds)
        if (accountClause) query = query.or(accountClause)
        const categoryClause = categoryFilterClause(filters?.categories)
        if (categoryClause) query = query.or(categoryClause)
        if (filters?.search) {
          const safe = escapeOrFilterValue(filters.search)
          query = query.or(`description.ilike.${safe},vendor.ilike.${safe}`)
        }
        const { count, error } = await query
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: count || 0, error: null }
      },

      /**
       * Fetch distinct category options for the dashboard filter dropdown.
       *
       * Source-agnostic (DCP-223 follow-up): the dashboard table renders
       * categories for every source, so the filter options must too.
       * Two branches are merged:
       *   - user-defined categories via the `category_id` FK → `categories.name`
       *     (covers manual / file-import / Plaid rows that were categorized)
       *   - legacy Plaid `plaid_category` strings → human label via getCategoryLabel
       * Each option's `value` is what `list`/`count` match against
       * (UUID = category_id, uppercase string = plaid_category).
       *
       * Filters by date range, facility, and account — categories filter is
       * intentionally excluded to avoid a circular dependency (selecting a
       * category would hide the others from the dropdown).
       * @param filters - Optional date range, facility, and account filters
       * @returns Deduplicated `{ value, label }` options sorted by label
       */
      async distinctCategories(filters?: {
        startDate?: string
        endDate?: string
        facilityIds?: string[]
        accountIds?: string[]
      }): Promise<DbResult<Array<{ value: string; label: string }>>> {
        // Same validated account OR-clause as list()/count().
        const accountOrClause = accountFilterClause(filters?.accountIds)

        // Branch 1: user-defined categories joined via category_id FK
        let categoryQuery = client
          .from('transactions')
          .select(`category_id, categories(name), ${TRANSACTION_SCOPE_EMBED}`)
          .not('category_id', 'is', null)
        if (filters?.startDate) categoryQuery = categoryQuery.gte('transaction_date', filters.startDate)
        if (filters?.endDate) categoryQuery = categoryQuery.lte('transaction_date', filters.endDate)
        // DCP-292 (AC2) + DCP-324 (AC#22): scope the Category-filter options the
        // same way the table is scoped, so an option can never select rows the
        // table refuses to show — neither a deactivated facility's categories
        // nor an unassigned account's.
        // DCP-400: also drop credit_card account transactions — see applyDailyBankingScope.
        categoryQuery = applyDailyBankingScope(categoryQuery, filters?.facilityIds)
        if (accountOrClause) categoryQuery = categoryQuery.or(accountOrClause)
        const { data: categoryRows, error: categoryError } = await categoryQuery
        if (categoryError) {
          return { data: null, error: { message: categoryError.message, code: categoryError.code } }
        }

        // Branch 2: legacy Plaid category strings
        let plaidQuery = client
          .from('transactions')
          .select(`plaid_category, ${TRANSACTION_SCOPE_EMBED}`)
          .eq('source', 'plaid')
          .not('plaid_category', 'is', null)
        if (filters?.startDate) plaidQuery = plaidQuery.gte('transaction_date', filters.startDate)
        if (filters?.endDate) plaidQuery = plaidQuery.lte('transaction_date', filters.endDate)
        // DCP-292 / DCP-324: scope the legacy-category branch identically to
        // branch 1 and to bankTransactions.list/count, so the two branches of
        // one dropdown cannot describe different populations.
        // DCP-400: also drop credit_card account transactions — see applyDailyBankingScope.
        plaidQuery = applyDailyBankingScope(plaidQuery, filters?.facilityIds)
        if (accountOrClause) plaidQuery = plaidQuery.or(accountOrClause)
        const { data: plaidRows, error: plaidError } = await plaidQuery
        if (plaidError) {
          return { data: null, error: { message: plaidError.message, code: plaidError.code } }
        }

        // Merge + dedup by value (option value is what list/count filter on)
        const byValue = new Map<string, { value: string; label: string }>()
        for (const row of (categoryRows || []) as Array<{ category_id: string | null; categories: { name: string } | null }>) {
          const value = row.category_id
          const label = row.categories?.name
          // Skip rows whose joined category name is null (orphaned FK)
          if (value && label) byValue.set(value, { value, label })
        }
        for (const row of (plaidRows || []) as Array<{ plaid_category: string }>) {
          const value = row.plaid_category
          if (value && !byValue.has(value)) byValue.set(value, { value, label: getCategoryLabel(value) })
        }

        const options = [...byValue.values()].sort((a, b) => a.label.localeCompare(b.label))
        return { data: options, error: null }
      },

      /**
       * Assign one category to one or more transactions from the Daily Banking
       * table — the inline Category dropdown and the bulk action (DCP-123,
       * AC#2/#3).
       *
       * Goes through the `update_transactions_category` RPC rather than a
       * client `.update()`: the RPC checks `manage_banking` (accounts_payable,
       * admin) SERVER-side and rejects everyone else with 42501, so the page's
       * `canCategorize` gate is no longer the only thing standing between a
       * treasury / view_only user and the write. Same reasoning and shape as the
       * DCP-167 LOC RPCs (`save_pack_borrowing_base`): public client,
       * `p_org_id` = this tenant's organization.
       *
       * Deliberately NOT source-gated, unlike `manualTransactions.update` (which
       * only touches MANUAL_PAGE_TRANSACTION_SOURCES): categorizing BANK-FEED
       * rows is the point of this feature — they arrive uncategorized and
       * Daily Banking is where the user classifies them. Only `category_id` is
       * written, so no other field of a bank-feed row can change through here.
       *
       * ONE request, ONE atomic UPDATE: the ids travel in the POST body, not
       * the URL, so a bulk selection needs no batching and can no longer
       * half-commit. The RPC caps a call at 5000 ids (54000, see the
       * migration) — far beyond a hand-built selection, so the cap is not
       * re-batched here; hitting it surfaces as that error.
       *
       * Every id is UUID-validated before the call — an invalid one fails the
       * whole request with INVALID_INPUT rather than being silently dropped, so
       * the caller can never believe it saved rows it did not. Errors from the
       * RPC (42501 permission, 23503 unknown category, 22023 inactive one, …)
       * are passed through raw.
       *
       * @param ids - transaction ids to (re)categorize; duplicates are dropped;
       *              empty = no-op returning 0 (no request)
       * @param categoryId - the `categories.id` to assign
       * @returns the number of rows the UPDATE actually changed — out of the
       *          de-duplicated ids, so fewer than that only when some ids no
       *          longer exist (deleted or never existed)
       */
      async updateCategory(ids: string[], categoryId: string): Promise<DbResult<number>> {
        if (!isUuid(categoryId)) {
          return { data: null, error: { message: 'Invalid category id', code: 'INVALID_INPUT' } }
        }
        const uniqueIds = [...new Set(ids)]
        if (uniqueIds.length === 0) return { data: 0, error: null }
        if (!uniqueIds.every(isUuid)) {
          return { data: null, error: { message: 'Invalid transaction id', code: 'INVALID_INPUT' } }
        }
        const { data, error } = await getSupabaseClient().rpc('update_transactions_category', {
          p_org_id: orgId,
          p_transaction_ids: uniqueIds,
          p_category_id: categoryId,
        })
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data, error: null }
      },
    },

    manualTransactions: {
      /**
       * List manually entered transactions with optional filters.
       * Queries transactions table filtered to sources in MANUAL_PAGE_TRANSACTION_SOURCES.
       */
      async list(filters?: {
        startDate?: string
        endDate?: string
        facilityIds?: string[]
        limit?: number
        offset?: number
      }): Promise<DbResult<TenantTransaction[]>> {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let query = (client as any)
          .from('transactions')
          .select(`*, ${TRANSACTION_ACCOUNT_EMBED_SCOPED}, categories!transactions_category_id_fkey(name, type)`)
          .in('source', MANUAL_PAGE_TRANSACTION_SOURCES)
          .order('transaction_date', { ascending: false })
        if (filters?.startDate) query = query.gte('transaction_date', filters.startDate)
        if (filters?.endDate) query = query.lte('transaction_date', filters.endDate)
        // DCP-292 + DCP-324 (AC#22): show only the transactions of accounts that
        // sit in an ACTIVE facility. Explicit ids come from the active-only
        // selector, but are still intersected with `is_active` in SQL rather
        // than trusted (see applyTransactionFacilityScope).
        query = applyTransactionFacilityScope(query, filters?.facilityIds)
        if (filters?.offset !== undefined) {
          query = query.range(filters.offset, filters.offset + (filters.limit || MANUAL_TRANSACTIONS_DEFAULT_PAGE_SIZE) - 1)
        } else if (filters?.limit) {
          query = query.limit(filters.limit)
        }
        const { data, error } = await query
        if (error) return { data: null, error: { message: error.message, code: error.code } }

        const rows = (data || []) as unknown as TenantTransaction[]

        // DCP-169: flatten `metadata.notes` into a first-class `notes` string
        // for the table to render (the `*` select already returns metadata).
        for (const row of rows) flattenNotes(row)

        // Profiles live in public schema — cross-schema join not supported by PostgREST.
        // Fetch user names separately and merge into results.
        const userIds = [...new Set(rows.map((r) => r.user_id).filter(Boolean))] as string[]
        if (userIds.length > 0) {
          const { data: profiles } = await getSupabaseClient()
            .from('profiles')
            .select('id, full_name')
            .in('id', userIds)
          const profileMap = new Map(profiles?.map((p) => [p.id, p.full_name]) ?? [])
          for (const row of rows) {
            if (row.user_id) {
              row.profiles = { full_name: profileMap.get(row.user_id) ?? '-' }
            }
          }
        }

        return { data: rows, error: null }
      },

      /**
       * Count manually entered transactions with optional filters (for pagination).
       * Queries transactions table filtered to sources in MANUAL_PAGE_TRANSACTION_SOURCES.
       */
      async count(filters?: {
        startDate?: string
        endDate?: string
        facilityIds?: string[]
      }): Promise<DbResult<number>> {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let query = (client as any)
          .from('transactions')
          .select(`id, ${TRANSACTION_SCOPE_EMBED}`, { count: 'exact', head: true })
          .in('source', MANUAL_PAGE_TRANSACTION_SOURCES)
        if (filters?.startDate) query = query.gte('transaction_date', filters.startDate)
        if (filters?.endDate) query = query.lte('transaction_date', filters.endDate)
        // DCP-292 + DCP-324: match list() exactly — same embed, same scope — so
        // the paginated count stays in lockstep with the visible rows.
        query = applyTransactionFacilityScope(query, filters?.facilityIds)
        const { count, error } = await query
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: count ?? 0, error: null }
      },

      /**
       * Update a manual or file-imported transaction by ID.
       * Scope matches manualTransactions.list — both `source = 'manual'` and `source = 'file_import'`
       * rows are visible in the Manual Input table and therefore must be editable from the same UI.
       * Skips undefined values to preserve existing data; explicit null clears the field.
       */
      async update(id: string, updateData: ManualTransactionInput): Promise<DbResult<TenantTransaction>> {
        const dbData: Record<string, unknown> = {}
        const updateDataRecord = updateData as unknown as Record<string, unknown>
        Object.keys(updateDataRecord).forEach(key => {
          const value = updateDataRecord[key]
          if (value !== undefined) {
            dbData[key] = value
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await (client as any)
          .from('transactions')
          .update(dbData)
          .eq('id', id)
          // Include file_import rows: the Manual Transactions list shows
          // them, so the edit dialog must be able to PATCH them too
          // (DCP-131). Plaid rows stay untouchable.
          .in('source', MANUAL_PAGE_TRANSACTION_SOURCES)
          .select(`*, ${TRANSACTION_ACCOUNT_EMBED}, categories!transactions_category_id_fkey(name, type)`)
          .single()
        if (error) return { data: null, error: { message: error.message, code: error.code } }

        // Profiles live in public schema — cross-schema join not supported by PostgREST.
        // Fetch user name separately and merge into result.
        const row = flattenNotes(data as unknown as TenantTransaction)
        if (row.user_id) {
          const { data: profile } = await getSupabaseClient()
            .from('profiles')
            .select('full_name')
            .eq('id', row.user_id)
            .single()
          row.profiles = { full_name: profile?.full_name ?? '-' }
        }

        return { data: row, error: null }
      },

      /**
       * Delete a transaction by ID, scoped to the Manual Transactions page's
       * mutable sources. file_import rows count — they show up in the same
       * list and the UI's delete button must actually delete them
       * (DCP-131); a `.eq('source', 'manual')` filter here was a silent
       * no-op for any non-manual row.
       */
      async delete(id: string): Promise<DbResult<null>> {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (client as any)
          .from('transactions')
          .delete()
          .eq('id', id)
          .in('source', MANUAL_PAGE_TRANSACTION_SOURCES)
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: null, error: null }
      },

      /**
       * Persist a transaction's free-text notes (DCP-169).
       *
       * Notes have no dedicated column — they live at `metadata.notes` (the
       * convention established by the DCP-290 `get_loc_transactions` RPC).
       *
       * Goes through the `update_transaction_notes` RPC rather than a direct
       * client `.update()` (PR #169 review): it merges ONLY the `notes` key in a
       * single atomic UPDATE (`metadata || jsonb_build_object('notes', …)`),
       * removing the read-then-write race the previous two-step client merge had.
       *
       * The RPC does NOT check a role permission — per a BA decision Notes are
       * editable by every role. It still enforces `can_access_organization`
       * (no cross-org edits) and the manual-page source gate
       * ('manual','file_import'), so a Plaid/Sage row stays untouchable.
       *
       * @param id    Transaction ID to annotate.
       * @param notes New notes text (empty string clears the note).
       * @returns Standard DbResult with `null` data on success.
       */
      async updateNotes(id: string, notes: string): Promise<DbResult<null>> {
        // `rpc` is cast until the next `pnpm db:types` regenerates database.ts
        // with the new update_transaction_notes function (deployed via migration
        // 20260731020000). Same temporary-cast pattern as the DCP-169 read path.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (getSupabaseClient().rpc as any)('update_transaction_notes', {
          p_org_id: orgId,
          p_transaction_id: id,
          p_notes: notes,
        })
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: null, error: null }
      },

      /**
       * Create a new manual transaction.
       * Automatically sets source = 'manual'.
       */
      async create(input: ManualTransactionInput): Promise<DbResult<TenantTransaction>> {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await (client as any)
          .from('transactions')
          .insert({ ...input, source: 'manual' })
          .select(`*, ${TRANSACTION_ACCOUNT_EMBED}, categories!transactions_category_id_fkey(name, type)`)
          .single()
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: flattenNotes(data as unknown as TenantTransaction), error: null }
      },

      /**
       * Create multiple manual transactions in a single database call.
       * Automatically sets source = 'manual' for each row.
       * @param inputs - Array of transaction data
       */
      async createMany(inputs: ManualTransactionInput[]): Promise<DbResult<TenantTransaction[]>> {
        const rows = inputs.map((input) => ({ ...input, source: 'manual' }))
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await (client as any)
          .from('transactions')
          .insert(rows)
          .select(`*, ${TRANSACTION_ACCOUNT_EMBED}, categories!transactions_category_id_fkey(name, type)`)
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: ((data || []) as unknown as TenantTransaction[]).map(flattenNotes), error: null }
      },
    },

    fileImports: {
      /**
       * List file import records ordered by creation date descending.
       * Optionally filtered by status, with limit/offset pagination.
       * Fetches user display names from the public.profiles table separately,
       * since cross-schema joins are not supported by PostgREST.
       * @param filters - Optional status filter and pagination controls
       */
      async list(filters?: { status?: string; limit?: number; offset?: number; importType?: string }): Promise<DbResult<FileImportRow[]>> {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let query = (client as any)
          .from('file_imports')
          .select('*')
          .order('created_at', { ascending: false })

        if (filters?.status) query = query.eq('status', filters.status)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const importTypeFilter = applyImportTypeFilter<any>(query, filters?.importType)
        if (importTypeFilter.error) return { data: null, error: importTypeFilter.error }
        query = importTypeFilter.query
        if (filters?.offset !== undefined) {
          query = query.range(filters.offset, filters.offset + (filters.limit || 20) - 1)
        } else if (filters?.limit) {
          query = query.limit(filters.limit)
        }

        const { data, error } = await query
        if (error) return { data: null, error: { message: error.message, code: error.code } }

        const rows = (data || []) as unknown as FileImportRow[]

        // Parse errors JSONB field if it arrived as a string
        for (const row of rows) {
          if (typeof row.errors === 'string') {
            try { row.errors = JSON.parse(row.errors) } catch { row.errors = [] }
          }
        }

        // Profiles live in public schema — cross-schema join not supported by PostgREST.
        // Fetch user names separately and merge into results.
        const userIds = [...new Set(rows.map(r => r.user_id).filter(Boolean))] as string[]
        if (userIds.length > 0) {
          const { data: profiles } = await getSupabaseClient()
            .from('profiles')
            .select('id, full_name')
            .in('id', userIds)
          const profileMap = new Map(profiles?.map(p => [p.id, p.full_name]) ?? [])
          for (const row of rows) {
            if (row.user_id) {
              row.profiles = { full_name: profileMap.get(row.user_id) ?? '-' }
            }
          }
        }

        return { data: rows, error: null }
      },

      /**
       * Count file import records for pagination, with optional status filter.
       * @param filters - Optional status filter
       */
      async count(filters?: { status?: string; importType?: string }): Promise<DbResult<number>> {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let query = (client as any)
          .from('file_imports')
          .select('id', { count: 'exact', head: true })
        if (filters?.status) query = query.eq('status', filters.status)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const importTypeFilter = applyImportTypeFilter<any>(query, filters?.importType)
        if (importTypeFilter.error) return { data: null, error: importTypeFilter.error }
        query = importTypeFilter.query
        const { count, error } = await query
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: count ?? 0, error: null }
      },

      /**
       * Get a single file import record by ID.
       * Parses the errors JSONB field from string if necessary.
       * @param id - UUID of the file import record
       */
      async getById(id: string): Promise<DbResult<FileImportRow | null>> {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await (client as any)
          .from('file_imports')
          .select('*')
          .eq('id', id)
          .single()

        if (error) return { data: null, error: { message: error.message, code: error.code } }

        const row = data as unknown as FileImportRow
        if (typeof row.errors === 'string') {
          try { row.errors = JSON.parse(row.errors) } catch { row.errors = [] }
        }

        return { data: row, error: null }
      },

      /**
       * Create a new file import record with status 'pending'.
       * The Trigger.dev background job picks it up and updates status/counts.
       * @param input - File metadata and uploader's user ID
       */
      async create(input: FileImportInput): Promise<DbResult<FileImportRow>> {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await (client as any)
          .from('file_imports')
          .insert({
            file_name: input.file_name,
            file_path: input.file_path,
            file_type: input.file_type,
            user_id: input.user_id,
            import_type: input.import_type,
            status: 'pending',
          })
          .select()
          .single()

        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: data as unknown as FileImportRow, error: null }
      },

      /**
       * Fetch transactions belonging to a specific file import with FK joins.
       * Reuses the same join pattern as manualTransactions.list() for consistency.
       *
       * DCP-324: the facility comes through the account like everywhere else,
       * but via the LEFT embed and with NO facility scope — this is an
       * import-scoped audit view, and hiding an unassigned account's rows would
       * contradict both the import's own row counter and `countTransactions`,
       * which joins nothing. See {@link TRANSACTION_ACCOUNT_EMBED}.
       *
       * @param fileImportId - UUID of the file_imports record
       * @param filters - Optional pagination (limit, offset)
       * @returns Array of TenantTransaction objects
       */
      async listTransactions(
        fileImportId: string,
        filters?: { limit?: number; offset?: number },
      ): Promise<TenantTransaction[]> {
        const limit = filters?.limit ?? DEFAULT_DETAILS_PAGE_SIZE
        const offset = filters?.offset ?? 0

        const { data, error } = await client
          .from('transactions')
          .select(`*, ${TRANSACTION_ACCOUNT_EMBED}, categories!transactions_category_id_fkey(name, type)`)
          .eq('file_import_id', fileImportId)
          .order('transaction_date', { ascending: true })
          .range(offset, offset + limit - 1)

        if (error) {
          // Throw instead of returning [] — empty-from-error is indistinguishable
          // from empty-from-no-data at the caller, and React Query needs the
          // rejection to flip its `error` state so the UI can render a real
          // error banner instead of silently showing "no rows".
          throw new Error(`Failed to fetch file import transactions: ${error.message}`)
        }

        // Flatten metadata.notes → notes like every other TenantTransaction
        // path (DCP-169): the type marks `notes` required, so a caller reading
        // it off this result must get '' not undefined (PR #169 review).
        return ((data ?? []) as unknown as TenantTransaction[]).map(flattenNotes)
      },

      /**
       * Count transactions belonging to a specific file import.
       *
       * @param fileImportId - UUID of the file_imports record
       * @returns Total count of transactions for this import
       */
      async countTransactions(fileImportId: string): Promise<number> {
        const { count, error } = await client
          .from('transactions')
          .select('id', { count: 'exact', head: true })
          .eq('file_import_id', fileImportId)

        if (error) {
          // Throw to match listTransactions / listProjections (unified error
          // handling). React Query lifts the rejection into `query.error`
          // and the pagination UI can render "Unknown total" instead of "0".
          throw new Error(`Failed to count file import transactions: ${error.message}`)
        }

        return count ?? 0
      },

      /**
       * Fetch projections belonging to a specific file import with FK joins.
       * @param fileImportId - UUID of the file_imports record
       * @param filters - Optional pagination (limit, offset)
       * @returns Array of projection objects with related category and facility data
       */
      async listProjections(
        fileImportId: string,
        filters?: { limit?: number; offset?: number },
      ): Promise<FileImportProjectionRow[]> {
        const limit = filters?.limit ?? DEFAULT_DETAILS_PAGE_SIZE
        const offset = filters?.offset ?? 0

        const { data, error } = await client
          .from('projections')
          .select('id, date, description, amount, notes, categories!projections_category_id_fkey(name), facilities!projections_facility_id_fkey(name)')
          .eq('file_import_id', fileImportId)
          .order('date', { ascending: true })
          .range(offset, offset + limit - 1)

        if (error) {
          throw new Error(`Failed to fetch file import projections: ${error.message}`)
        }

        return (data ?? []) as unknown as FileImportProjectionRow[]
      },

      /**
       * Count projections belonging to a specific file import.
       * @param fileImportId - UUID of the file_imports record
       * @returns Total count of projections for this import
       */
      async countProjections(fileImportId: string): Promise<number> {
        const { count, error } = await client
          .from('projections')
          .select('id', { count: 'exact', head: true })
          .eq('file_import_id', fileImportId)

        if (error) {
          // Throw to match listProjections (unified error handling). See
          // countTransactions for the rationale.
          throw new Error(`Failed to count file import projections: ${error.message}`)
        }

        return count ?? 0
      },
    },

    projections: {
      /**
       * List projections with related category and facility data, with optional filters.
       * Joins to tenant categories and facilities tables.
       * @param filters - Optional filters for date range, facility, category, type, and pagination
       */
      async list(filters?: ProjectionFilters): Promise<DbResult<{ items: ProjectionWithRelations[]; total: number }>> {
        // Facility narrowing, search RPC and the DCP-292 active-facility default
        // are resolved once and applied to BOTH queries below, so the page and
        // its count — and `findPage` — always describe the same rows.
        const { data: scope, error: scopeError } = await resolveProjectionScope(filters)
        if (scopeError) return { data: null, error: scopeError }
        if (scope.empty) return { data: { items: [], total: 0 }, error: null }

        // Build the data query with full relation joins for the result set.
        // category join uses left (not !inner) — categories.type is nullable in DCP-250
        // and the type filter is retired, so !inner would wrongly exclude uncategorised rows.
        // Sort: see orderProjectionsAsTable (DCP-406).
        let query = scope.apply(
          orderProjectionsAsTable(
            client
              .from('projections')
              .select(`
                *,
                category:categories(id, name, type),
                facility:facilities(id, name, code)
              `),
          ),
        )

        // Count query — no category join needed; type filter is retired (DCP-250 Phase 1)
        const countQuery = scope.apply(
          client.from('projections').select('*', { count: 'exact', head: true }),
        )

        const { count, error: countError } = await countQuery
        if (countError) return { data: null, error: { message: countError.message, code: countError.code } }
        const total = count || 0

        // Apply pagination to the data query only (not the count query)
        const page = filters?.page || 1
        const pageSize = filters?.pageSize || 5
        const from = (page - 1) * pageSize
        query = query.range(from, from + pageSize - 1)

        const { data, error } = await query
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: { items: (data || []) as unknown as ProjectionWithRelations[], total }, error: null }
      },

      /**
       * DCP-406: on which page of the Projections table — under `filters` and
       * `pageSize` — the FIRST of `ids` (in table order) is shown. Used after a
       * create to open the page that holds the new rows: the table sorts by
       * Projected Payment Date, so a new row lands where its date puts it,
       * often not on page 1.
       *
       * Applies exactly the row scope of {@link list} (via
       * resolveProjectionScope) and its order (via orderProjectionsAsTable);
       * any drift between the two would open a page without the row.
       *
       * PostgREST cannot express "sorts before row X" lexicographically across
       * the embedded category/facility names in one filter, so the position is
       * assembled from parts:
       *   1. the earliest date D among the ids that are in scope (looked up
       *      in chunks of PROJECTION_ID_CHUNK ids, which travel in the URL);
       *   2. `before` — how many in-scope rows have a date earlier than D (a
       *      head count);
       *   3. `index` — where the first of the ids sits among the rows OF day D,
       *      read in table order. Read in chunks advanced by the number of rows
       *      actually returned: the API caps a response at `max_rows` (1000,
       *      supabase/config.toml), and one day can hold a whole file import.
       *
       * @param ids - ids of the rows to locate (e.g. just created)
       * @param filters - the table's current filters, search included;
       *   `page`/`pageSize` inside are ignored
       * @param pageSize - rows per table page, a positive integer
       * @returns the 1-based page number, or `null` when none of `ids` is in
       *   the scope (filtered out, or deleted meanwhile); an error DbResult when
       *   a query failed or `pageSize` is invalid
       */
      async findPage(
        ids: string[],
        filters: ProjectionFilters | undefined,
        pageSize: number,
      ): Promise<DbResult<number | null>> {
        if (!Number.isInteger(pageSize) || pageSize < 1) {
          return {
            data: null,
            error: { message: `Invalid page size: ${pageSize}`, code: 'INVALID_PAGE_SIZE' },
          }
        }
        if (ids.length === 0) return { data: null, error: null }

        const { data: scope, error: scopeError } = await resolveProjectionScope(filters)
        if (scopeError) return { data: null, error: scopeError }
        if (scope.empty) return { data: null, error: null }

        // 1. Earliest date among the ids that the table can show. A second
        //    `id=in` next to the search scope's one is ANDed by PostgREST.
        //    The ids travel in the URL, so a large batch is split into chunks
        //    (see PROJECTION_ID_CHUNK) and the minimum is taken across them.
        //    Sequential on purpose: a batch create is a handful of chunks at
        //    most, and one request at a time keeps the first error the one
        //    reported, like every other step here.
        let firstDate: string | null = null
        for (let start = 0; start < ids.length; start += PROJECTION_ID_CHUNK) {
          const { data: firstRows, error: firstError } = await scope
            .apply(client.from('projections').select('id, date'))
            .in('id', ids.slice(start, start + PROJECTION_ID_CHUNK))
            .order('date', { ascending: true })
            .limit(1)
          if (firstError) return { data: null, error: { message: firstError.message, code: firstError.code } }
          const chunkDate = (firstRows?.[0] as { date: string } | undefined)?.date
          // `date` is a Postgres DATE, served as YYYY-MM-DD: string order is
          // calendar order.
          if (chunkDate && (firstDate === null || chunkDate < firstDate)) firstDate = chunkDate
        }
        if (!firstDate) return { data: null, error: null }

        // 2. Rows of earlier days precede every row of day D.
        const { count: before, error: beforeError } = await scope
          .apply(client.from('projections').select('id', { count: 'exact', head: true }))
          .lt('date', firstDate)
        if (beforeError) return { data: null, error: { message: beforeError.message, code: beforeError.code } }

        // 3. Walk day D in table order until the first of the ids shows up.
        const wanted = new Set(ids)
        let offset = 0
        for (;;) {
          const { data: dayRows, error: dayError } = await orderProjectionsAsTable(
            scope.apply(
              client
                .from('projections')
                .select('id, category:categories(name), facility:facilities(name)'),
            ),
          )
            .eq('date', firstDate)
            .range(offset, offset + PROJECTION_DAY_CHUNK - 1)
          if (dayError) return { data: null, error: { message: dayError.message, code: dayError.code } }
          const rows = (dayRows ?? []) as Array<{ id: string }>
          // Exhausted without a match: the row left the scope between step 1
          // and now (deleted or edited concurrently).
          if (rows.length === 0) return { data: null, error: null }
          const index = rows.findIndex((row) => wanted.has(row.id))
          if (index >= 0) {
            const position = (before ?? 0) + offset + index
            return { data: Math.floor(position / pageSize) + 1, error: null }
          }
          offset += rows.length
        }
      },

      /**
       * Create a new projection
       * @param input - Projection data (no organization_id needed — schema provides isolation)
       */
      async create(input: ProjectionInsert): Promise<DbResult<ProjectionRow>> {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await client.from('projections').insert(input as any).select().single()
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data, error: null }
      },

      /**
       * Create multiple projections in a single database call
       * @param inputs - Array of projection data
       */
      async createMany(inputs: ProjectionInsert[]): Promise<DbResult<ProjectionRow[]>> {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await client.from('projections').insert(inputs as any).select()
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: data || [], error: null }
      },

      /** Update an existing projection by ID */
      async update(id: string, updateData: Partial<ProjectionRow>): Promise<DbResult<ProjectionRow>> {
        // Skip undefined values to preserve existing data; explicit null clears the field
        const dbUpdateData: Record<string, unknown> = {}
        Object.keys(updateData).forEach(key => {
          const value = (updateData as Record<string, unknown>)[key]
          if (value !== undefined) {
            dbUpdateData[key] = value
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await (client.from('projections') as any)
          .update(dbUpdateData)
          .eq('id', id)
          .select()
          .single()
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data, error: null }
      },

      /** Delete a projection by ID */
      async delete(id: string): Promise<DbResult<null>> {
        const { error } = await client.from('projections').delete().eq('id', id)
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: null, error: null }
      },
    },

    bankBalance: {
      /**
       * Get the total Bank Balance aggregated across all accounts as of the
       * given endDate (or today).
       *
       * DCP-218: the balance now comes from the integration-agnostic tenant
       * `balances` table (one snapshot per account/date), not the dead
       * `plaid_balances_history`. We take the latest snapshot per account on or
       * before endDate (via `latestBalancesPerAccount`) and sum their
       * `bank_balance`. Accounts can therefore be reported on DIFFERENT dates;
       * `asOfDate` is the OLDEST included snapshot date so the most-stale
       * account drives the future staleness flag (DCP-218 AC.3).
       *
       * @param filters.endDate   - Upper bound date in YYYY-MM-DD format (defaults to the current business day)
       * @param filters.facilityIds - Optional list of facility IDs to restrict the aggregation
       * @param filters.accountIds  - Optional list of bank_account IDs to restrict the aggregation
       * @returns `{ total, asOfDate }` — aggregated balance and the oldest included
       *          snapshot date, or `{ total: 0, asOfDate: null }` when no balance
       *          records exist
       */
      async getTotal(
        filters?: { endDate?: string; facilityIds?: string[]; accountIds?: string[] }
      ): Promise<DbResult<{ total: number; asOfDate: string | null }>> {
        // Use the provided endDate, or fall back to the current BUSINESS day.
        // Must be the same primitive the write path stamps rows with, or this
        // read would exclude a balance entered earlier the same evening.
        const effectiveEndDate = filters?.endDate ?? businessDate()

        // Latest snapshot per account on or before endDate (inclusive).
        const { data: latest, error } = await latestBalancesPerAccount(effectiveEndDate, {
          facilityIds: filters?.facilityIds,
          accountIds: filters?.accountIds,
        })
        if (error) {
          return { data: null, error }
        }

        // No balance records for the given constraints.
        if (latest.size === 0) {
          return { data: { total: 0, asOfDate: null }, error: null }
        }

        // Sum balances; asOfDate = oldest (min) snapshot date among included
        // accounts so the staleness signal reflects the worst case.
        let total = 0
        let asOfDate: string | null = null
        for (const { balance, date } of latest.values()) {
          total += balance
          if (asOfDate === null || date < asOfDate) {
            asOfDate = date
          }
        }

        return { data: { total, asOfDate }, error: null }
      },

      /**
       * DCP-218 AC.3: report current-day balance coverage so the Bank Balance
       * tile can warn when some accounts are missing a balance for the target
       * date (e.g. a sync failed or no manual input was entered for an account).
       *
       * `expected` = active, non-deleted bank accounts in scope.
       * `covered`  = how many of them have a `balances` row dated exactly on the
       *              target date (`endDate`, defaulting to today).
       * The caller warns when `expected > 0 && covered < expected`.
       *
       * @param filters.endDate     Target date (YYYY-MM-DD); defaults to the
       *        current business day (the same day the write path stamps).
       * @param filters.facilityIds Optional facility scope (skipped when empty).
       * @param filters.accountIds  Optional bank-account scope. Empty array →
       *        no accounts in scope (short-circuit), matching getTotal.
       * @returns `{ expected, covered, targetDate }`.
       */
      async getCoverage(
        filters?: { endDate?: string; facilityIds?: string[]; accountIds?: string[] }
      ): Promise<DbResult<{ expected: number; covered: number; targetDate: string }>> {
        // Default target = the current BUSINESS day. This is an `eq` match on
        // `balances.date` below, so it must resolve to exactly the value
        // `upsertManual` stamps; a UTC "today" would report every account as
        // uncovered for the last few hours of each Central-time day.
        const targetDate = filters?.endDate ?? businessDate()

        // Denominator: active, non-deleted bank accounts in scope (single source
        // of truth shared with getTotal).
        //
        // KNOWN RESIDUAL, deliberately left to its own ticket: `scopedActiveAccountIds`
        // does NOT page, so an org holding more than 1000 in-scope accounts loses
        // the tail of this denominator to PostgREST's silent cap (and, since the
        // numerator is scoped to these same ids, of the numerator with it) — even
        // though the numerator below now pages. Paging it here is not a local fix:
        // the very same ids are handed to un-chunked `.in()` filters in getTotal
        // and latestBalancesPerAccount, so lifting the cap would
        // swap today's silent truncation for a hard URL-length failure on those
        // reads. The same unpaged cap also applies to the balances read inside
        // latestBalancesPerAccount and to sageBankAccounts.sumIntacctBalance /
        // sageBankAccounts.getIntacctFreshness — one ticket, not five patches.
        const { data: scopedIds, error: scopeError } = await scopedActiveAccountIds({
          facilityIds: filters?.facilityIds,
          accountIds: filters?.accountIds,
        })
        if (scopeError) {
          return { data: null, error: scopeError }
        }
        const expected = scopedIds.length
        if (expected === 0) {
          return { data: { expected: 0, covered: 0, targetDate }, error: null }
        }

        // Numerator (Presto #3): distinct accounts with a balance row ON the
        // target date, restricted to the SAME scoped account set as the
        // denominator. Without the `.in(scopedIds)` a soft-deleted/inactive
        // account's row could inflate `covered` and mask a missing active one.
        //
        // `eq`, NOT `gte`: this call answers "is there a balance FOR the selected
        // day?", which is exactly what its date filter means. `endDate` arrives
        // from the user's URL date filter (`useBankingFilterParams`), so it is an
        // arbitrary — routinely historical — day, not the current business day.
        // Under `gte` any past end date would read as covered for every account
        // carrying any LATER balance, so filtering to e.g. January would show a
        // stale historical figure with the missing-data warning silently switched
        // off: precisely the failure DCP-218 / DCP-161 exist to remove.
        // `getCoverageByFacility` reads `gte` instead because its target is always
        // the CURRENT business day, where the question is the different one of
        // "updated no earlier than the last day a balance was expected".
        //
        // Read in BATCHES and exhaustively for the reasons given on `chunkIds` /
        // `fetchAllRows`. Under `eq` a batch can match at most one row per account
        // — (bank_account_id, date) is UNIQUE — so the walk ends on its second,
        // empty page; that confirming request is the price of a termination rule
        // that stays correct if the server's page cap ever drops below ours.
        //
        // The batches are independent reads into one Set, so they are issued
        // CONCURRENTLY: serially, a 300-account org paid 3 round trips (times
        // their pages) end to end where 1 will do.
        const batchResults = await Promise.all(
          chunkIds(scopedIds, IN_FILTER_CHUNK_SIZE).map((batch) =>
            fetchAllRows<{ bank_account_id: string }>((from, to) =>
              client
                .from('balances')
                .select('bank_account_id')
                .eq('date', targetDate)
                .in('bank_account_id', batch)
                // `id` is the table's primary key — the unique tiebreaker that
                // makes the paging order total (see `fetchAllRows`).
                .order('id')
                .range(from, to),
            ),
          ),
        )
        // First error wins, exactly as the serial loop's early return did:
        // failures come back in-band (no rejection), so scan the results in
        // chunk order and surface the earliest one rather than swallowing any.
        const failedBatch = batchResults.find((result) => result.error !== null)
        if (failedBatch?.error) {
          return { data: null, error: failedBatch.error }
        }

        // Distinct accounts only — the pair uniqueness above makes duplicates
        // impossible, but the Set also keeps the merge across batches trivial.
        const coveredIds = new Set<string>()
        for (const result of batchResults) {
          for (const row of result.data ?? []) {
            coveredIds.add(row.bank_account_id)
          }
        }

        return { data: { expected, covered: coveredIds.size, targetDate }, error: null }
      },

      /**
       * DCP-161: per-facility variant of getCoverage, powering the "missing
       * data" warning icons on the Cash Position Summary report. Unlike
       * getCoverage (a single org-wide ratio for the Banking tile), this
       * returns one `{ expected, covered }` pair per facility so each cell of
       * the report can be flagged independently.
       *
       * `expected` mirrors the `latest_balance` CTE's WHERE clause in the Cash
       * Position engine RPC (`bank` CTE, migration
       * 20260730000000_dcp308_facility_id_single_source.sql): active,
       * non-soft-deleted accounts with a non-null `facility_id` — accounts
       * with `facility_id === null` are DROPPED entirely (not bucketed under a
       * fake key) so the denominator here matches the number the RPC renders
       * per facility. This is stricter than getCoverage, which has no facility
       * concept and therefore never filters on `facility_id`.
       *
       * @param filters.date - Target date (YYYY-MM-DD) to check coverage for;
       *   defaults to the current business day. Callers that care about the
       *   weekend pass the last business day explicitly.
       * @returns `{ targetDate, byFacility }` where `byFacility` is keyed by
       *   `facility_id` and each entry is `{ expected, covered }`.
       */
      async getCoverageByFacility(
        filters?: { date?: string },
      ): Promise<DbResult<{
        targetDate: string
        byFacility: Record<string, { expected: number; covered: number }>
      }>> {
        // Default target = the current BUSINESS day (same primitive as the
        // write path — see upsertManual).
        const targetDate = filters?.date ?? businessDate()

        // Denominator: active, non-deleted, facility-attributed bank accounts.
        //
        // There is deliberately NO facility-is_active filter here, and the omission
        // mirrors the engine RPC rather than contradicting DCP-292. The RPC splits
        // the same rule across two layers: `latest_balance` selects by ACCOUNT state
        // only — `ba.deleted_at IS NULL AND ba.facility_id IS NOT NULL`
        // (`get_cash_position_summary`, latest_balance CTE) — and applies
        // `WHERE f.is_active` one level up, on the per-facility rows it
        // emits as columns. This read mirrors the CTE; the column-level half is
        // mirrored by the caller, which only ever looks up `byFacility[column.key]`
        // for columns the RPC has already filtered. DCP-292 left
        // `get_cash_position_summary` untouched for exactly that reason and cites its
        // split as the correct pattern.
        //
        // DCP-324: this description previously included a third account-level
        // conjunct, `ba.status = 'active'`. Both halves — the RPC's and the
        // matching `.eq('status', 'active')` that used to be on the query below —
        // are gone, because 20260910050000 drops the column. Nothing is filtered
        // differently: the column was NOT NULL DEFAULT 'active' with no writer of
        // any other value, so the predicate always held. The comment is corrected
        // rather than deleted because the denominator's exact population is the
        // thing that has to keep matching the RPC, and a stale quote of the RPC
        // is how the two silently drift.
        //
        // DCP-324 (AC#19) then ADDED three account-level conjuncts to that same
        // `latest_balance` CTE — `sync_disabled_at IS NULL`,
        // `provider_removed_at IS NULL`, `include_in_cash_position` — so the
        // RPC's rule now reads, in full:
        //   ba.deleted_at IS NULL AND ba.facility_id IS NOT NULL
        //   AND ba.sync_disabled_at IS NULL AND ba.provider_removed_at IS NULL
        //   AND ba.include_in_cash_position
        //   AND (DCP-338 still-linked) AND (DCP-371 not a credit card)
        // and this read mirrors all of it — the first five conjuncts via the
        // shared {@link applyCashPositionScope}, the last two via their own
        // helpers. The `WHERE f.is_active` half stays one level up, exactly as
        // described above; nothing about that split changed.
        //
        // Why the tile-side helpers differ: `scopedActiveAccountIds` and everything
        // built on it DO scope facilities, because they collapse to one flat SUM where
        // a single out-of-scope account corrupts the figure. This function returns a
        // map KEYED BY FACILITY and every account increments only its own facility's
        // bucket, so a deactivated facility can contribute a key nobody asks for but
        // can never change an active facility's counts. `getIntacctFreshnessByFacility`
        // is unscoped for the same reason — the two halves of this feature have to
        // describe the same population, and its location→facility resolution
        // deliberately reproduces the RPC's `is_active DESC, id` tie-break (:257)
        // instead of dropping inactive facilities outright.
        //
        // The one rule a future caller must respect: do NOT reduce this map wholesale
        // (`Object.values(byFacility)`), which would count deactivated facilities back
        // in. Aggregate over the columns on screen, the way `buildCashPositionWarnings`
        // does for pack subtotals and the Combined Total.
        //
        // Read exhaustively (`fetchAllRows`) because this is a POPULATION: a
        // silent PostgREST truncation at 1000 would drop whole facilities out of
        // the map and under-report `expected` for the rest — and, since the
        // numerator below is scoped to the ids read here, the two halves would
        // stay superficially consistent while the warning quietly stopped firing.
        const { data: accountRows, error: accountError } = await fetchAllRows<{
          id: string
          facility_id: string | null
        }>((from, to) =>
          // DCP-371: credit cards are excluded here TOO, even though this read
          // deliberately does not share `scopedActiveAccountIds`. Coverage is
          // "how many accounts that count towards Bank Balance have a fresh
          // balance": a card no longer counts, so leaving it in the denominator
          // would raise a permanent "missing balance" warning for a figure the
          // tiles no longer use — and no user action could ever clear it.
          // DCP-324 AC#19: and the same connected / assigned / opted-in rule as
          // the tiles, for the SAME reason spelled out just above. This is the
          // "missing balance" warning's DENOMINATOR: an account the tiles no
          // longer sum must leave it too, or the user gets a permanent warning
          // about a figure nothing uses and NO action can clear it — there is
          // no screen on which to enter a balance for an excluded account,
          // because `listImportStatus` applies this predicate as well.
          //
          // `facility_id IS NOT NULL` is redundant here in effect — the loop
          // below already drops NULL-facility rows, since a facility-keyed map
          // has no bucket for them — but it arrives as part of the shared
          // predicate and is left in rather than hand-trimmed: this read has to
          // keep describing the same population as `latest_balance`, and a
          // locally "optimised" copy of the rule is how the two drift.
          applyCashPositionScope(
            applyBankBalanceAccountTypeScope(
              client
                .from('bank_accounts')
                .select('id, facility_id')
                .is('deleted_at', null)
                // `id` is the table's primary key — the unique tiebreaker that
                // makes the paging order total (see `fetchAllRows`).
                .order('id'),
            ),
          ).range(from, to),
        )
        if (accountError) {
          return { data: null, error: accountError }
        }

        // Drop accounts with no facility_id (they can't be attributed to a
        // report row) and index by account id → facility id for the covered pass.
        const facilityByAccountId = new Map<string, string>()
        const byFacility: Record<string, { expected: number; covered: number }> = {}
        for (const row of accountRows) {
          if (row.facility_id === null) continue
          facilityByAccountId.set(row.id, row.facility_id)
          const bucket = byFacility[row.facility_id] ?? { expected: 0, covered: 0 }
          bucket.expected += 1
          byFacility[row.facility_id] = bucket
        }

        // Short-circuit when there are no in-scope accounts: modern PostgREST
        // renders `.in([])` as `= ANY('{}')`, which matches nothing, so the
        // request could only come back empty — skipping it saves a round trip
        // (same convention as scopedActiveAccountIds / getCoverage).
        const scopedIds = [...facilityByAccountId.keys()]
        if (scopedIds.length === 0) {
          return { data: { targetDate, byFacility: {} }, error: null }
        }

        // Numerator: distinct accounts carrying a balance dated ON OR AFTER the
        // target day, restricted to the SAME scoped account set as the
        // denominator.
        //
        // `gte`, NOT `eq`, and that distinction is still load-bearing — though
        // now for the RIGHT reason.
        //
        // It used to compensate for a bug: the write path stamped today-UTC, so
        // a Friday-evening entry landed on Saturday-UTC and an `eq` would have
        // reported it missing. That skew is gone — `upsertManual` and the reads
        // here now share `businessDate()`.
        //
        // The genuine reason survives it. Callers pass the LAST BUSINESS DAY as
        // the target (see `lastBusinessDay`), because banks post no balances at
        // the weekend, so on a Saturday the target is the preceding Friday. A
        // balance legitimately entered on that Saturday carries business-date
        // Saturday, which is AFTER the Friday target: under `eq` it would read
        // as missing, the user could not clear the warning by re-entering it,
        // and the icon would contradict the figure beside it, since the report's
        // own `latest_balance` CTE takes the newest row with no date filter.
        // `gte` reads as "this account has been updated no earlier than the last
        // day a balance was expected", which is the actual question. No writer
        // can post a future date, so it cannot be gamed.
        //
        // The `.in(scopedIds)` is LOAD-BEARING too — the same fix already applied
        // in getCoverage — so a soft-deleted account's lingering balance row can
        // never mask a missing active one.
        //
        // It is issued in BATCHES because `scopedIds` is now read exhaustively
        // and therefore grows with the table: a single `in.(...)` filter carrying
        // every account id travels in the request URL and would eventually exceed
        // the server's URL limit (see `chunkIds`). Each batch is itself paged,
        // since one account can hold several rows in the window — on a Sunday the
        // target is the preceding Friday, so Fri/Sat/Sun all qualify — meaning
        // the 1000-row cap is reached at roughly a THIRD of the account count,
        // not at 1000 accounts.
        //
        // The batches are independent reads into one Set, so they are issued
        // CONCURRENTLY: serially, an org with 300 facility-attributed accounts
        // paid 3 chunks' worth of round trips end to end where 1 will do.
        const batchResults = await Promise.all(
          chunkIds(scopedIds, IN_FILTER_CHUNK_SIZE).map((batch) =>
            fetchAllRows<{ bank_account_id: string }>((from, to) =>
              client
                .from('balances')
                .select('bank_account_id')
                .gte('date', targetDate)
                .in('bank_account_id', batch)
                // `id` is the table's primary key — the unique tiebreaker that
                // makes the paging order total (see `fetchAllRows`).
                .order('id')
                .range(from, to),
            ),
          ),
        )
        // First error wins, exactly as the serial loop's early return did:
        // failures come back in-band (no rejection), so scan the results in
        // chunk order and surface the earliest one rather than swallowing any.
        const failedBatch = batchResults.find((result) => result.error !== null)
        if (failedBatch?.error) {
          return { data: null, error: failedBatch.error }
        }

        // Distinct accounts only: `gte` matches several days per account by
        // design, and each covered account must be counted once.
        const coveredAccountIds = new Set<string>()
        for (const result of batchResults) {
          for (const row of result.data ?? []) {
            coveredAccountIds.add(row.bank_account_id)
          }
        }

        // Attribute each covered account to its facility bucket.
        for (const accountId of coveredAccountIds) {
          const facilityId = facilityByAccountId.get(accountId)
          if (facilityId === undefined) continue
          byFacility[facilityId].covered += 1
        }

        return { data: { targetDate, byFacility }, error: null }
      },
    },

    balances: {
      /**
       * Page through the "Bank Balance Import Status" rows for
       * /banking/balance-import. One row per bank account, carrying the
       * latest known balance (from the tenant `balances` snapshot table),
       * the facility name, and an `isApiAccount` flag derived from
       * `integration_id` (so the UI can hide the inline-edit pencil for
       * Plaid/Sage-managed accounts).
       *
       * DCP-338: ORPHANED accounts (integration-backed, connection already gone)
       * are not listed — see {@link applyLinkedAccountScope}. An account whose
       * connection merely BROKE is still listed, and carries `connectionStatus`
       * so the table can mark it "connection lost" instead of dropping it
       * silently. `isApiAccount` still comes from `integration_id`, which
       * survives a disconnect untouched.
       *
       * DCP-371: CREDIT-CARD accounts are not listed — the table exists to keep
       * the numbers behind Bank Balance current, and a card contributes to none
       * of them (see {@link applyBankBalanceAccountTypeScope}). They remain
       * listed and editable in Banking Settings, which is where accounts are
       * managed.
       *
       * DCP-324 (AC#19): for the same reason, neither are accounts that are
       * DISCONNECTED, UNASSIGNED or not OPTED IN ({@link applyCashPositionScope}).
       * Two consequences are deliberate and worth knowing before "fixing" them:
       *   - an UNASSIGNED account now leaves this table, where DCP-292 kept it.
       *     Assigning it a facility in Banking Settings brings it straight back;
       *   - a new manual account is created OUT of Cash Position and appears
       *     here once someone turns on its "Include in Cash Position" toggle
       *     on its manual bank's page in Settings (`bankAccounts.setIncludeInCashPosition`);
       *     20260910070000 section 2 opted in the manual accounts that predate
       *     the column.
       *
       * Server-side pagination: a HEAD count gives `total`, then bank_accounts
       * are sliced via `.range()` and only the page's accounts trigger the
       * balances fetch. Result: O(pageSize) accounts and ≤ pageSize × history
       * rows per call, regardless of how big the tenant grows.
       *
       * Why this shape and not a SQL view / RPC: PostgREST has no DISTINCT ON,
       * but the latest-per-account merge is trivial in JS over a single
       * page worth of rows. Keeping the helper plain SQL-via-PostgREST avoids
       * a migration just for this surface.
       */
      async listImportStatus(opts?: {
        page?: number
        pageSize?: number
        facilityIds?: string[]
      }): Promise<DbResult<{ items: BalanceImportStatus[]; total: number }>> {
        // Defaults match the page-level state in BalanceImportPage. The
        // pagination control hides itself when total ≤ 10, so first-render
        // with no `opts` still gives the correct page.
        const page = opts?.page ?? 1
        const pageSize = opts?.pageSize ?? 10
        const from = (page - 1) * pageSize
        const to = from + pageSize - 1

        // Empty `facilityIds` is treated as "no rows match this filter":
        // mirroring the same convention used in sageChecks.list. PostgREST's
        // .in([]) is skipped client-side and silently degrades to "no
        // filter", which would leak unrelated facilities through the
        // selector — short-circuit before issuing any query.
        if (opts?.facilityIds && opts.facilityIds.length === 0) {
          return { data: { items: [], total: 0 }, error: null }
        }

        // 1. HEAD count — total number of accounts (within the facility
        // filter, when one is active). No rows shipped over the wire.
        // Drives both the pagination UI and the visibility threshold on
        // the consumer side. Soft-deleted accounts (DCP-222) are excluded
        // so the count + page stay in lockstep with the Settings UI.
        let countQuery = client
          .from('bank_accounts')
          .select('id', { count: 'exact', head: true })
          .is('deleted_at', null)
        {
          // DCP-292: an empty facilityIds already short-circuited above; a MISSING
          // filter now defaults to ACTIVE facilities (unassigned/NULL-facility
          // accounts kept) so a deactivated facility's accounts leave the table.
          const scope = await applyActiveFacilityScope(countQuery, opts?.facilityIds)
          if (scope.error) return { data: null, error: scope.error }
          countQuery = scope.query
        }
        // DCP-338: orphaned accounts (their bank was disconnected) do not belong
        // in the Bank Balance Update table — nothing can refresh them and their
        // "Resync" action is dead. Applied to the count as well as the page
        // below: if the two predicates ever diverge, `total` counts rows the page
        // cannot return and the pagination drifts — an empty last page and a page
        // count that never matches the rows on screen.
        countQuery = applyLinkedAccountScope(countQuery)
        // DCP-371: credit cards do not belong in the Bank Balance Update table
        // either — their balance never reaches a Bank Balance figure, so asking
        // the operator to keep it current is busywork that also implies the
        // money is counted. Applied to BOTH the count and the page below for the
        // same lockstep reason as the predicate above.
        countQuery = applyBankBalanceAccountTypeScope(countQuery)
        // DCP-324 AC#19: and the connected / assigned / opted-in rule. This
        // table exists to keep the numbers BEHIND Bank Balance current, so it
        // has to list exactly the accounts that produce them — the same
        // argument DCP-371 made for cards, now for the three DCP-324 states
        // plus the opt-in. Asking an operator to maintain a balance that
        // reaches no figure is busywork that also implies the money is counted.
        // Applied to BOTH halves for the lockstep reason above.
        countQuery = applyCashPositionScope(countQuery)
        const { count, error: countError } = await countQuery

        if (countError) {
          return { data: null, error: { message: countError.message, code: countError.code } }
        }
        const total = count ?? 0

        if (total === 0) {
          return { data: { items: [], total: 0 }, error: null }
        }

        // 2. Page of bank accounts with embedded facility (single LEFT JOIN
        // via PostgREST). Same facility + soft-delete filters as the count
        // query so the two stay in lockstep. Ordering (DCP-267): the top-level
        // `bank_accounts` rows are sorted by the to-one embedded facility name
        // ASC, with account `name` ASC as a deterministic tiebreaker so pages
        // don't shuffle between fetches. Accounts with no facility sort last
        // (nullsFirst: false).
        //
        // The embed path MUST be passed as the order column itself
        // (`facilities(name)` → PostgREST `order=facilities(name).asc.nullslast`)
        // — NOT via the `referencedTable` option. `referencedTable` emits a
        // separate `facilities.order=` param that reorders rows WITHIN the
        // embedded resource, which is a no-op for a to-one embed and leaves the
        // parent rows sorted by account name only (DCP-267 regression, verified
        // against local PostgREST). Covered by db-tenant.listImportStatus.order.test.ts.
        let pageQuery = client
          .from('bank_accounts')
          .select(
            'id, name, institution_name, account_number, integration_id, facility_id, last_balance_sync_status, last_balance_sync_at, last_balance_sync_error, facilities!bank_accounts_facility_id_fkey(name), ledgersync_connections!bank_accounts_ledgersync_connection_id_fkey(status)',
          )
          .is('deleted_at', null)
          .order('facilities(name)', { ascending: true, nullsFirst: false })
          .order('name', { ascending: true })
          .range(from, to)
        {
          // DCP-292: same active-facility default as the count query above so the
          // page slice and the total stay in lockstep.
          const scope = await applyActiveFacilityScope(pageQuery, opts?.facilityIds)
          if (scope.error) return { data: null, error: scope.error }
          pageQuery = scope.query
        }
        // DCP-338: identical linked-account predicate as the count query, so the
        // page slice and `total` stay in lockstep (see the note there).
        pageQuery = applyLinkedAccountScope(pageQuery)
        // DCP-371: identical credit-card predicate as the count query, so the
        // page slice and `total` stay in lockstep (see the note there).
        pageQuery = applyBankBalanceAccountTypeScope(pageQuery)
        // DCP-324: identical connected / assigned / opted-in predicate as the
        // count query, so the page slice and `total` stay in lockstep (see the
        // note there).
        pageQuery = applyCashPositionScope(pageQuery)
        const { data: accountsData, error: accountsError } = await pageQuery

        if (accountsError) {
          return { data: null, error: { message: accountsError.message, code: accountsError.code } }
        }

        const accounts = (accountsData ?? []) as Array<{
          id: string
          name: string
          institution_name: string | null
          account_number: string | null
          integration_id: string | null
          facility_id: string | null
          // Per-account balance-sync health (DCP-217). Populated by the
          // LedgerSync balance jobs on the tenant `bank_accounts` row; null on
          // accounts that have never had a balance sync. Drives the API Update
          // Status column + the Actions "Sync"/"Resync" affordance.
          last_balance_sync_status: 'syncing' | 'success' | 'failed' | null
          last_balance_sync_at: string | null
          last_balance_sync_error: string | null
          // PostgREST embeds a single-result join as an object (not array) when
          // the FK is many-to-one; declared as a union so the narrowing below
          // covers both shapes if PostgREST flips to array form for a future
          // schema change.
          facilities: { name: string } | { name: string }[] | null
          // DCP-338: the owning connection's live status, embedded through the
          // same many-to-one shape as `facilities`. NULL for manual accounts
          // (no link at all). Drives the "connection lost" marker in the table —
          // a broken bank stays listed and keeps counting, so this is the ONLY
          // thing that tells the user its balance stopped being refreshed.
          ledgersync_connections: { status: string } | { status: string }[] | null
        }>

        if (accounts.length === 0) {
          // Page beyond the last one — return empty items, but report total
          // so the UI can clamp the active page.
          return { data: { items: [], total }, error: null }
        }

        // 3. Newest balance snapshot per bank account, scoped to the page's
        // accounts only. Fetch ordered, then first-wins-per-account in JS.
        // `updated_at` is the tiebreaker for the (admittedly rare) case of
        // two snapshots on the same calendar day — the inline-edit flow
        // upserts on conflict so this stays small.
        const accountIds = accounts.map((a) => a.id)
        const { data: balancesData, error: balancesError } = await client
          .from('balances')
          .select('bank_account_id, bank_balance, date, source, updated_at')
          .in('bank_account_id', accountIds)
          .order('date', { ascending: false })
          .order('updated_at', { ascending: false })

        if (balancesError) {
          return { data: null, error: { message: balancesError.message, code: balancesError.code } }
        }

        type BalanceSnapshot = {
          bank_balance: number
          date: string
          source: string | null
          updated_at: string
        }
        const latestByAccount = new Map<string, BalanceSnapshot>()
        for (const row of (balancesData ?? []) as Array<{
          bank_account_id: string
          bank_balance: number
          date: string
          source: string | null
          updated_at: string
        }>) {
          if (!latestByAccount.has(row.bank_account_id)) {
            // bank_balance is a DECIMAL column — PostgREST surfaces it as a
            // number for finite values but as a string when the driver can't
            // safely fit it into a JS number; coerce defensively. The
            // isFinite guard catches the small set of pathological inputs
            // (null / undefined / non-numeric strings from schema drift or
            // a corrupt mock) so the value doesn't propagate as NaN through
            // formatCurrency / sorting / comparison — that would surface as
            // "$NaN" in the cell and silently break inline edit init.
            const rawBalance = Number(row.bank_balance)
            const safeBalance = Number.isFinite(rawBalance) ? rawBalance : 0
            if (!Number.isFinite(rawBalance) && import.meta.env.DEV) {
              console.warn(
                `[balances.listImportStatus] Non-finite bank_balance for account ${row.bank_account_id}: ${row.bank_balance} — falling back to 0`,
              )
            }
            latestByAccount.set(row.bank_account_id, {
              bank_balance: safeBalance,
              date: row.date,
              source: row.source,
              updated_at: row.updated_at,
            })
          }
        }

        // 4. Compose result rows. The order is inherited from step 2 —
        // facility name ASC (nulls last), with account `name` ASC as the
        // tiebreaker — which is the user-visible sort the DataTable renders.
        const items: BalanceImportStatus[] = accounts.map((acc) => {
          const latest = latestByAccount.get(acc.id)
          const isApi = acc.integration_id !== null

          // PostgREST many-to-one join can surface as object OR array depending
          // on the cardinality the planner picks; handle both.
          const facilityJoin = acc.facilities
          const facilityName = Array.isArray(facilityJoin)
            ? facilityJoin[0]?.name ?? '-'
            : facilityJoin?.name ?? '-'

          // Map DB `source` column to the UI `lastUpdateSource` label.
          //   'manual'      → 'Manual' (entered via inline edit)
          //   'plaid'/'api' → 'API'    (synced from upstream)
          //   null (legacy) → infer from `integration_id` so historical rows
          //                   don't all flip to 'Manual'.
          let lastUpdateSource: UpdateSource = 'Manual'
          if (latest?.source === 'plaid' || latest?.source === 'api') {
            lastUpdateSource = 'API'
          } else if (latest?.source === 'manual') {
            lastUpdateSource = 'Manual'
          } else if (isApi) {
            lastUpdateSource = 'API'
          }

          // API Update Status reflects the LedgerSync BALANCE-sync health
          // (DCP-217), read from the tenant `bank_accounts` sync columns — NOT
          // the balances snapshot. Manual accounts have no upstream API, so
          // they keep the neutral 'pending' default (the UI renders '-' for
          // them). Connected accounts map:
          //   'success'      → success  (UI: green "Loaded")
          //   'failed'       → failed   (UI: red "Failed" + error text)
          //   'syncing'/null → pending  (UI: muted "Pending")
          let apiUpdateStatus: ImportStatus = 'pending'
          let apiUpdateMessage: string | undefined
          if (isApi) {
            if (acc.last_balance_sync_status === 'success') {
              apiUpdateStatus = 'success'
            } else if (acc.last_balance_sync_status === 'failed') {
              apiUpdateStatus = 'failed'
              // Carry the raw sync error so the UI can expose it (tooltip/title).
              apiUpdateMessage = acc.last_balance_sync_error ?? undefined
            }
          }

          // Same object-or-array narrowing as the facility embed above.
          const connectionJoin = acc.ledgersync_connections
          const rawConnectionStatus = Array.isArray(connectionJoin)
            ? connectionJoin[0]?.status
            : connectionJoin?.status
          // Only states that stop the data flowing are surfaced, by the rule
          // every connection surface shares (`connectionAttention`): `active`
          // needs no marker (the API Update Status column already reports the
          // sync), `initiated` owns no accounts, and any other value —
          // `requires_action`, `failed`, `disconnected` or one newer than this
          // bundle (`'unknown'`) — gets the marker.
          const connectionStatus: BalanceImportStatus['connectionStatus'] =
            connectionAttention(rawConnectionStatus) ?? undefined

          return {
            id: acc.id,
            bankAccountId: acc.id,
            isApiAccount: isApi,
            connectionStatus,
            facility: facilityName,
            bank: acc.institution_name ?? '-',
            accountName: acc.name,
            accountNumber: acc.account_number ?? '-',
            bankBalance: latest?.bank_balance ?? 0,
            apiUpdateStatus,
            apiUpdateMessage,
            // API-side timestamp comes from the balance-sync watermark, not the
            // balances snapshot. Only meaningful for connected accounts.
            lastApiUpdateTimestamp: isApi ? acc.last_balance_sync_at ?? '' : '',
            lastUpdateSource,
            lastUpdateTimestamp: latest?.updated_at ?? '',
          }
        })

        return { data: { items, total }, error: null }
      },

      /**
       * Upsert a manual bank balance for `bankAccountId` on today's date.
       *
       * Conflict target is `(bank_account_id, date)` — the existing UNIQUE
       * constraint on the table. PostgREST applies DO UPDATE only to the
       * columns present in the payload, which is why we deliberately omit
       * `book_balance`: re-editing today's row must not wipe the Plaid-side
       * book figure (and on a brand-new row book_balance stays NULL — the
       * NOT NULL constraint is dropped in the DCP-120 migration).
       *
       * `updated_by` records the AP user behind the inline edit for the
       * future Balance Import History view; the FK to `public.profiles`
       * lives only on each tenant schema (see DCP-120 schema migration).
       *
       * THE DATE IS THE BUSINESS DAY, and this is the WRITE path that defines
       * it. `businessDate()` — not the UTC day, and not the browser's day — is
       * what lands in the `date` column, which is half of the
       * `onConflict: 'bank_account_id,date'` key.
       *
       * That makes this line a contract, not a detail: every reader of this
       * table must resolve "today" through the SAME primitive, or it will look
       * for a row that exists under a different key. Two things break the
       * moment they diverge — the upsert stops finding the row it should
       * update (producing a second row for the same day instead of editing the
       * first), and the manual-precedence guards that compare a manual row's
       * date against "today" stop matching, so a manual entry silently loses
       * to the API-synced figure it was meant to override.
       *
       * The previous UTC behaviour was the defect this replaces: a user
       * entering Friday's closing balance at 19:30 Central had it stamped
       * Saturday, and a reader asking for Friday did not find it.
       */
      async upsertManual(params: {
        bankAccountId: string
        balance: number
        userId: string
      }): Promise<DbResult<null>> {
        const today = businessDate()
        // The `as any` cast follows the project-wide pattern documented at
        // the top of this file (see "NOTE ON TYPE CASTS"): Supabase's
        // generated tenant types don't compose with the dynamic-schema
        // wrapper for write methods. The shape of the payload is still
        // type-checked at the function boundary — params is fully typed and
        // the column names map 1:1 to TenantTables['balances']['Insert'].
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (client as any).from('balances').upsert(
          {
            bank_account_id: params.bankAccountId,
            date: today,
            bank_balance: params.balance,
            source: 'manual',
            updated_by: params.userId,
          },
          { onConflict: 'bank_account_id,date' },
        )
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: null, error: null }
      },
    },

    sageSyncState: {
      /**
       * List rows from tenant.sage_sync_state ordered by sync_type asc.
       * Table may be empty until the first Trigger.dev run inserts rows, so
       * callers must tolerate a missing sync_type entry.
       */
      async list(): Promise<DbResult<SageSyncStateRow[]>> {
        const { data, error } = await client
          .from('sage_sync_state')
          .select('*')
          .order('sync_type', { ascending: true })
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: (data || []) as unknown as SageSyncStateRow[], error: null }
      },
    },

    sageBankAccounts: {
      /**
       * List Sage bank account catalog rows for the tenant ordered by name asc.
       * When activeOnly is true, restricts to rows that are still present in Sage
       * (status='active' AND missing_in_sage=false).
       */
      async list(options: { activeOnly?: boolean } = {}): Promise<DbResult<SageBankAccountRow[]>> {
        let query = client
          .from('sage_bank_accounts')
          .select('*')
          .order('name', { ascending: true })
        if (options.activeOnly) {
          query = query.eq('status', 'active').eq('missing_in_sage', false)
        }
        const { data, error } = await query
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: (data || []) as unknown as SageBankAccountRow[], error: null }
      },

      /**
       * Sum the imported Intacct balance (`intacct_balance`) across bank
       * accounts, treating a null balance (never-synced account) as 0.
       *
       * Powers the "Intacct Balance" KPI tile (DCP-159/160). The account set
       * is a small per-tenant catalog (tens of rows at most), so summing in
       * the application after a single SELECT is cheaper and simpler than a
       * PostgREST aggregate RPC — and keeps the null-as-zero rule in one place
       * alongside the table-display rendering.
       *
       * DCP-335: the total honours an explicit facility filter. `sage_bank_accounts`
       * has no `facility_id`, but it links to one through
       * `location_id → facilities.sage_location_id` (UPPER-cased, per DCP-262) —
       * the same chain the Intacct Balances table, the DCP-292 deny set and the
       * `sba_facility` CTE of `get_cash_position_summary` already use. Scoping is
       * delegated to {@link intacctLocationScope}, which documents the ordering and
       * the plain-membership decision.
       *
       * @param options.activeOnly - default true; when true only rows still
       *   present in Sage (status='active' AND missing_in_sage=false) are
       *   summed, matching the accounts shown in the Intacct Balances table.
       * @param options.facilityIds - optional facility scope (DCP-335). Absent or
       *   empty = org-wide, exactly as before. When set, the total equals the sum of
       *   the rows the Intacct Balances table shows for the same filter — that
       *   identity IS the acceptance criterion. Note the corollary: unmapped GL
       *   accounts drop out under a filter, so the per-facility totals do NOT have
       *   to add up to the org-wide one. That is correct, not a rounding bug.
       * @returns DbResult wrapping the numeric total.
       */
      async sumIntacctBalance(
        options: { activeOnly?: boolean; facilityIds?: string[] } = {},
      ): Promise<DbResult<number>> {
        const activeOnly = options.activeOnly ?? true
        let query = client.from('sage_bank_accounts').select('intacct_balance, location_id')
        if (activeOnly) {
          query = query.eq('status', 'active').eq('missing_in_sage', false)
        }
        const { data, error } = await query
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        // DCP-292 (deactivated facilities) + DCP-335 (selected facilities), one
        // predicate shared with getIntacctFreshness so the tile's number and its
        // warning can never be computed over different account populations.
        const { data: inScope, error: scopeError } = await intacctLocationScope(options.facilityIds)
        if (scopeError) return { data: null, error: scopeError }
        // Null balances (accounts that have never been synced) contribute 0 to
        // the total so the tile reflects only the balances Sage has reported.
        // PostgREST returns NUMERIC columns as strings to preserve precision, so
        // coerce defensively (mirrors the bank_balance handling above): without
        // Number() the reduce would string-concatenate values into garbage, and
        // the isFinite guard keeps schema drift / non-numeric input from
        // propagating NaN into the Intacct Balance KPI tile.
        const total = (data ?? []).reduce((sum, row) => {
          if (!inScope((row as { location_id: string | null }).location_id)) return sum
          const n = Number(row.intacct_balance)
          return sum + (Number.isFinite(n) ? n : 0)
        }, 0)
        return { data: total, error: null }
      },

      /**
       * DCP-218 AC.3: report Intacct-balance freshness so the Intacct tile can
       * warn when any account's book balance failed to sync or was never
       * synced. The Intacct balance is period-based (e.g. "Month Ended June
       * 2026"), not daily, so there is no current-day check — the meaningful
       * signal is a failed or never-run per-account sync.
       *
       * @param options.activeOnly default true — only accounts still present in
       *   Sage (status='active' AND missing_in_sage=false), matching the tile sum.
       * @param options.facilityIds optional facility scope (DCP-335). MUST be kept
       *   in step with {@link sumIntacctBalance}: an unscoped warning beside a
       *   scoped total would report failures for accounts that are not on screen
       *   ("7 accounts have a failed sync" next to a one-account figure) — the same
       *   class of inconsistency DCP-335 fixes. Both call the one shared predicate.
       * @returns `{ total, failed, neverSynced }` account counts.
       */
      async getIntacctFreshness(
        options: { activeOnly?: boolean; facilityIds?: string[] } = {},
      ): Promise<DbResult<{ total: number; failed: number; neverSynced: number }>> {
        const activeOnly = options.activeOnly ?? true
        let query = client
          .from('sage_bank_accounts')
          .select('intacct_balance, intacct_balance_status, location_id')
        if (activeOnly) {
          query = query.eq('status', 'active').eq('missing_in_sage', false)
        }
        const { data, error } = await query
        if (error) return { data: null, error: { message: error.message, code: error.code } }

        // DCP-292 (deactivated facilities) + DCP-335 (selected facilities): the
        // SAME predicate the tile total uses, so the counts always describe exactly
        // the accounts that were summed.
        const { data: inScope, error: scopeError } = await intacctLocationScope(options.facilityIds)
        if (scopeError) return { data: null, error: scopeError }
        const rows = ((data ?? []) as Array<{
          intacct_balance: number | string | null
          intacct_balance_status: string | null
          location_id: string | null
        }>).filter((row) => inScope(row.location_id))

        let failed = 0
        let neverSynced = 0
        for (const row of rows) {
          if (row.intacct_balance_status === 'failed') {
            failed++
          } else if (row.intacct_balance === null) {
            // No balance value at all → the account is active but contributes
            // nothing to the tile total. A null status with a present balance is
            // NOT flagged: the value is real (e.g. seeded/legacy), only the sync
            // bookkeeping is absent, so warning there would be a false positive.
            neverSynced++
          }
        }
        return { data: { total: rows.length, failed, neverSynced }, error: null }
      },

      /**
       * DCP-161: per-facility variant of getIntacctFreshness, powering the
       * "missing data" warning icons on the Cash Position Summary report.
       *
       * Attribution mirrors the `sba_facility` CTE in the Cash Position engine
       * RPC (migration 20260730000000_dcp308_facility_id_single_source.sql): a
       * Sage account belongs to the facility whose `sage_location_id` matches
       * its `location_id` CASE-INSENSITIVELY (`UPPER(...) = UPPER(...)`). When
       * several facilities share a location id case-variantly, exactly ONE is
       * picked using the SAME tie-break as the RPC — `is_active DESC, id ASC`
       * — so a Sage account is never attributed to two facilities (which would
       * double-warn). Facilities with a null/blank `sage_location_id` get no
       * entry in the map — they can never have an Intacct balance.
       *
       * @param options.activeOnly default true — `status='active'`, EXACTLY the
       *   predicate of the RPC's `intacct` CTE. Deliberately NOT the tile's
       *   `getIntacctFreshness` rule, which additionally drops
       *   `missing_in_sage` accounts: those accounts still contribute their
       *   balance to the report cell, so excluding them here would let a
       *   facility whose only Sage accounts are missing-in-Sage render an
       *   unflagged `$0` — precisely the blind spot this feature exists to
       *   remove. The warning population must be the population the number is
       *   summed from.
       * @returns `byFacility` keyed by `facility_id`, each entry
       *   `{ expected, failed, neverSynced }` account counts.
       */
      async getIntacctFreshnessByFacility(
        options?: { activeOnly?: boolean },
      ): Promise<DbResult<{
        byFacility: Record<string, { expected: number; failed: number; neverSynced: number }>
      }>> {
        const activeOnly = options?.activeOnly ?? true

        // Independent reads — issue them together rather than awaiting the
        // facility catalog before the accounts query is even sent. Both walks
        // interleave, so paging costs latency only within each read.
        //
        // Both are POPULATIONS the counts are derived from, so both are
        // exhaustive: a silent PostgREST truncation at 1000 would under-report
        // `expected` and drop `failed` / `neverSynced` accounts entirely,
        // turning a real missing-data warning into silence — the one failure
        // mode this feature exists to prevent. `sage_bank_accounts` is by far
        // the likelier of the two to cross the cap, but a truncated facility
        // catalog is worse: every account whose location resolved to a lost
        // facility disappears from the map with no trace.
        const [
          { data: facilityRows, error: facilityError },
          { data: accountRows, error: accountError },
        ] = await Promise.all([
          fetchAllRows<{ id: string; sage_location_id: string | null; is_active: boolean }>(
            (from, to) =>
              client
                .from('facilities')
                .select('id, sage_location_id, is_active')
                // Primary key — the unique tiebreaker the offset walk needs.
                .order('id')
                .range(from, to),
          ),
          fetchAllRows<{
            financial_entity_id: string
            location_id: string
            intacct_balance: number | string | null
            intacct_balance_status: string | null
          }>((from, to) => {
            // Rebuilt per page: a PostgREST builder is single-use, so the filter
            // chain cannot be hoisted out of the loop and reused.
            let accountQuery = client
              .from('sage_bank_accounts')
              .select('financial_entity_id, location_id, intacct_balance, intacct_balance_status')
            if (activeOnly) {
              accountQuery = accountQuery.eq('status', 'active')
            }
            // `financial_entity_id` (the Sage BANKACCOUNTID) is this table's
            // PRIMARY KEY, so it is a stable and unique paging key.
            return accountQuery.order('financial_entity_id').range(from, to)
          }),
        ])
        if (facilityError) {
          return { data: null, error: facilityError }
        }
        if (accountError) {
          return { data: null, error: accountError }
        }

        // Like `getCoverageByFacility`, this map is deliberately NOT scoped to
        // ACTIVE facilities. The resolution below reproduces the RPC's `sba_facility`
        // tie-break verbatim (`ORDER BY sba.financial_entity_id, f.is_active DESC,
        // f.id`, 20260730000000_dcp308_facility_id_single_source.sql:257), which
        // considers inactive facilities as a fallback rather than dropping them, and
        // the caller reads the map by column key — a set the RPC has already filtered
        // on `f.is_active`. See `getCoverageByFacility` for the full rationale and for
        // the rule it implies: never reduce either map wholesale.

        // Resolve each location_id (UPPER-cased) to exactly one facility,
        // tie-broken is_active DESC then id ASC — same rule as sba_facility.
        // NOTE the tie-break is resolved HERE, in JS, and not by the `.order('id')`
        // above: that order is the paging key only, and must stay a single unique
        // column for the walk to be sliceable.
        const facilityByUpperLocationId = new Map<string, { id: string; is_active: boolean }>()
        for (const facility of facilityRows) {
          if (!facility.sage_location_id) continue
          const key = facility.sage_location_id.toUpperCase()
          const current = facilityByUpperLocationId.get(key)
          if (
            current === undefined ||
            (facility.is_active && !current.is_active) ||
            (facility.is_active === current.is_active && facility.id < current.id)
          ) {
            facilityByUpperLocationId.set(key, { id: facility.id, is_active: facility.is_active })
          }
        }

        const byFacility: Record<string, { expected: number; failed: number; neverSynced: number }> = {}
        for (const account of accountRows) {
          const facility = facilityByUpperLocationId.get(account.location_id.toUpperCase())
          if (facility === undefined) continue

          const bucket = byFacility[facility.id] ?? { expected: 0, failed: 0, neverSynced: 0 }
          bucket.expected += 1
          if (account.intacct_balance_status === 'failed') {
            bucket.failed += 1
          } else if (account.intacct_balance === null) {
            // Same rule as getIntacctFreshness: a null status with a present
            // balance is NOT flagged (real seeded/legacy value); only a truly
            // absent balance counts as never-synced.
            bucket.neverSynced += 1
          }
          byFacility[facility.id] = bucket
        }

        return { data: { byFacility }, error: null }
      },
    },

    sageChecks: {
      /**
       * List checks pulled from Sage for the tenant.
       *
       * @param opts.page                 - 1-indexed page number; combined with pageSize for `.range()`
       * @param opts.pageSize             - rows per page; both page and pageSize must be set together
       * @param opts.states               - filter by sage_checks.state values (e.g. ['C'] for Posted)
       * @param opts.outstandingOnly      - when true, restricts to effectively-issued checks: adds
       *                                    `.is('date_cashed', null)` AND keeps only rows whose
       *                                    `dcp_check_status` is null or 'issued', so checks the user marked
       *                                    paid (via Date Cashed) or voided drop out of the result.
       *                                    DCP-320: a row with no user override also drops out once Sage
       *                                    reports it reconciled (`sage_cleared = 'T'`); an explicit
       *                                    'issued' override still wins over Sage.
       *                                    No production caller passes it today: since DCP-237 the
       *                                    OutstandingChecksPage lists checks in ALL statuses, and
       *                                    SageIntacctPage's aggregator (which needs paid + outstanding
       *                                    rows to compute the mix) omits it too. It is kept as the
       *                                    client-side mirror of the SQL "effectively issued" predicate
       *                                    (`sage_checks_outstanding_total` / `sage_checks_aggregate_by_entity`
       *                                    / `get_cash_position_summary`) and MUST stay in lockstep with
       *                                    it — and with `isOutstandingCheck` — if either changes.
       * @param opts.statuses             - filter by EFFECTIVE status (the one the Status badge shows,
       *                                    see `checkStatusFilter`); several values are OR-ed.
       *                                    Undefined/empty = no restriction; only unknown values → no rows.
       * @param opts.facilityIds          - filter by tenant facility ids, applied directly on the
       *                                    denormalized `sage_checks.facility_id` column (DCP-308).
       *                                    Empty array = "caller resolved zero facilities" → no rows.
       * @param opts.dateFrom / opts.dateTo - filter by issued date (when_paid; falls back to when_due
       *                                    for non-Posted rows handled at UI layer)
       * @param opts.sortColumn           - one of the orderable sage_checks columns (whitelisted below);
       *                                    UI passes column key matching the DataTable schema
       * @param opts.sortDirection        - 'asc' | 'desc'; default 'desc'
       *
       * When both page and pageSize are omitted, returns all rows (legacy behaviour
       * used by SageIntacctPage to aggregate per-bank-account counts).
       */
      async list(
        opts: {
          page?: number
          pageSize?: number
          states?: readonly string[]
          statuses?: readonly DcpCheckStatus[]
          outstandingOnly?: boolean
          facilityIds?: string[]
          dateFrom?: string
          dateTo?: string
          sortColumn?: SageCheckSortColumn
          sortDirection?: 'asc' | 'desc'
        } = {},
      ): Promise<DbResult<SageCheckRow[]>> {
        const {
          page,
          pageSize,
          states,
          statuses,
          outstandingOnly,
          facilityIds,
          dateFrom,
          dateTo,
          sortColumn,
          sortDirection = 'desc',
        } = opts
        // Defensive whitelist on `states` — guards against future callers
        // (URL params, RPC playground, server-side code) passing arbitrary
        // STATE codes that would silently fall through PostgREST's `.in()`
        // as an empty filter clause. See {@link validateSageCheckStates}
        // for the full rationale; the helper is shared with `count`
        // below so the two paths can't drift out of sync.
        const validStates = validateSageCheckStates(states, 'sageChecks.list')
        // Pagination guards: reject invalid `page`/`pageSize` before they
        // reach `.range()`, where negative/zero/oversized values silently
        // produce empty results or hit PostgREST upper limits. Both fields
        // are nullable (legacy "fetch all rows" mode), but when supplied
        // they must be positive integers and pageSize bounded so callers
        // can't accidentally request 10k rows in one go.
        //
        // The `Number.isFinite` + `Number.isInteger` checks run BEFORE the
        // range test on purpose: every comparison with `NaN` is `false`
        // (`NaN < 1`, `NaN > 100`, `NaN < 0` all evaluate to `false`), so a
        // pure `page < 1` guard silently lets `NaN` and `±Infinity` through.
        // That would feed `(NaN - 1) * NaN = NaN` into `.range(NaN, NaN)`
        // and produce a PostgREST 400 with no actionable message. The
        // tighter guard catches every realistic bad-input path (URL params
        // parsed without `parseInt`, a hand-rolled fetch caller, malformed
        // JSON) at the helper boundary instead.
        if (
          page !== undefined &&
          (!Number.isFinite(page) || !Number.isInteger(page) || page < 1)
        ) {
          return {
            data: null,
            error: { message: `Invalid page=${page}: must be a positive integer`, code: 'INVALID_PAGINATION' },
          }
        }
        if (
          pageSize !== undefined &&
          (!Number.isFinite(pageSize) || !Number.isInteger(pageSize) || pageSize < 1 || pageSize > 100)
        ) {
          return {
            data: null,
            error: {
              message: `Invalid pageSize=${pageSize}: must be an integer between 1 and 100`,
              code: 'INVALID_PAGINATION',
            },
          }
        }
        // Caller convention for `facilityIds` (DCP-308):
        //   - undefined      → no facility filter, show every row
        //   - []             → caller resolved a facility filter that
        //                      produced zero facilities, so the *correct*
        //                      answer is "no rows", not "all rows".
        //                      Short-circuit before building the query:
        //                      PostgREST's `.in()` is skipped for empty
        //                      arrays, which would silently collapse to
        //                      no-filter and leak unrelated rows back
        //                      through the facility selector.
        //   - [a, b, c]      → filter to that facility_id set
        if (facilityIds && facilityIds.length === 0) {
          return { data: [], error: null }
        }

        // Same short-circuit semantics for `states`: if the caller supplied a
        // list and every code was filtered out by the whitelist above, the
        // correct answer is "no rows" — not "all rows". Falling through to
        // PostgREST with no `.in()` clause would collapse to no-filter and
        // leak unrelated states back through the request.
        if (states && validStates && validStates.length === 0) {
          return { data: [], error: null }
        }

        // Shared with `count` so the page rows and the pagination total agree.
        const statusFilter = checkStatusFilter(statuses)
        if (statusFilter.kind === 'none') {
          return { data: [], error: null }
        }

        let query = client.from('sage_checks').select('*')

        // Apply order: explicit column if provided, otherwise stable default
        // (most-recent first by when_paid, then by record_no for tie-break).
        // Runtime guard: even though SageCheckSortColumn restricts the type
        // at compile time, the value can reach us from URL params or
        // DataTable state, so we reject anything outside the allow-list
        // before it hits .order() — that PostgREST clause interpolates the
        // column name verbatim into the request query string.
        if (sortColumn && SAGE_CHECK_SORT_COLUMNS.has(sortColumn)) {
          query = query.order(sortColumn, {
            ascending: sortDirection === 'asc',
            nullsFirst: false,
          })
          // Stable secondary sort to avoid shuffling on identical primary values.
          if (sortColumn !== 'record_no') {
            query = query.order('record_no', { ascending: false })
          }
        } else {
          // Surface the rejection in dev so a typo / stale URL param doesn't
          // silently fall through to the default ordering. Production keeps
          // the silent fallback (it's the safer behaviour for end users) but
          // a dev iterating on the column whitelist will see the log.
          if (sortColumn && import.meta.env.DEV) {
            console.warn(
              `sageChecks.list: ignoring sortColumn "${sortColumn}" — not in SAGE_CHECK_SORT_COLUMNS whitelist`,
            )
          }
          query = query
            .order('when_paid', { ascending: false, nullsFirst: false })
            .order('record_no', { ascending: false })
        }

        if (validStates && validStates.length > 0) {
          query = query.in('state', validStates)
        }

        // Outstanding-only filter: keep only effectively-issued rows, matching
        // the SQL predicate ("aggregated outstanding totals exclude these
        // rows"). No production caller sets it since DCP-237 (the page shows
        // every status; SageIntacctPage needs paid + outstanding rows to
        // compute the per-financial-entity mix) — see the JSDoc above for the
        // lockstep contract.
        if (outstandingOnly) {
          query = query.is('date_cashed', null)
          // DCP-213 C4: keep only effectively-issued checks — exclude user-voided
          // (and any explicitly-paid) rows so they leave the outstanding list on
          // the next refetch, matching effectiveCheckStatus/isOutstandingCheck.
          query = query.or('dcp_check_status.is.null,dcp_check_status.eq.issued')
          // DCP-320: with no user override, Sage's reconciliation flag decides.
          // Why a SECOND flat .or() rather than a nested one: the rule is
          //   status = 'issued' OR (status IS NULL AND cleared <> 'T')
          // and since `status = 'issued'` and `status IS NULL` are mutually
          // exclusive it distributes into two independent disjunctions —
          //   (status IS NULL OR status = 'issued') AND (status = 'issued' OR cleared <> 'T')
          // the first of which is the clause above. Verified equivalent to the SQL
          // predicate over all 16 status x cleared combinations. Staying flat
          // keeps this clause readable; nested and()/or() is used by the Status
          // filter below (see `sage-check-status-filter.ts`).
          //
          // `sage_cleared.is.null` is NOT redundant: `neq` renders as `<> 'T'`,
          // which is NULL — and therefore excluded — for every row the check sync
          // has not re-read since migration 20260922000000 added the column.
          // Without it, switching this flag on would silently empty the list.
          query = query.or(
            'dcp_check_status.eq.issued,sage_cleared.is.null,sage_cleared.neq.T',
          )
        }

        if (statusFilter.kind === 'match') query = query.or(statusFilter.or)

        // DCP-308: filter directly on the denormalized facility_id column —
        // no facility → sage_location_id → bank account → financial_entity
        // resolution. Every selected facility contributes to the query
        // regardless of whether it has a synced bank account.
        //
        // DEPLOY ORDERING: this column is added by migration 20260724010000,
        // which is applied MANUALLY while the web app auto-deploys on merge. The
        // list/count use select('*'), so the table survives without the column —
        // but the moment a facility is selected this `.in('facility_id', …)`
        // returns PostgREST 400 `column sage_checks.facility_id does not exist`
        // (postgrest-js doesn't type-check the column name; the row is cast
        // `as unknown as SageCheckRow[]`, so there's no compile-time guard). The
        // migration MUST land before or with the merge (DCP-308 review #3).
        {
          // DCP-292: empty facilityIds already short-circuited above (→ no rows).
          // A missing filter now defaults to ACTIVE facilities so a deactivated
          // facility's outstanding checks leave the table; unmapped checks
          // (facility_id IS NULL) are kept.
          const scope = await applyActiveFacilityScope(query, facilityIds)
          if (scope.error) return { data: null, error: scope.error }
          query = scope.query
        }

        // Date range filter on when_paid. We use when_paid because it's the
        // canonical "Issued" date for Posted checks (per BA AC#8).
        if (dateFrom) query = query.gte('when_paid', dateFrom)
        if (dateTo) query = query.lte('when_paid', dateTo)

        if (page !== undefined && pageSize !== undefined) {
          const from = (page - 1) * pageSize
          const to = from + pageSize - 1
          query = query.range(from, to)
        }

        const { data, error } = await query
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: (data || []) as unknown as SageCheckRow[], error: null }
      },

      /**
       * Count rows in tenant.sage_checks matching the same filters as `list`.
       * Used to compute total page count for pagination.
       */
      async count(
        opts: {
          states?: readonly string[]
          statuses?: readonly DcpCheckStatus[]
          outstandingOnly?: boolean
          facilityIds?: string[]
          dateFrom?: string
          dateTo?: string
        } = {},
      ): Promise<DbResult<number>> {
        const { states, statuses, outstandingOnly, facilityIds, dateFrom, dateTo } = opts

        // Same short-circuit as `list` above (DCP-308): an empty facility
        // array means the caller's filter has no possible matches, so the
        // count is 0 — falling through to PostgREST would skip the `.in()`
        // clause and over-count.
        if (facilityIds && facilityIds.length === 0) {
          return { data: 0, error: null }
        }

        // Mirror `list`: filter `states` via the shared
        // {@link validateSageCheckStates} helper so an unknown code can't
        // slip past as an empty PostgREST clause and silently over-count.
        // Both helpers must apply the same rule or the pagination math
        // (count → totalPages) diverges from the page rows.
        const validStates = validateSageCheckStates(states, 'sageChecks.count')

        // Mirror `list`: if every supplied state was filtered out, the
        // count is 0 — falling through to PostgREST with no `.in()` clause
        // would over-count by matching every state.
        if (states && validStates && validStates.length === 0) {
          return { data: 0, error: null }
        }

        // Mirror `list`: same effective-status filter, same "no rows" outcome.
        const statusFilter = checkStatusFilter(statuses)
        if (statusFilter.kind === 'none') {
          return { data: 0, error: null }
        }

        let query = client
          .from('sage_checks')
          .select('*', { count: 'exact', head: true })

        if (validStates && validStates.length > 0) {
          query = query.in('state', validStates)
        }
        if (outstandingOnly) {
          query = query.is('date_cashed', null)
          // DCP-213 C4: mirror the list query — count only effectively-issued checks.
          query = query.or('dcp_check_status.is.null,dcp_check_status.eq.issued')
          // DCP-320: mirror the list query — see the rationale for the second
          // flat .or() there. The two must never diverge, or the footer count
          // disagrees with the rows above it.
          query = query.or(
            'dcp_check_status.eq.issued,sage_cleared.is.null,sage_cleared.neq.T',
          )
        }
        if (statusFilter.kind === 'match') query = query.or(statusFilter.or)
        // DCP-308: mirror `list` — filter directly on facility_id. Same deploy-
        // ordering caveat as the list path: 400s until migration 20260724010000
        // adds the column, so it must land before/with the merge (review #3).
        {
          // DCP-292: mirror list() — default to ACTIVE facilities (keep NULL) so
          // the count matches the visible outstanding rows.
          const scope = await applyActiveFacilityScope(query, facilityIds)
          if (scope.error) return { data: null, error: scope.error }
          query = scope.query
        }
        if (dateFrom) query = query.gte('when_paid', dateFrom)
        if (dateTo) query = query.lte('when_paid', dateTo)

        const { count, error } = await query
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: count ?? 0, error: null }
      },

      /**
       * Atomically update a check's DCP-owned Status override AND Date Cashed in
       * a single write (DCP-275). Replaces the former split `updateStatus` /
       * `updateDateCashed` methods: the Outstanding Checks page now edits both
       * fields together in one row-level inline editor, and writing them in one
       * `.update()` keeps the two columns consistent (no window where the row
       * carries, e.g., status='paid' but no date because a second request hadn't
       * landed yet).
       *
       * `date_cashed` and `dcp_check_status` are the only DCP-mutable fields on
       * sage_checks — everything else is owned by Sage and overwritten on every
       * sync. This method follows the file convention of validating only its
       * arguments; it never does a pre-flight read.
       *
       * Date resolution: only 'paid' carries a Date Cashed (it records WHEN the
       * check cleared). For any other status ('issued'/'voided') the date is
       * meaningless and is forced to null here — this is WHY `canAssignStatus`
       * does not require an empty date for non-paid statuses, and why a stale
       * date can never survive to re-derive the row as 'paid'.
       *
       * Status resolution (DCP-320): the stored override is resolved via
       * `storedStatusFor` — an Issued save persists the explicit 'issued'
       * override ONLY when it contradicts Sage (`sageCleared === 'T'`);
       * otherwise it stores NULL ("follow Sage"), so a later Sage
       * reconciliation can still close the check. `sageCleared` is REQUIRED
       * (not optional) so no caller can silently fall into the "not 'T'"
       * branch by forgetting to pass it; it comes from the row being edited,
       * which keeps the "no pre-flight read" convention.
       *
       * @param recordNo    - sage_checks PK of the row to update.
       * @param status      - The DCP status the user assigned.
       * @param dateCashed  - The Date Cashed the user entered (ISO YYYY-MM-DD) or null.
       * @param sageCleared - The row's current `sage_cleared` flag ('T'/'F'/'M'/null).
       * @returns The updated SageCheckRow, or a DbResult error.
       */
      async updateCheck(
        recordNo: number,
        status: DcpCheckStatus,
        dateCashed: string | null,
        sageCleared: SageCheckRow['sage_cleared'],
      ): Promise<DbResult<SageCheckRow>> {
        if (!(DCP_CHECK_STATUSES as readonly string[]).includes(status)) {
          return {
            data: null,
            error: { message: `Invalid status "${status}".`, code: 'INVALID_STATUS' },
          }
        }

        // Resolve the final date BEFORE validation/persistence via the shared
        // helper: only 'paid' carries a date; every other status clears it so the
        // row can't keep a stale Date Cashed that would otherwise re-derive it as
        // 'paid' via effectiveCheckStatus().
        const resolvedDate = resolveDateCashed(status, dateCashed)

        // Validate the date shape only when one is actually being written.
        // PostgreSQL's `date` column accepts only ISO 8601; a malformed value
        // like '25/05/2026' would surface as an opaque PostgREST 400. Catching
        // it at the client boundary gives a precise, actionable message.
        if (resolvedDate !== null) {
          // ISO 8601 calendar-date regex (YYYY-MM-DD). The shape check is
          // load-bearing: `new Date('2026-13-99')` parses to an Invalid Date in
          // jsdom but to a real future date in some Node versions, so we lean on
          // the regex to reject the structurally-wrong string rather than relying
          // on Date arithmetic alone.
          const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/
          if (!ISO_DATE_RE.test(resolvedDate)) {
            return {
              data: null,
              error: {
                message: `Invalid date_cashed format "${resolvedDate}" — expected ISO 8601 (YYYY-MM-DD).`,
                code: 'INVALID_DATE_FORMAT',
              },
            }
          }
          // Catch calendar-impossible values (2026-02-30, 2026-13-01). The regex
          // above already enforces the structural shape, so this second pass only
          // fires on semantically-invalid dates.
          const parsed = new Date(resolvedDate)
          if (Number.isNaN(parsed.getTime())) {
            return {
              data: null,
              error: {
                message: `Invalid date_cashed value "${resolvedDate}" — date does not exist in calendar.`,
                code: 'INVALID_DATE_VALUE',
              },
            }
          }
        }

        // Cross-field rule (paid ⇒ date present) — shared with the UI Save gate
        // via canAssignStatus so the two can never drift. Validated against the
        // RESOLVED date so an 'issued' save (date forced to null) always passes.
        const allowed = canAssignStatus(status, resolvedDate)
        if (!allowed.ok) {
          return {
            data: null,
            error: { message: allowed.reason, code: 'STATUS_DATE_CONFLICT' },
          }
        }

        // Single atomic write of BOTH columns. The override value comes from
        // storedStatusFor (DCP-320): a non-contradicting Issued is stored as NULL.
        const { data, error } = await client
          .from('sage_checks')
          .update({
            dcp_check_status: storedStatusFor(status, sageCleared),
            date_cashed: resolvedDate,
          })
          .eq('record_no', recordNo)
          .select('*')
          .single()
        if (error) return { data: null, error: { message: error.message, code: error.code } }
        return { data: data as unknown as SageCheckRow, error: null }
      },
    },
  }
}

/** Cache of tenant DB wrappers keyed by orgId to avoid re-creating on every call */
const tenantDbCache = new Map<string, ReturnType<typeof _buildTenantDb>>()

/**
 * Clear tenant DB wrapper cache (lightweight helper objects only).
 * Does NOT clear underlying Supabase client instances — they share the main
 * client's storageKey and pick up token changes automatically via localStorage.
 * Recreating them causes new GoTrueClient instances that broadcast SIGNED_IN
 * through BroadcastChannel, triggering an infinite loop in AuthContext.
 * Use clearTenantClients() separately for full cleanup on sign out.
 */
export function clearTenantDbCache(): void {
  tenantDbCache.clear()
}

/**
 * Create typed database helpers for a specific tenant schema.
 * Wrapper objects are cached per orgId — the underlying Supabase client is also cached.
 * @param orgId - Organization UUID (used to select the correct schema)
 */
export function createTenantDb(orgId: string) {
  const cached = tenantDbCache.get(orgId)
  if (cached) return cached

  const db = _buildTenantDb(orgId)
  tenantDbCache.set(orgId, db)
  return db
}
